import React, { useState, useEffect } from 'react';
import { toast as sonnerToast } from 'sonner';
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { api } from '../api';
import { useT } from '../contexts/AppContext';
import Profil from './Profil';
import Abonnement from './Abonnement';
import Parametres from './Parametres';
import InvoiceDetail from './InvoiceDetail';
import QuoteDetail from './QuoteDetail';
import Analytique from './Analytique';
import Contrats from './Contrats';
import LivreRecettes from './LivreRecettes';

const N8N_WEBHOOK =
  import.meta.env.VITE_N8N_WEBHOOK || 'https://n8n.akodigitale.fr/webhook/facture-devis';

const notifyN8n = async (type, data) => {
  const res = await fetch(N8N_WEBHOOK, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type, ...data }),
  });
  return res.json().catch(() => ({}));
};

// ── Toast (sonner) ──────────────────────────────────────────────────────────
// Wrapper uniforme : toast('msg') → succès, toast('msg','error') → erreur
const useToast = () => {
  const toast = (message, type = 'success') => {
    if (type === 'error')   sonnerToast.error(message);
    else if (type === 'warning') sonnerToast.warning(message);
    else sonnerToast.success(message);
  };
  // Compatibilité avec le pattern ancien { toasts, removeToast }
  return { toasts: [], toast, removeToast: () => {} };
};

// ── Styles ──────────────────────────────────────────────────────────────────

const s = {
  layout: { display: 'flex', minHeight: '100vh', background: 'var(--ako-bg)' },
  sidebar: { width: '240px', background: 'var(--ako-surface)', borderRight: '1px solid var(--ako-border)', padding: '1.5rem 1rem', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  main: { flex: 1, padding: '2rem', overflow: 'auto' },
  navBtn: (active) => ({
    display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem',
    borderRadius: '0.5rem', cursor: 'pointer',
    color: active ? '#60a5fa' : 'var(--ako-text-2)',
    background: active ? 'rgba(59,130,246,0.15)' : 'transparent',
    border: 'none', width: '100%', textAlign: 'left',
    fontWeight: active ? '600' : '400', fontSize: '0.95rem', marginBottom: '0.25rem', transition: 'all 0.2s',
  }),
  card: { background: 'var(--ako-surface)', border: '1px solid var(--ako-border)', borderRadius: '0.75rem', padding: '1.5rem', marginBottom: '1rem' },
  btn: { background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.6rem 1.2rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem', transition: 'all 0.2s' },
  btnRed: { background: 'rgba(239,68,68,0.15)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '0.5rem', padding: '0.6rem 1.2rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem' },
  btnSmall: { background: 'rgba(239,68,68,0.12)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '0.375rem', padding: '0.3rem 0.65rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' },
  th: { padding: '0.75rem 1rem', textAlign: 'left', color: 'var(--ako-text-2)', fontWeight: '600', fontSize: '0.85rem', borderBottom: '1px solid var(--ako-border)' },
  td: { padding: '0.75rem 1rem', color: 'var(--ako-text)', borderBottom: '1px solid var(--ako-border)', fontSize: '0.9rem' },
  input: { width: '100%', background: 'var(--ako-input-bg)', border: '1px solid var(--ako-border-in)', borderRadius: '0.5rem', padding: '0.75rem', color: 'var(--ako-text)', marginBottom: '0.75rem', outline: 'none', boxSizing: 'border-box', fontSize: '0.9rem' },
  searchInput: { background: 'var(--ako-input-bg)', border: '1px solid var(--ako-border-in)', borderRadius: '0.5rem', padding: '0.6rem 1rem', color: 'var(--ako-text)', outline: 'none', fontSize: '0.9rem', width: '220px', minHeight: '40px' },
  label: { display: 'block', color: 'var(--ako-text-2)', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.3rem' },
  emptyState: { textAlign: 'center', padding: '3rem 2rem', color: 'var(--ako-text-2)', fontSize: '0.95rem' },
  badge: (status) => {
    const c = { paid: '#86efac', active: '#86efac', pending: '#fed7aa', draft: '#d8b4fe', planning: '#93c5fd', 'in-progress': '#67e8f9', completed: '#86efac', cancelled: '#fca5a5', user: '#cbd5e1', admin: '#fbbf24', starter: '#93c5fd', pro: '#818cf8', enterprise: '#fbbf24' };
    const bg = { paid: 'rgba(34,197,94,0.15)', active: 'rgba(34,197,94,0.15)', pending: 'rgba(251,146,60,0.15)', draft: 'rgba(168,85,247,0.15)', planning: 'rgba(59,130,246,0.15)', 'in-progress': 'rgba(6,182,212,0.15)', completed: 'rgba(34,197,94,0.15)', cancelled: 'rgba(239,68,68,0.15)', user: 'rgba(100,116,139,0.15)', admin: 'rgba(251,191,36,0.15)', starter: 'rgba(59,130,246,0.15)', pro: 'rgba(129,140,248,0.15)', enterprise: 'rgba(251,191,36,0.15)' };
    return { display: 'inline-block', padding: '0.2rem 0.7rem', borderRadius: '999px', fontSize: '0.8rem', fontWeight: '600', color: c[status] || '#cbd5e1', background: bg[status] || 'rgba(100,116,139,0.15)' };
  },
};

// ── Loading Spinner ──────────────────────────────────────────────────────────

const LoadingSpinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4rem' }}>
    <div style={{ width: '40px', height: '40px', border: '3px solid #334155', borderTop: '3px solid #3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
  </div>
);

// ── Modal ────────────────────────────────────────────────────────────────────

const Modal = ({ title, fields, onSave, onCancel }) => {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--ako-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '1rem' }} role="dialog" aria-modal="true">
      <div style={{ background: 'var(--ako-surface)', border: '1px solid var(--ako-border)', borderRadius: '1rem', padding: '2rem', width: '100%', maxWidth: '600px', boxShadow: 'var(--ako-shadow)', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ color: 'var(--ako-heading)', marginTop: 0, marginBottom: '1.5rem' }}>{title}</h3>
        {fields.map(f => (
          <div key={f.key}>
            <label style={s.label}>{f.label}</label>
            {f.type === 'select' ? (
              <select onChange={e => setData({ ...data, [f.key]: e.target.value })} style={{ ...s.input, appearance: 'none' }}>
                <option value="">-- Choisir --</option>
                {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : f.type === 'textarea' ? (
              <textarea placeholder={f.placeholder || f.label} onChange={e => setData({ ...data, [f.key]: e.target.value })}
                rows={8} style={{ ...s.input, resize: 'vertical', fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: '1.6' }} />
            ) : (
              <input type={f.type || 'text'} placeholder={f.placeholder || f.label}
                onChange={e => setData({ ...data, [f.key]: e.target.value })} style={s.input} />
            )}
          </div>
        ))}
        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
          <button onClick={onCancel} style={{ ...s.btnRed, flex: 1 }}>Annuler</button>
          <button disabled={saving} onClick={async () => {
            setSaving(true);
            try { await onSave(data); } catch (e) { console.error('Modal onSave error:', e); }
            setSaving(false);
          }}
            style={{ ...s.btn, flex: 1, opacity: saving ? 0.6 : 1 }}>
            {saving ? '⏳ Envoi...' : '📤 Envoyer'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Demande Modal ────────────────────────────────────────────────────────────

const DemandeModal = ({ user, type, onClose }) => {
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const placeholder = type === 'facture'
    ? `Exemple :\nclient : SARL TechNova\nadresse : 12 rue des Lilas, 75015 Paris\nemail : contact@technova.fr\nSIRET : 823 456 789 00012\n\nprestations :\n- Création site e-commerce   quantité 1   1800 € HT\n- Design graphique du logo   quantité 1    650 € HT\n\nTVA 20%\nPaiement sous 30 jours\nIBAN : FR76 3000 4020 0100 0100 1234 567\n\nmerci de l'envoyer à contact@client.fr`
    : `Exemple :\nclient : SARL TechNova\nadresse : 12 rue des Lilas, 75015 Paris\nemail : contact@technova.fr\n\nprestations :\n- Développement application web   quantité 1   3500 € HT\n- Maintenance mensuelle           quantité 12   200 € HT/mois\n\nValidité du devis : 30 jours\nTVA 20%`;

  const handleSend = async () => {
    if (!message.trim()) return;
    setLoading(true);
    await notifyN8n('demande_' + type, {
      type, message, client_name: user.name, client_email: user.email,
      date: new Date().toLocaleDateString('fr-FR'),
    });
    setSent(true);
    setLoading(false);
  };

  if (sent) return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--ako-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} role="dialog" aria-modal="true">
      <div style={{ background: 'var(--ako-surface)', border: '1px solid var(--ako-border)', borderRadius: '1rem', padding: '3rem', maxWidth: '400px', textAlign: 'center', boxShadow: 'var(--ako-shadow)' }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>✅</div>
        <h3 style={{ color: 'var(--ako-heading)', marginBottom: '0.5rem' }}>Demande envoyée !</h3>
        <p style={{ color: 'var(--ako-text-2)', marginBottom: '1.5rem' }}>Votre agent va traiter votre demande et vous recontacter.</p>
        <button onClick={onClose} style={s.btn}>Fermer</button>
      </div>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--ako-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '1rem' }} role="dialog" aria-modal="true">
      <div style={{ background: 'var(--ako-surface)', border: '1px solid var(--ako-border)', borderRadius: '1rem', padding: '2rem', width: '100%', maxWidth: '650px', boxShadow: 'var(--ako-shadow)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ color: 'var(--ako-heading)', margin: 0 }}>{type === 'facture' ? '📄 Demande de facture' : '💰 Demande de devis'}</h3>
          <button aria-label="Fermer" onClick={onClose} style={{ ...s.btnRed, padding: '0.3rem 0.75rem' }}>✕</button>
        </div>
        <div style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1.5rem' }}>
          <p style={{ color: '#93c5fd', margin: 0, fontSize: '0.875rem' }}>
            💡 Décrivez votre demande en détail. Plus vous donnez d'informations, plus votre agent pourra traiter rapidement.
          </p>
        </div>
        <label style={s.label}>Votre demande</label>
        <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder={placeholder} rows={15}
          style={{ ...s.input, resize: 'vertical', minHeight: '300px', fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: '1.7' }} />
        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
          <button onClick={onClose} style={{ ...s.btnRed, flex: 1 }}>Annuler</button>
          <button onClick={handleSend} disabled={loading || !message.trim()}
            style={{ ...s.btn, flex: 1, opacity: loading || !message.trim() ? 0.6 : 1 }}>
            {loading ? '⏳ Envoi...' : "📤 Envoyer à l'agent"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Subcomponents (defined outside Dashboard to avoid re-mount on re-render) ─

const HoverRow = ({ children, ...props }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <tr {...props}
      style={{ background: hovered ? 'rgba(59,130,246,0.06)' : 'transparent', transition: 'background 0.15s' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      {children}
    </tr>
  );
};

const SearchBar = ({ value, onChange, placeholder = 'Rechercher...' }) => (
  <input value={value} onChange={e => onChange(e.target.value)}
    placeholder={`🔍 ${placeholder}`} style={s.searchInput} className="ako-search-inp" />
);

// ── FactureDevis Modal ────────────────────────────────────────────────────────

const EMPTY_ITEM = { description: '', qty: 1, unit_price: 0 };

const FactureDevisModal = ({ type, user, clients, artisanProfile, invoiceCount, quoteCount, onClose, onSuccess, toast }) => {
  const isInvoice = type === 'invoice';
  const today = new Date().toISOString().split('T')[0];
  const due = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];
  const numero = isInvoice
    ? `FAC-${new Date().getFullYear()}-${String((invoiceCount || 0) + 1).padStart(3, '0')}`
    : `DEV-${new Date().getFullYear()}-${String((quoteCount || 0) + 1).padStart(3, '0')}`;

  const [selectedClientId, setSelectedClientId] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [date, setDate] = useState(today);
  const [dueDate, setDueDate] = useState(due);
  const [tvaRate, setTvaRate] = useState(20);
  const [notes, setNotes] = useState('');
  const [sending, setSending] = useState(false);

  const handleClientSelect = (id) => {
    setSelectedClientId(id);
    const c = clients.find(c => String(c.id) === String(id));
    if (c) {
      setClientName(c.name || '');
      setClientEmail(c.email || '');
      setClientAddress(c.address || '');
    }
  };

  const updateItem = (i, key, val) => {
    const updated = [...items];
    updated[i] = { ...updated[i], [key]: val };
    setItems(updated);
  };

  const total_ht = items.reduce((s, it) => s + (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0), 0);
  const tva_amount = total_ht * (tvaRate / 100);
  const total_ttc = total_ht + tva_amount;

  const handleSubmit = async () => {
    if (!clientEmail) return toast('Email client requis', 'error');
    if (!clientName) return toast('Nom client requis', 'error');
    if (items.every(it => !it.description)) return toast('Au moins une prestation requise', 'error');
    setSending(true);
    try {
      const activeLines = items.filter(it => it.description);
      // Normaliser les champs artisan pour le template n8n
      const artisanPayload = artisanProfile
        ? {
            name: artisanProfile.nom_entreprise || user.name,
            company: artisanProfile.nom_entreprise,
            email: user.email,
            phone: artisanProfile.telephone || '',
            address: artisanProfile.adresse || '',
            siret: artisanProfile.siret || '',
            tva: artisanProfile.tva_intracommunautaire || '',
          }
        : { name: user.name, email: user.email };

      const basePayload = {
        type,
        artisan: artisanPayload,
        client: { name: clientName, email: clientEmail, address: clientAddress },
        items: activeLines,
        numero,
        date,
        due_date: dueDate,
        tva_rate: parseFloat(tvaRate),
        notes,
      };

      if (isInvoice) {
        // Facture : notifier n8n d'abord (génère le PDF), puis sauvegarder
        const res = await notifyN8n(type, basePayload);
        if (res && res.success === false) throw new Error(res.error || 'Erreur inconnue');
        await api.createInvoice({
          client: clientName, clientEmail, clientAddress,
          amount: total_ttc, amountHt: total_ht,
          tvaRate: parseFloat(tvaRate), tvaAmount: tva_amount, amountTtc: total_ttc,
          dueDate,
          description: activeLines.map(it => `${it.description} x${it.qty} à ${it.unit_price}€`).join('\n') + (notes ? '\n' + notes : ''),
          lines: activeLines.map(it => ({ description: it.description, quantity: parseFloat(it.qty) || 1, unit_price: parseFloat(it.unit_price) || 0, tva_rate: parseFloat(tvaRate) })),
        });
      } else {
        // Devis : sauvegarder EN PREMIER pour obtenir l'ID et générer le lien signature
        const savedQuote = await api.createQuote({
          client: clientName,
          clientEmail,
          clientAddress,
          clientId: selectedClientId || null,
          amount: total_ttc,
          expiryDate: dueDate,
          description: activeLines.map(it => `${it.description} x${it.qty} à ${it.unit_price}€`).join('\n') + (notes ? '\n' + notes : ''),
        });
        // Générer le lien de signature
        let sign_url = null;
        if (savedQuote && savedQuote.id) {
          try {
            const shareRes = await api.shareQuote(savedQuote.id);
            sign_url = shareRes.url;
          } catch (_) { /* non bloquant */ }
        }
        // Notifier n8n avec le lien signature inclus
        const payload = { ...basePayload, sign_url, quote_number: savedQuote?.number };
        const res = await notifyN8n(type, payload);
        if (res && res.success === false) throw new Error(res.error || 'Erreur inconnue');
      }

      toast(`${isInvoice ? 'Facture' : 'Devis'} envoyé par email à ${clientEmail} ✉️`);
      onSuccess && onSuccess();
      onClose();
    } catch (e) {
      toast(e.message || 'Erreur envoi', 'error');
    }
    setSending(false);
  };

  const inp = { background: 'rgba(15,23,42,0.8)', border: '1px solid var(--ako-border)', borderRadius: '0.5rem', padding: '0.6rem 0.75rem', color: 'white', width: '100%', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' };
  const lbl = { color: 'var(--ako-text-2)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px', display: 'block' };
  const color = isInvoice ? '#10b981' : '#8b5cf6';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--ako-overlay)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 100, padding: '1rem', overflowY: 'auto' }} role="dialog" aria-modal="true">
      <div className="ako-modal-pad" style={{ background: 'var(--ako-surface)', border: `1px solid ${color}40`, borderRadius: '1rem', padding: '2rem', width: '100%', maxWidth: '700px', boxShadow: 'var(--ako-shadow)', marginTop: '1rem', marginBottom: '1rem' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <h3 style={{ color: 'var(--ako-heading)', margin: 0 }}>{isInvoice ? '📄 Nouvelle facture' : '💰 Nouveau devis'}</h3>
            <span style={{ color, fontSize: '0.875rem', fontWeight: '600' }}>{numero}</span>
          </div>
          <button aria-label="Fermer" onClick={onClose} style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', borderRadius: '0.5rem', padding: '0.3rem 0.75rem', cursor: 'pointer' }}>✕</button>
        </div>

        {/* Client */}
        <div style={{ background: 'rgba(15,23,42,0.5)', borderRadius: '0.75rem', padding: '1rem', marginBottom: '1rem' }}>
          <div style={{ fontWeight: '600', color: 'var(--ako-heading)', marginBottom: '0.75rem', fontSize: '0.875rem' }}>👤 Client</div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={lbl}>Choisir un client existant</label>
            <select value={selectedClientId} onChange={e => handleClientSelect(e.target.value)} style={{ ...inp, appearance: 'none' }}>
              <option value="">— Sélectionner —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name} {c.email ? `(${c.email})` : ''}</option>)}
            </select>
          </div>
          <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={lbl}>Nom *</label>
              <input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Nom du client" style={inp} />
            </div>
            <div>
              <label style={lbl}>Email * (destinataire)</label>
              <input type="email" value={clientEmail} onChange={e => setClientEmail(e.target.value)} placeholder="email@client.fr" style={inp} />
            </div>
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <label style={lbl}>Adresse</label>
            <input value={clientAddress} onChange={e => setClientAddress(e.target.value)} placeholder="Adresse du client" style={inp} />
          </div>
        </div>

        {/* Dates & TVA */}
        <div className="grid-3col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
          <div>
            <label style={lbl}>Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inp} />
          </div>
          <div>
            <label style={lbl}>{isInvoice ? "Date d'échéance" : 'Validité'}</label>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={inp} />
          </div>
          <div>
            <label style={lbl}>TVA (%)</label>
            <select value={tvaRate} onChange={e => setTvaRate(e.target.value)} style={{ ...inp, appearance: 'none' }}>
              {[0, 5.5, 10, 20].map(r => <option key={r} value={r}>{r}%</option>)}
            </select>
          </div>
        </div>

        {/* Items */}
        <div style={{ background: 'rgba(15,23,42,0.5)', borderRadius: '0.75rem', padding: '1rem', marginBottom: '1rem' }}>
          <div style={{ fontWeight: '600', color: 'var(--ako-heading)', marginBottom: '0.75rem', fontSize: '0.875rem' }}>📋 Prestations</div>
          <div className="table-wrap">
          {items.map((item, i) => (
            <div key={i} className="ako-items-row" style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 32px', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
              <input value={item.description} onChange={e => updateItem(i, 'description', e.target.value)} placeholder="Description de la prestation" style={inp} />
              <input type="number" value={item.qty} onChange={e => updateItem(i, 'qty', e.target.value)} min="1" style={{ ...inp, textAlign: 'center' }} />
              <input type="number" value={item.unit_price} onChange={e => updateItem(i, 'unit_price', e.target.value)} min="0" step="0.01" placeholder="Prix HT" style={{ ...inp, textAlign: 'right' }} />
              <button onClick={() => setItems(items.filter((_, j) => j !== i))} disabled={items.length === 1}
                style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', borderRadius: '0.4rem', cursor: items.length === 1 ? 'not-allowed' : 'pointer', opacity: items.length === 1 ? 0.4 : 1, height: '36px', width: '32px' }} aria-label="Supprimer la ligne">✕</button>
            </div>
          ))}
          <div className="ako-items-row" style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 32px', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <div style={{ color: '#64748b', fontSize: '0.7rem', paddingLeft: '2px' }}>Description</div>
            <div style={{ color: '#64748b', fontSize: '0.7rem', textAlign: 'center' }}>Qté</div>
            <div style={{ color: '#64748b', fontSize: '0.7rem', textAlign: 'right' }}>Prix HT (€)</div>
            <div />
          </div>
          </div>
          <button onClick={() => setItems([...items, { ...EMPTY_ITEM }])}
            style={{ background: 'rgba(59,130,246,0.1)', border: '1px dashed #334155', color: '#64748b', borderRadius: '0.5rem', padding: '0.5rem', cursor: 'pointer', width: '100%', fontSize: '0.875rem' }}>
            + Ajouter une ligne
          </button>
        </div>

        {/* Totaux */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
          <div style={{ minWidth: '240px', background: 'rgba(15,23,42,0.5)', borderRadius: '0.75rem', padding: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--ako-text-2)', fontSize: '0.875rem', marginBottom: '6px' }}>
              <span>Total HT</span><span style={{ color: 'white' }}>{total_ht.toFixed(2)} €</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--ako-text-2)', fontSize: '0.875rem', marginBottom: '8px' }}>
              <span>TVA ({tvaRate}%)</span><span style={{ color: 'white' }}>{tva_amount.toFixed(2)} €</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', background: color, borderRadius: '0.5rem', padding: '0.5rem 0.75rem' }}>
              <span style={{ color: 'white', fontWeight: '700' }}>Total TTC</span>
              <span style={{ color: 'white', fontWeight: '700' }}>{total_ttc.toFixed(2)} €</span>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={lbl}>Notes (optionnel)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="IBAN, conditions de paiement, mentions légales..." style={{ ...inp, resize: 'vertical' }} />
        </div>

        <div style={{ display: 'flex', gap: '1rem' }}>
          <button onClick={onClose} style={{ flex: 1, background: 'rgba(100,116,139,0.15)', border: '1px solid var(--ako-border)', color: 'var(--ako-text-2)', borderRadius: '0.5rem', padding: '0.75rem', cursor: 'pointer' }}>Annuler</button>
          <button onClick={handleSubmit} disabled={sending}
            style={{ flex: 2, background: `linear-gradient(135deg,${color},${color}cc)`, border: 'none', color: 'white', borderRadius: '0.5rem', padding: '0.75rem', cursor: sending ? 'not-allowed' : 'pointer', fontWeight: '700', opacity: sending ? 0.7 : 1, fontSize: '0.95rem' }}>
            {sending ? '⏳ Génération PDF...' : `📤 Envoyer ${isInvoice ? 'la facture' : 'le devis'} par email`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Edit Client Modal ────────────────────────────────────────────────────────

const EditClientModal = ({ client, onSave, onCancel }) => {
  const [data, setData] = useState({ name: client.name || '', email: client.email || '', phone: client.phone || '', address: client.address || '' });
  const [saving, setSaving] = useState(false);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--ako-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '1rem' }} role="dialog" aria-modal="true">
      <div style={{ background: 'var(--ako-surface)', border: '1px solid var(--ako-border)', borderRadius: '1rem', padding: '2rem', width: '100%', maxWidth: '500px', boxShadow: 'var(--ako-shadow)' }}>
        <h3 style={{ color: 'var(--ako-heading)', marginTop: 0, marginBottom: '1.5rem' }}>✏️ Modifier le client</h3>
        {[{ key: 'name', label: 'Nom' }, { key: 'email', label: 'Email', type: 'email' }, { key: 'phone', label: 'Téléphone' }, { key: 'address', label: 'Adresse' }].map(f => (
          <div key={f.key}>
            <label style={s.label}>{f.label}</label>
            <input type={f.type || 'text'} value={data[f.key]} onChange={e => setData({ ...data, [f.key]: e.target.value })} style={s.input} />
          </div>
        ))}
        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
          <button onClick={onCancel} style={{ ...s.btnRed, flex: 1 }}>Annuler</button>
          <button disabled={saving} onClick={async () => { setSaving(true); await onSave(data); setSaving(false); }}
            style={{ ...s.btn, flex: 1, opacity: saving ? 0.6 : 1 }}>
            {saving ? '⏳ Sauvegarde...' : '💾 Sauvegarder'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Locked Feature Banner ────────────────────────────────────────────────────

const LockedBanner = ({ title, onUnlock }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4rem 2rem', textAlign: 'center' }}>
    <div style={{ fontSize: '3.5rem', marginBottom: '1rem' }}>🔒</div>
    <h3 style={{ color: 'white', margin: '0 0 0.5rem', fontSize: '1.3rem' }}>{title}</h3>
    <p style={{ color: '#64748b', margin: '0 0 1.5rem', fontSize: '0.9rem', maxWidth: '380px', lineHeight: '1.6' }}>
      Cette fonctionnalité est incluse dans le plan <strong style={{ color: '#60a5fa' }}>Essentiel à 19€/mois</strong>.
    </p>
    <button onClick={() => onUnlock('abonnement')} style={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.65rem 1.5rem', cursor: 'pointer', fontWeight: '700', fontSize: '0.9rem' }}>
      Voir les offres
    </button>
  </div>
);

// ── Empty State ──────────────────────────────────────────────────────────────

const EmptyState = ({ icon, title, description, action, actionLabel, secondAction, secondLabel }) => (
  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'4rem 2rem', textAlign:'center', minHeight:'260px' }}>
    <div style={{ fontSize:'3rem', marginBottom:'1rem', lineHeight:1 }}>{icon}</div>
    <h3 style={{ color:'#e2e8f0', margin:'0 0 0.5rem', fontSize:'1.1rem', fontWeight:'700' }}>{title}</h3>
    {description && <p style={{ color:'#64748b', margin:'0 0 1.5rem', fontSize:'0.88rem', maxWidth:'340px', lineHeight:'1.6' }}>{description}</p>}
    {action && (
      <div style={{ display:'flex', gap:'0.75rem', flexWrap:'wrap', justifyContent:'center' }}>
        <button onClick={action} style={{ background:'linear-gradient(135deg,#3b82f6,#2563eb)', color:'#fff', border:'none', borderRadius:'0.6rem', padding:'0.65rem 1.5rem', cursor:'pointer', fontWeight:'600', fontSize:'0.9rem' }}>
          {actionLabel}
        </button>
        {secondAction && (
          <button onClick={secondAction} style={{ background:'transparent', color:'#64748b', border:'1px solid #334155', borderRadius:'0.6rem', padding:'0.65rem 1.25rem', cursor:'pointer', fontWeight:'500', fontSize:'0.9rem' }}>
            {secondLabel}
          </button>
        )}
      </div>
    )}
  </div>
);

// ── Onboarding Wizard ────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Votre entreprise', icon: '🏢' },
  { id: 2, label: 'Adresse & légal', icon: '📍' },
  { id: 3, label: 'Coordonnées bancaires', icon: '🏦' },
];

const OnboardingWizard = ({ user, artisanProfile, onComplete, toast }) => {
  const [step, setStep] = React.useState(1);
  const [saving, setSaving] = React.useState(false);
  const [form, setForm] = React.useState({
    nom_entreprise: artisanProfile?.nom_entreprise || user?.name || '',
    metier: artisanProfile?.metier || '',
    telephone: artisanProfile?.telephone || user?.phone || '',
    email_pro: artisanProfile?.email_pro || user?.email || '',
    adresse: artisanProfile?.adresse || '',
    code_postal: artisanProfile?.code_postal || '',
    ville: artisanProfile?.ville || '',
    siret: artisanProfile?.siret || '',
    tva_number: artisanProfile?.tva_number || '',
    iban: artisanProfile?.iban || '',
    bic: artisanProfile?.bic || '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const inp = {
    width: '100%', background: 'var(--ako-bg)', border: '1px solid var(--ako-border)', borderRadius: '0.5rem',
    color: 'var(--ako-text)', padding: '0.7rem 1rem', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box',
  };
  const label = { display: 'block', color: 'var(--ako-text-2)', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.35rem', textTransform: 'uppercase', letterSpacing: '0.05em' };
  const field = { marginBottom: '1.1rem' };

  const handleFinish = async () => {
    setSaving(true);
    try {
      await api.updateArtisan(form);
      localStorage.setItem('ako_onboarded', '1');
      toast('Profil complété — bienvenue !');
      onComplete(form);
    } catch (e) {
      toast(e.message || 'Erreur lors de la sauvegarde', 'error');
    } finally {
      setSaving(false);
    }
  };

  const progress = ((step - 1) / (STEPS.length - 1)) * 100;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--ako-overlay)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }} role="dialog" aria-modal="true">
      <div style={{ width: '100%', maxWidth: '520px', background: 'var(--ako-surface)', borderRadius: '1rem', overflow: 'hidden', boxShadow: 'var(--ako-shadow)' }}>

        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg,#1e3a5f,#1e40af)', padding: '1.5rem 2rem 1rem' }}>
          <div style={{ fontSize: '0.75rem', color: '#93c5fd', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
            Bienvenue sur FacturArt 👋
          </div>
          <h2 style={{ color: 'white', margin: '0 0 1rem', fontSize: '1.3rem', fontWeight: '800' }}>
            Configurons votre profil
          </h2>
          {/* Progress bar */}
          <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '99px', height: '6px', marginBottom: '0.75rem' }}>
            <div style={{ width: `${progress}%`, height: '100%', background: 'linear-gradient(90deg,#60a5fa,#3b82f6)', borderRadius: '99px', transition: 'width 0.4s ease' }} />
          </div>
          {/* Steps */}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {STEPS.map(s => (
              <div key={s.id} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: '1.1rem', opacity: step >= s.id ? 1 : 0.4 }}>{s.icon}</div>
                <div style={{ fontSize: '0.65rem', color: step >= s.id ? '#bfdbfe' : '#475569', fontWeight: step === s.id ? '700' : '400', marginTop: '2px' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '1.75rem 2rem' }}>
          {step === 1 && (
            <div>
              <div style={field}>
                <label style={label}>Nom de l'entreprise *</label>
                <input style={inp} value={form.nom_entreprise} onChange={e => set('nom_entreprise', e.target.value)} placeholder="Mon Atelier Pro" />
              </div>
              <div style={field}>
                <label style={label}>Métier / Activité *</label>
                <input style={inp} value={form.metier} onChange={e => set('metier', e.target.value)} placeholder="Plombier, Électricien, Menuisier..." />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div style={field}>
                  <label style={label}>Téléphone</label>
                  <input style={inp} value={form.telephone} onChange={e => set('telephone', e.target.value)} placeholder="06 00 00 00 00" />
                </div>
                <div style={field}>
                  <label style={label}>Email professionnel</label>
                  <input style={inp} value={form.email_pro} onChange={e => set('email_pro', e.target.value)} placeholder="contact@monentreprise.fr" />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <div style={field}>
                <label style={label}>Adresse</label>
                <input style={inp} value={form.adresse} onChange={e => set('adresse', e.target.value)} placeholder="12 rue de la Paix" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.75rem' }}>
                <div style={field}>
                  <label style={label}>Code postal</label>
                  <input style={inp} value={form.code_postal} onChange={e => set('code_postal', e.target.value)} placeholder="75001" />
                </div>
                <div style={field}>
                  <label style={label}>Ville</label>
                  <input style={inp} value={form.ville} onChange={e => set('ville', e.target.value)} placeholder="Paris" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div style={field}>
                  <label style={label}>SIRET</label>
                  <input style={inp} value={form.siret} onChange={e => set('siret', e.target.value)} placeholder="123 456 789 00010" />
                </div>
                <div style={field}>
                  <label style={label}>N° TVA intracommunautaire</label>
                  <input style={inp} value={form.tva_number} onChange={e => set('tva_number', e.target.value)} placeholder="FR12345678900" />
                </div>
              </div>
              <p style={{ color: '#475569', fontSize: '0.78rem', margin: '0.25rem 0 0' }}>Ces informations apparaîtront sur vos factures et devis.</p>
            </div>
          )}

          {step === 3 && (
            <div>
              <p style={{ color: 'var(--ako-text-2)', fontSize: '0.85rem', margin: '0 0 1.25rem', lineHeight: '1.6' }}>
                Vos coordonnées bancaires apparaissent en bas de vos factures pour faciliter le virement de vos clients.
              </p>
              <div style={field}>
                <label style={label}>IBAN</label>
                <input style={inp} value={form.iban} onChange={e => set('iban', e.target.value)} placeholder="FR76 3000 6000 0112 3456 7890 189" />
              </div>
              <div style={field}>
                <label style={label}>BIC / SWIFT</label>
                <input style={inp} value={form.bic} onChange={e => set('bic', e.target.value)} placeholder="BNPAFRPPXXX" />
              </div>
              <div style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginTop: '0.5rem' }}>
                <p style={{ color: '#60a5fa', fontSize: '0.78rem', margin: 0 }}>
                  🔒 Ces données sont stockées de manière sécurisée et ne sont jamais partagées avec des tiers.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '1rem 2rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #334155' }}>
          {step > 1
            ? <button onClick={() => setStep(s => s - 1)} style={{ background: 'none', border: '1px solid var(--ako-border)', color: 'var(--ako-text-2)', borderRadius: '0.5rem', padding: '0.65rem 1.25rem', cursor: 'pointer', fontSize: '0.9rem' }}>← Retour</button>
            : <button onClick={() => { localStorage.setItem('ako_onboarded', '1'); onComplete(null); }} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '0.85rem', padding: '0.65rem 0' }}>Passer pour l'instant</button>
          }
          {step < STEPS.length
            ? <button onClick={() => setStep(s => s + 1)} disabled={!form.nom_entreprise} style={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.65rem 1.5rem', cursor: form.nom_entreprise ? 'pointer' : 'not-allowed', fontWeight: '700', fontSize: '0.9rem', opacity: form.nom_entreprise ? 1 : 0.5 }}>
                Suivant →
              </button>
            : <button onClick={handleFinish} disabled={saving} style={{ background: 'linear-gradient(135deg,#22c55e,#16a34a)', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.65rem 1.5rem', cursor: 'pointer', fontWeight: '700', fontSize: '0.9rem' }}>
                {saving ? 'Sauvegarde...' : '✅ Terminer'}
              </button>
          }
        </div>
      </div>
    </div>
  );
};

// ── Dashboard ────────────────────────────────────────────────────────────────

const Dashboard = ({ user, onLogout }) => {
  const t = useT();
  const [tab, setTab] = useState('dashboard');
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [users, setUsers] = useState([]);
  const [artisans, setArtisans] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [stats, setStats] = useState(null);
  const [modal, setModal] = useState(null);
  const [editClient, setEditClient] = useState(null);
  const [demande, setDemande] = useState(null);
  const [factureModal, setFactureModal] = useState(null); // 'invoice' | 'quote' | null
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);
  const [artisanProfile, setArtisanProfile] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { toasts, toast, removeToast } = useToast();
  const [userPlan, setUserPlan] = useState(null); // { plan, source, expires_at }
  const [documents, setDocuments] = useState([]);
  const [ecritures, setEcritures] = useState([]);
  const [bilan, setBilan] = useState(null);
  const [tvaData, setTvaData] = useState(null);
  const [caData, setCaData] = useState(null);
  const [caCategorie, setCaCategorie] = useState('bic_services');
  const [exercices, setExercices] = useState([]);
  const [kanban, setKanban] = useState({});
  const [kanbanProject, setKanbanProject] = useState(null);
  const [catalogItems, setCatalogItems] = useState([]);
  const [catalogFilter, setCatalogFilter] = useState({ categorie: '', favori: false });
  const [portailTokens, setPortailTokens] = useState([]);
  const [depenses, setDepenses] = useState([]);
  const [depenseForm, setDepenseForm] = useState(null);
  const [depenseFiltreAnnee, setDepenseFiltreAnnee] = useState(String(new Date().getFullYear()));
  const [appointments, setAppointments] = useState([]);
  const [agendaMois, setAgendaMois] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; });
  const [rdvForm, setRdvForm] = useState(null); // null=fermé, {}=nouveau, {id,...}=édition
  const [rdvSaved, setRdvSaved] = useState(null); // dernier RDV enregistré (pour bouton envoi)
  const [rdvNotifying, setRdvNotifying] = useState(false);
  const [rdvNotifyEmail, setRdvNotifyEmail] = useState('');
  const [categories, setCategories] = useState([]);
  const [comptaFilters, setComptaFilters] = useState({ annee: new Date().getFullYear() });
  const [invoiceFilter, setInvoiceFilter] = useState('all');
  const [quoteFilter, setQuoteFilter] = useState('all');
  const [progressDismissed, setProgressDismissed] = useState(() => localStorage.getItem('ako_progress_dismissed') === '1');
  // Groupes sidebar repliables — Comptabilité replié par défaut (trop d'items)
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set(['Comptabilité']));
  const toggleGroup = (label) => setCollapsedGroups(prev => {
    const next = new Set(prev);
    next.has(label) ? next.delete(label) : next.add(label);
    return next;
  });

  useEffect(() => {
    api.getArtisan().then(d => {
      const artisan = d?.artisan || null;
      setArtisanProfile(artisan);
      // Afficher l'onboarding si jamais complété ET profil incomplet (nom_entreprise vide)
      const alreadyOnboarded = localStorage.getItem('ako_onboarded');
      if (!alreadyOnboarded && user?.role !== 'admin' && !artisan?.nom_entreprise) {
        setShowOnboarding(true);
      }
    }).catch(() => {});
    api.getPlan().then(d => setUserPlan(d || { plan: 'free' })).catch(() => setUserPlan({ plan: 'free' }));
    // Retour depuis Stripe → ouvrir onglet abonnement
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true' || params.get('cancelled') === 'true') {
      setTab('abonnement');
    }
    // PWA install prompt
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); setShowInstallBanner(true); };
    window.addEventListener('beforeinstallprompt', handler);
    // Mobile resize listener
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('resize', handleResize);
    };
  }, []); // eslint-disable-line

  const handleInstallApp = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') setShowInstallBanner(false);
    setInstallPrompt(null);
  };

  const apiFetchStats = async () => {
    try { return await api.getUserStats(); } catch { return null; }
  };

  const sendAgentMessage = async () => {
    if (!agentInput.trim() || agentLoading) return;
    const msg = agentInput.trim();
    setAgentInput('');
    setAgentMessages(p => [...p, { role: 'user', text: msg }]);
    setAgentLoading(true);
    try {
      const res = await fetch('https://n8n.akodigitale.fr/webhook/agent-ia', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, user_name: user.name }),
      });
      const data = await res.json();
      setAgentMessages(p => [...p, { role: 'assistant', text: data.reply || 'Désolé, pas de réponse.' }]);
    } catch {
      setAgentMessages(p => [...p, { role: 'assistant', text: 'Erreur de connexion à l\'agent.', error: true }]);
    }
    setAgentLoading(false);
  };

  const triggerSocialAutopilot = async () => {
    setSocialLoading(true);
    try {
      const res = await fetch('https://n8n.akodigitale.fr/webhook/social-trigger', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ triggered_by: user.name, date: new Date().toISOString() }),
      });
      const data = await res.json();
      setSocialStatus({ success: true, message: data.message || 'Autopilot lancé !', date: new Date().toLocaleString('fr-FR') });
      toast('Autopilot lancé — planning en cours sur Telegram !');
    } catch {
      setSocialStatus({ success: false, message: 'Erreur de connexion', date: new Date().toLocaleString('fr-FR') });
      toast('Erreur lors du lancement', 'error');
    }
    setSocialLoading(false);
  };

  const disconnectLinkedIn = () => {
    localStorage.removeItem('linkedin_token');
    localStorage.removeItem('linkedin_name');
    localStorage.removeItem('linkedin_sub');
    setLinkedinConnected(false);
    setLinkedinPostStatus(null);
  };

  const disconnectFacebook = () => {
    localStorage.removeItem('facebook_token');
    localStorage.removeItem('facebook_name');
    setFacebookConnected(false);
  };

  const disconnectInstagram = () => {
    localStorage.removeItem('instagram_token');
    localStorage.removeItem('instagram_name');
    setInstagramConnected(false);
  };

  const publishToLinkedIn = async () => {
    if (!linkedinContent.trim()) return;
    setLinkedinPosting(true);
    setLinkedinPostStatus(null);
    try {
      const token = localStorage.getItem('linkedin_token');
      const res = await fetch(
        import.meta.env.VITE_N8N_SOCIAL_POST || 'https://n8n.akodigitale.fr/webhook/ako-social-post',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: linkedinContent, linkedin_token: token }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur publication');
      setLinkedinPostStatus({ success: true, message: 'Post publié sur LinkedIn !' });
      setLinkedinContent('');
      toast('Post LinkedIn publié avec succès !');
    } catch (e) {
      setLinkedinPostStatus({ success: false, message: e.message || 'Erreur de publication' });
      toast('Erreur publication LinkedIn', 'error');
    }
    setLinkedinPosting(false);
  };

  useEffect(() => { setSearch(''); loadData(); }, [tab]); // eslint-disable-line

  const safeLoad = async (fn, fallback) => {
    try { return await fn(); } catch (e) {
      if (e.status === 403) return fallback;
      throw e;
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      if (tab === 'dashboard') {
        const [c, i, p, q, st] = await Promise.all([
          api.getClients(),
          safeLoad(api.getInvoices, { invoices: [] }),
          api.getProjects().catch(() => ({ projects: [] })),
          safeLoad(api.getQuotes, { quotes: [] }),
          apiFetchStats(),
        ]);
        setClients(c.clients || []); setInvoices(i.invoices || []);
        setProjects(p.projects || []); setQuotes(q.quotes || []);
        setUserStats(st);
        if (user.role === 'admin') { const adm = await api.getStats(); setStats(adm || {}); }
      }
      if (tab === 'clients') { const d = await api.getClients(); setClients(d.clients || []); }
      if (tab === 'invoices') { const d = await safeLoad(api.getInvoices, { invoices: [] }); setInvoices(d.invoices || []); }
      if (tab === 'quotes') { const d = await safeLoad(api.getQuotes, { quotes: [] }); setQuotes(d.quotes || []); }
      if (tab === 'projects') { const d = await api.getKanban().catch(() => ({ kanban: {}, projects: [] })); setProjects(d.kanban ? Object.values(d.kanban).flat() : []); setKanban(d.kanban || {}); }
      if (tab === 'proposals') { const d = await safeLoad(api.getProposals, { proposals: [] }); setProposals(d.proposals || []); }
      if (tab === 'users' && user.role === 'admin') { const d = await api.getUsers(); setUsers(d || []); }
      if (tab === 'artisans' && user.role === 'admin') { const d = await api.getArtisans(); setArtisans(d.artisans || d || []); }
      if (tab === 'access-codes' && user.role === 'admin') { const d = await api.listCodes(); setAccessCodes(d.codes || []); }
      if (tab === 'documents') {
        const [docs, cats] = await Promise.all([api.getDocuments(comptaFilters), api.getCategories()]);
        setDocuments(docs.documents || []);
        setCategories(cats.categories || []);
      }
      if (tab === 'grand-livre') {
        const [ecrs, cats] = await Promise.all([api.getEcritures(comptaFilters), api.getCategories()]);
        setEcritures(ecrs.ecritures || []);
        setCategories(cats.categories || []);
      }
      if (tab === 'bilan') {
        const [bil, cats] = await Promise.all([api.getBilan(comptaFilters), api.getCategories()]);
        setBilan(bil);
        setCategories(cats.categories || []);
      }
      if (tab === 'tva') {
        const d = await api.getTvaDeclaration(comptaFilters.annee);
        setTvaData(d);
      }
      if (tab === 'ca-micro') {
        const d = await api.getCaDeclaration(comptaFilters.annee, caCategorie);
        setCaData(d);
      }
      if (tab === 'cloture') {
        const d = await api.getExercices();
        setExercices(d.exercices || []);
      }
      if (tab === 'catalogue') {
        const params = {};
        if (catalogFilter.categorie) params.categorie = catalogFilter.categorie;
        if (catalogFilter.favori) params.favori = 'true';
        const d = await api.getCatalog(params);
        setCatalogItems(d.items || []);
      }
      if (tab === 'portail') {
        const d = await api.getPortailTokens();
        setPortailTokens(d.tokens || []);
      }
      if (tab === 'depenses') {
        const d = await api.getDepenses({ annee: depenseFiltreAnnee });
        setDepenses(d.depenses || []);
      }
      if (tab === 'agenda') {
        const d = await api.getAppointments({ mois: agendaMois });
        setAppointments(d.appointments || []);
      }
    } catch (e) {
      toast(e.message || 'Erreur lors du chargement', 'error');
    }
    setIsLoading(false);
  };

  const handleDeleteClient = async (id, name) => {
    try {
      await api.deleteClient(id);
      toast(`Client "${name}" supprimé`);
      loadData();
    } catch (e) { toast(e.message || 'Erreur suppression', 'error'); }
  };

  // Nouveau barème : starter(9€) < essentiel(19€) < pro(39€)
  // Les anciens plans (business, actif) sont mappés sur pro
  const isAdmin      = user?.role === 'admin';
  const hasStarter   = isAdmin || (userPlan && ['starter', 'essentiel', 'pro', 'business', 'actif'].includes(userPlan.plan));
  const hasEssentiel = isAdmin || (userPlan && ['essentiel', 'pro', 'business', 'actif'].includes(userPlan.plan));
  const hasPro       = isAdmin || (userPlan && ['pro', 'business', 'actif'].includes(userPlan.plan));

  const handleDeleteProject = async (id, name) => {
    try {
      await api.deleteProject(id);
      toast(`Projet "${name}" supprimé`);
      loadData();
    } catch (e) { toast(e.message || 'Erreur suppression', 'error'); }
  };

  // ── Navigation groupée ────────────────────────────────────────────────────
  // badge = étiquette plan affichée sur l'item verrouillé
  const navGroups = [
    {
      label: 'Principal',
      items: [
        { id: 'dashboard',   icon: '▣',  label: 'Tableau de bord' },
        { id: 'analytique',  icon: '📊', label: 'Analytique' },
        { id: 'clients',     icon: '◎',  label: 'Clients' },
        { id: 'portail',     icon: '🔗', label: 'Portail client',  locked: !hasStarter,  badge: 'Starter' },
        { id: 'agenda',      icon: '📅', label: 'Agenda',           locked: !hasStarter,  badge: 'Starter' },
        { id: 'profil',      icon: '◈',  label: 'Mon profil' },
      ],
    },
    {
      label: 'Facturation',
      items: [
        { id: 'invoices',  icon: '◻',  label: 'Factures',      locked: !hasStarter,   badge: 'Starter' },
        { id: 'quotes',    icon: '◇',  label: 'Devis',         locked: !hasStarter,   badge: 'Starter' },
        { id: 'contrats',  icon: '📄', label: 'Contrats',      locked: !hasStarter,   badge: 'Starter' },
        { id: 'livre-recettes', icon: '📒', label: 'Livre recettes' },
        { id: 'catalogue', icon: '🏷️', label: 'Catalogue',     locked: !hasEssentiel, badge: 'Essentiel' },
        { id: 'proposals', icon: '◈',  label: 'Propositions',  locked: !hasPro,       badge: 'Pro' },
        { id: 'projects',  icon: '◉',  label: 'Projets',       locked: !hasPro,       badge: 'Pro' },
      ],
    },
    {
      label: 'Comptabilité',
      items: [
        { id: 'depenses',    icon: '🛒', label: 'Achats & dépenses', locked: !hasEssentiel, badge: 'Essentiel' },
        { id: 'documents',   icon: '▦', label: 'Documents',        locked: !hasEssentiel, badge: 'Essentiel' },
        { id: 'grand-livre', icon: '▤', label: 'Grand livre',       locked: !hasEssentiel, badge: 'Essentiel' },
        { id: 'tva',         icon: '⊞', label: 'Déclaration TVA',   locked: !hasEssentiel, badge: 'Essentiel' },
        { id: 'ca-micro',    icon: '📋', label: 'Déclaration CA',    locked: !hasEssentiel, badge: 'Essentiel' },
        { id: 'cloture',     icon: '🔒', label: 'Clôture exercice',  locked: !hasEssentiel, badge: 'Essentiel' },
        { id: 'bilan',       icon: '▲', label: 'Bilan',              locked: !hasPro,       badge: 'Pro' },
      ],
    },
    {
      label: 'IA & Marketing',
      items: [
        { id: 'agent-ia', icon: '◆', label: 'Agent IA',        locked: !hasEssentiel, badge: 'Essentiel' },
        { id: 'social',   icon: '◈', label: 'Réseaux sociaux', locked: !hasPro,       badge: 'Pro' },
      ],
    },
    {
      label: 'Mon compte',
      items: [
        { id: 'abonnement', icon: '◎', label: 'Abonnement' },
        { id: 'parametres', icon: '◉', label: 'Paramètres' },
      ],
    },
    ...(user.role === 'admin'
      ? [{
          label: 'Administration',
          items: [
            { id: 'users',        icon: '◎', label: 'Utilisateurs' },
            { id: 'artisans',     icon: '◈', label: 'Artisans' },
            { id: 'access-codes', icon: '▣', label: 'Codes accès' },
          ],
        }]
      : []),
  ];

  // Flat nav for activeNav lookup
  const nav = navGroups.flatMap(g => g.items);

  const [userStats, setUserStats] = useState(null);
  const [accessCodes, setAccessCodes] = useState([]);
  const [genPlan, setGenPlan] = useState('essentiel');
  const [genCount, setGenCount] = useState(1);
  const [genLoading, setGenLoading] = useState(false);
  const [newCodes, setNewCodes] = useState([]);
  const [agentMessages, setAgentMessages] = useState([
    { role: 'assistant', text: 'Bonjour ! Je suis votre assistant FacturArt. Comment puis-je vous aider ?' }
  ]);
  const [agentInput, setAgentInput] = useState('');
  const [agentLoading, setAgentLoading] = useState(false);
  const [socialStatus, setSocialStatus] = useState(null);
  const [socialLoading, setSocialLoading] = useState(false);
  const [linkedinConnected, setLinkedinConnected] = useState(() => !!localStorage.getItem('linkedin_token'));
  const [linkedinContent, setLinkedinContent] = useState('');
  const [linkedinPosting, setLinkedinPosting] = useState(false);
  const [linkedinPostStatus, setLinkedinPostStatus] = useState(null);
  const [facebookConnected, setFacebookConnected] = useState(() => !!localStorage.getItem('facebook_token'));
  const [instagramConnected, setInstagramConnected] = useState(() => !!localStorage.getItem('instagram_token'));

  // Filtered data
  const q = search.toLowerCase();
  const filteredClients = clients.filter(c => !q || c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q));
  const filteredInvoices = invoices.filter(i => !q || i.number?.toLowerCase().includes(q) || i.client?.toLowerCase().includes(q));
  const filteredQuotes = quotes.filter(qt => !q || qt.number?.toLowerCase().includes(q));
  const filteredProposals = proposals.filter(p => !q || p.title?.toLowerCase().includes(q) || p.client_id?.toLowerCase().includes(q));

  // ── Tab renderers ──────────────────────────────────────────────────────────

  const renderDashboard = () => {
    // ── Données dérivées ────────────────────────────────────────────────────
    const monthlyData = (userStats?.monthly || []).map(m => ({ name: m.month, ca: parseFloat(m.total) || 0 }));
    const lastTwo = monthlyData.slice(-2);
    const currentCA = lastTwo[1]?.ca ?? 0;
    const prevCA    = lastTwo[0]?.ca ?? 0;
    const caGrowth  = prevCA > 0 ? Math.round(((currentCA - prevCA) / prevCA) * 100) : null;

    const pendingInvoices = invoices.filter(i => i.status === 'pending' || i.status === 'sent');
    const pendingQuotes   = quotes.filter(q => q.status === 'sent' || q.status === 'pending' || q.status === 'draft');

    const toFloat = x => parseFloat(x?.amount ?? x?.total_amount ?? x?.total ?? 0);
    const pipelineDevis = quotes.filter(q => q.status !== 'refused' && q.status !== 'cancelled').reduce((s, q) => s + toFloat(q), 0);
    const pipelineFact  = invoices.filter(i => i.status !== 'paid' && i.status !== 'cancelled').reduce((s, i) => s + toFloat(i), 0);
    const pipelinePaid  = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + toFloat(i), 0);
    const pipelineTotal = pipelineDevis + pipelineFact + pipelinePaid;

    return (
      <div>

        {/* ── Header + Quick Action Bar ─────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h2 style={{ color: 'var(--ako-heading)', margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>Bonjour {user.name} 👋</h2>
            <div style={{ color: '#64748b', fontSize: '0.875rem', marginTop: '0.25rem' }}>
              {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              style={{ background: 'rgba(59,130,246,0.12)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.25)', borderRadius: '2rem', padding: '0.45rem 1rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.83rem' }}
              onClick={() => setTab('clients')}>
              👥 + Client
            </button>
            <button
              style={{ background: hasEssentiel ? 'rgba(139,92,246,0.12)' : 'rgba(71,85,105,0.2)', color: hasEssentiel ? '#a78bfa' : '#475569', border: `1px solid ${hasEssentiel ? 'rgba(139,92,246,0.25)' : 'transparent'}`, borderRadius: '2rem', padding: '0.45rem 1rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.83rem' }}
              onClick={() => hasEssentiel ? setFactureModal('quote') : setTab('abonnement')}>
              💰 + Devis{!hasEssentiel && ' 🔒'}
            </button>
            <button
              style={{ background: hasEssentiel ? 'rgba(16,185,129,0.12)' : 'rgba(71,85,105,0.2)', color: hasEssentiel ? '#34d399' : '#475569', border: `1px solid ${hasEssentiel ? 'rgba(16,185,129,0.25)' : 'transparent'}`, borderRadius: '2rem', padding: '0.45rem 1rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.83rem' }}
              onClick={() => hasEssentiel ? setFactureModal('invoice') : setTab('abonnement')}>
              📄 + Facture{!hasEssentiel && ' 🔒'}
            </button>
            {hasEssentiel && (
              <button
                style={{ background: 'linear-gradient(135deg,rgba(139,92,246,0.25),rgba(59,130,246,0.25))', color: '#c4b5fd', border: '1px solid rgba(139,92,246,0.35)', borderRadius: '2rem', padding: '0.45rem 1rem', cursor: 'pointer', fontWeight: '700', fontSize: '0.83rem' }}
                onClick={() => setTab('agent-ia')}>
                ⚡ Agent IA
              </button>
            )}
          </div>
        </div>

        {/* ── Checklist progression gamifiée ───────────────────────────── */}
        {(() => {
          const steps = [
            {
              id: 'profil',
              icon: '👤',
              label: 'Complétez votre profil artisan',
              desc: 'Nom, métier, adresse, SIRET',
              done: !!(artisanProfile?.nom_entreprise && artisanProfile?.metier),
              action: () => setTab('profil'),
            },
            {
              id: 'client',
              icon: '👥',
              label: 'Ajoutez votre premier client',
              desc: 'Créez votre carnet de clients',
              done: clients.length > 0,
              action: () => setTab('clients'),
            },
            {
              id: 'invoice',
              icon: '📄',
              label: 'Créez votre première facture',
              desc: 'Facture PDF envoyée en 30 secondes',
              done: invoices.length > 0,
              action: () => hasEssentiel ? setFactureModal('invoice') : setTab('abonnement'),
              locked: !hasEssentiel,
            },
            {
              id: 'quote',
              icon: '💰',
              label: 'Envoyez un devis signable en ligne',
              desc: 'Le client accepte depuis son email',
              done: quotes.length > 0,
              action: () => hasEssentiel ? setFactureModal('quote') : setTab('abonnement'),
              locked: !hasEssentiel,
            },
            {
              id: 'ia',
              icon: '⚡',
              label: 'Essayez l\'agent IA',
              desc: 'Génération de factures par dictée',
              done: false,
              action: () => hasEssentiel ? setTab('agent-ia') : setTab('abonnement'),
              locked: !hasEssentiel,
            },
          ];
          const doneCount = steps.filter(s => s.done).length;
          const allDone   = doneCount === steps.length;

          if (progressDismissed || allDone) return null;

          const pct = Math.round((doneCount / steps.length) * 100);
          const pctColor = pct >= 80 ? '#10b981' : pct >= 40 ? '#f59e0b' : '#3b82f6';

          return (
            <div style={{ background: 'rgba(30,41,59,0.7)', border: '1px solid var(--ako-border)', borderRadius: '0.85rem', padding: '1.1rem 1.25rem', marginBottom: '1.25rem' }}>
              {/* Header checklist */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.85rem', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: '0.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ color: 'white', fontWeight: '700', fontSize: '0.92rem' }}>🚀 Démarrage — {doneCount}/{steps.length} étapes</span>
                      <span style={{ color: pctColor, fontWeight: '700', fontSize: '0.85rem' }}>{pct}%</span>
                    </div>
                    <div style={{ height: '5px', background: 'rgba(51,65,85,0.8)', borderRadius: '999px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg,${pctColor},${pctColor}cc)`, borderRadius: '999px', transition: 'width 0.6s ease' }} />
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => { setProgressDismissed(true); localStorage.setItem('ako_progress_dismissed','1'); }}
                  aria-label="Masquer la checklist"
                  style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '1.1rem', padding: '0.1rem', lineHeight: 1, flexShrink: 0 }}>×</button>
              </div>

              {/* Steps list */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                {steps.map(st => (
                  <div key={st.id}
                    onClick={() => st.action && st.action()}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.65rem', borderRadius: '0.5rem', cursor: st.done ? 'default' : 'pointer', background: st.done ? 'transparent' : 'rgba(255,255,255,0.02)', transition: 'background 0.15s' }}
                    onMouseEnter={e => { if (!st.done) e.currentTarget.style.background = 'rgba(59,130,246,0.06)'; }}
                    onMouseLeave={e => { if (!st.done) e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}>
                    {/* Checkbox */}
                    <div style={{ width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: '800', background: st.done ? 'rgba(16,185,129,0.2)' : 'rgba(51,65,85,0.5)', border: `2px solid ${st.done ? '#10b981' : '#334155'}`, color: st.done ? '#34d399' : 'transparent', transition: 'all 0.2s' }}>
                      {st.done ? '✓' : ''}
                    </div>
                    {/* Icon */}
                    <span style={{ fontSize: '1rem', opacity: st.done ? 0.5 : 1 }}>{st.icon}</span>
                    {/* Text */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: st.done ? '#475569' : 'white', fontSize: '0.82rem', fontWeight: st.done ? '400' : '600', textDecoration: st.done ? 'line-through' : 'none' }}>{st.label}</div>
                      {!st.done && <div style={{ color: '#475569', fontSize: '0.72rem', marginTop: '0.05rem' }}>{st.desc}</div>}
                    </div>
                    {/* Badge locked */}
                    {st.locked && !st.done && <span style={{ fontSize: '0.65rem', color: '#f59e0b', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '4px', padding: '1px 6px', flexShrink: 0 }}>Essentiel</span>}
                    {/* Arrow if actionable */}
                    {!st.done && <span style={{ color: '#334155', fontSize: '0.7rem', flexShrink: 0 }}>›</span>}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* ── Smart Alerts ─────────────────────────────────────────────── */}
        {(pendingInvoices.length > 0 || pendingQuotes.length > 0) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
            {pendingInvoices.length > 0 && (
              <div style={{ background: 'rgba(251,146,60,0.07)', border: '1px solid rgba(251,146,60,0.22)', borderRadius: '0.75rem', padding: '0.85rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                  <div>
                    <span style={{ color: '#fed7aa', fontWeight: '600', fontSize: '0.875rem' }}>
                      {pendingInvoices.length} facture{pendingInvoices.length > 1 ? 's' : ''} en attente de paiement
                    </span>
                    <div style={{ color: 'var(--ako-text-2)', fontSize: '0.78rem', marginTop: '0.1rem' }}>
                      {pendingInvoices.slice(0, 3).map(i => i.client || i.number).filter(Boolean).join(' · ')}
                      {pendingInvoices.length > 3 && ` · +${pendingInvoices.length - 3} autres`}
                    </div>
                  </div>
                </div>
                <button
                  style={{ background: 'rgba(251,146,60,0.15)', color: '#fed7aa', border: '1px solid rgba(251,146,60,0.3)', borderRadius: '0.5rem', padding: '0.35rem 0.85rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                  onClick={() => setTab('invoices')}>
                  Voir les factures →
                </button>
              </div>
            )}
            {pendingQuotes.length > 0 && (
              <div style={{ background: 'rgba(139,92,246,0.07)', border: '1px solid rgba(139,92,246,0.22)', borderRadius: '0.75rem', padding: '0.85rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '1.2rem' }}>📬</span>
                  <div>
                    <span style={{ color: '#c4b5fd', fontWeight: '600', fontSize: '0.875rem' }}>
                      {pendingQuotes.length} devis en attente de réponse
                    </span>
                    <div style={{ color: 'var(--ako-text-2)', fontSize: '0.78rem', marginTop: '0.1rem' }}>
                      {pendingQuotes.slice(0, 3).map(q => q.client || q.number).filter(Boolean).join(' · ')}
                      {pendingQuotes.length > 3 && ` · +${pendingQuotes.length - 3} autres`}
                    </div>
                  </div>
                </div>
                <button
                  style={{ background: 'rgba(139,92,246,0.15)', color: '#c4b5fd', border: '1px solid rgba(139,92,246,0.3)', borderRadius: '0.5rem', padding: '0.35rem 0.85rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                  onClick={() => setTab('quotes')}>
                  Voir les devis →
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── KPI Cards ────────────────────────────────────────────────── */}
        <div className="ako-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(148px,1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
          {[
            { label: 'Clients',   value: userStats?.clients?.count  ?? clients.length,  icon: '👥', color: '#3b82f6', bg: 'rgba(59,130,246,0.12)',  tab: 'clients' },
            { label: 'Factures',  value: userStats?.invoices?.count ?? invoices.length,  icon: '📄', color: '#10b981', bg: 'rgba(16,185,129,0.12)',  tab: 'invoices' },
            { label: 'Devis',     value: userStats?.quotes?.count   ?? quotes.length,    icon: '💰', color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)', tab: 'quotes' },
            { label: 'Projets',   value: userStats?.projects?.count ?? projects.length,  icon: '🚀', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', tab: 'projects' },
            { label: 'CA Total',  value: `${(userStats?.invoices?.total ?? 0).toFixed(0)} €`, icon: '💵', color: '#ec4899', bg: 'rgba(236,72,153,0.12)', tab: null, trend: caGrowth },
          ].map((k, i) => (
            <div key={i} onClick={() => k.tab && setTab(k.tab)}
              role={k.tab ? 'button' : undefined}
              tabIndex={k.tab ? 0 : undefined}
              onKeyDown={e => k.tab && e.key === 'Enter' && setTab(k.tab)}
              aria-label={k.tab ? `Voir les ${k.label.toLowerCase()}` : undefined}
              style={{ background: 'var(--ako-surface)', border: '1px solid var(--ako-border)', borderRadius: '0.85rem', padding: '1.1rem 1.25rem', cursor: k.tab ? 'pointer' : 'default', transition: 'transform 0.15s,box-shadow 0.15s,border-color 0.15s' }}
              onMouseEnter={e => { if (k.tab) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = k.color; e.currentTarget.style.boxShadow = `0 8px 24px rgba(0,0,0,0.15)`; } }}
              onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.borderColor = 'var(--ako-border)'; e.currentTarget.style.boxShadow = ''; }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                <div style={{ width: '2.1rem', height: '2.1rem', borderRadius: '0.55rem', background: k.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>
                  {k.icon}
                </div>
                {k.trend !== null && k.trend !== undefined && (
                  <span style={{ fontSize: '0.72rem', fontWeight: '700', color: k.trend >= 0 ? '#34d399' : '#f87171', background: k.trend >= 0 ? 'rgba(52,211,153,0.1)' : 'rgba(248,113,113,0.1)', padding: '0.15rem 0.45rem', borderRadius: '999px' }}>
                    {k.trend >= 0 ? '▲' : '▼'} {Math.abs(k.trend)}%
                  </span>
                )}
              </div>
              <div style={{ color: 'var(--ako-text-2)', fontSize: '0.78rem', fontWeight: '500', marginBottom: '0.25rem' }}>{k.label}</div>
              <div style={{ color: 'var(--ako-heading)', fontSize: '1.6rem', fontWeight: '700', lineHeight: 1 }}>{k.value}</div>
            </div>
          ))}
        </div>

        {/* ── Score Santé Business ─────────────────────────────────────── */}
        {(() => {
          // Calcul du score (max 100)
          const scoreFactors = [];

          // 1. Profil artisan renseigné (20 pts)
          const profilOk = !!(artisanProfile?.nom_entreprise);
          scoreFactors.push({ label: 'Profil renseigné', pts: profilOk ? 20 : 0, max: 20, ok: profilOk, action: () => setTab('profil') });

          // 2. Clients actifs (15 pts)
          const hasClients = clients.length > 0;
          scoreFactors.push({ label: `${clients.length} client${clients.length > 1 ? 's' : ''}`, pts: hasClients ? Math.min(15, clients.length * 3) : 0, max: 15, ok: hasClients, action: () => setTab('clients') });

          // 3. Factures émises (15 pts)
          const hasInvoices = invoices.length > 0;
          scoreFactors.push({ label: `${invoices.length} facture${invoices.length > 1 ? 's' : ''}`, pts: hasInvoices ? Math.min(15, invoices.length * 2) : 0, max: 15, ok: hasInvoices, action: () => hasEssentiel && setTab('invoices') });

          // 4. CA en croissance (20 pts)
          const caOk = caGrowth !== null && caGrowth > 0;
          scoreFactors.push({ label: caGrowth !== null ? (caGrowth > 0 ? `CA +${caGrowth}%` : `CA ${caGrowth}%`) : 'Pas de données CA', pts: caOk ? 20 : (caGrowth === 0 ? 10 : 0), max: 20, ok: caOk, action: null });

          // 5. Taux recouvrement (30 pts)
          const totalFact = invoices.length;
          const paidFact  = invoices.filter(i => i.status === 'paid').length;
          const recovRate = totalFact > 0 ? paidFact / totalFact : 0;
          const recoverPts = Math.round(recovRate * 30);
          scoreFactors.push({ label: `${Math.round(recovRate * 100)}% factures payées`, pts: recoverPts, max: 30, ok: recovRate >= 0.7, action: () => hasEssentiel && setTab('invoices') });

          const totalScore = Math.min(100, scoreFactors.reduce((s, f) => s + f.pts, 0));
          const scoreColor = totalScore >= 70 ? '#10b981' : totalScore >= 40 ? '#f59e0b' : '#ef4444';
          const scoreBg    = totalScore >= 70 ? 'rgba(16,185,129,0.08)' : totalScore >= 40 ? 'rgba(245,158,11,0.08)' : 'rgba(239,68,68,0.08)';
          const scoreBorder = totalScore >= 70 ? 'rgba(16,185,129,0.25)' : totalScore >= 40 ? 'rgba(245,158,11,0.25)' : 'rgba(239,68,68,0.25)';
          const scoreLabel  = totalScore >= 70 ? 'Bonne santé' : totalScore >= 40 ? 'À améliorer' : 'Attention';

          // Gauge SVG (demi-cercle)
          const r = 30, cx = 40, cy = 38;
          const circ = Math.PI * r; // demi-cercle
          const dash = (totalScore / 100) * circ;

          return (
            <div style={{ background: scoreBg, border: `1px solid ${scoreBorder}`, borderRadius: '0.85rem', padding: '1rem 1.25rem', marginBottom: '1.25rem', display: 'flex', gap: '1.25rem', alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Gauge */}
              <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.1rem' }}>
                <svg width="80" height="46" viewBox="0 0 80 46" style={{ overflow: 'visible' }}>
                  {/* Track */}
                  <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="rgba(51,65,85,0.6)" strokeWidth="7" strokeLinecap="round" />
                  {/* Progress */}
                  <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke={scoreColor} strokeWidth="7" strokeLinecap="round"
                    strokeDasharray={`${dash} ${circ}`}
                    style={{ transition: 'stroke-dasharray 0.8s ease' }} />
                  <text x={cx} y={cy - 4} textAnchor="middle" fill="white" fontSize="13" fontWeight="800">{totalScore}</text>
                  <text x={cx} y={cy + 8} textAnchor="middle" fill={scoreColor} fontSize="7.5" fontWeight="600">{scoreLabel.toUpperCase()}</text>
                </svg>
                <div style={{ color: '#64748b', fontSize: '0.68rem', fontWeight: '600', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Score santé</div>
              </div>

              {/* Facteurs */}
              <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '0.5rem', minWidth: 0 }}>
                {scoreFactors.map((f, i) => (
                  <div key={i}
                    onClick={() => f.action && f.action()}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'rgba(15,23,42,0.4)', border: `1px solid ${f.ok ? 'rgba(16,185,129,0.2)' : 'rgba(71,85,105,0.3)'}`, borderRadius: '999px', padding: '0.25rem 0.65rem', cursor: f.action ? 'pointer' : 'default', transition: 'border-color 0.15s' }}>
                    <span style={{ fontSize: '0.72rem' }}>{f.ok ? '✓' : '○'}</span>
                    <span style={{ color: f.ok ? '#94a3b8' : '#475569', fontSize: '0.75rem', fontWeight: f.ok ? '500' : '400', whiteSpace: 'nowrap' }}>{f.label}</span>
                    <span style={{ color: f.ok ? scoreColor : '#475569', fontSize: '0.7rem', fontWeight: '700' }}>{f.pts}/{f.max}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* ── Chart CA + Pipeline ──────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: monthlyData.length > 0 ? 'minmax(0,1fr) 320px' : '1fr', gap: '1rem', marginBottom: '1.25rem' }}>

          {/* AreaChart recharts */}
          {monthlyData.length > 0 && (
            <div style={{ ...s.card, marginBottom: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.1rem' }}>
                <h3 style={{ color: 'var(--ako-heading)', margin: 0, fontSize: '0.92rem', fontWeight: '600' }}>📈 Évolution du CA</h3>
                {caGrowth !== null && (
                  <span style={{ fontSize: '0.82rem', fontWeight: '700', color: caGrowth >= 0 ? '#34d399' : '#f87171' }}>
                    {caGrowth >= 0 ? '▲ +' : '▼ '}{caGrowth}% ce mois
                  </span>
                )}
              </div>
              <ResponsiveContainer width="100%" height={110}>
                <AreaChart data={monthlyData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="akoCAGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.35}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ background: 'var(--ako-surface)', border: '1px solid var(--ako-border)', borderRadius: '0.5rem', color: 'white', fontSize: '0.82rem' }}
                    formatter={v => [`${Number(v).toFixed(0)} €`, 'CA']}
                  />
                  <Area type="monotone" dataKey="ca" stroke="#3b82f6" strokeWidth={2.5} fill="url(#akoCAGrad)" dot={false} activeDot={{ r: 4, fill: '#60a5fa' }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Pipeline CA */}
          <div style={{ ...s.card, marginBottom: 0 }}>
            <h3 style={{ color: 'var(--ako-heading)', margin: 0, marginBottom: '1.1rem', fontSize: '0.92rem', fontWeight: '600' }}>💼 Pipeline CA</h3>
            {[
              { label: 'Devis actifs',          amount: pipelineDevis, color: '#8b5cf6', icon: '💰', tab: 'quotes' },
              { label: 'En attente paiement',   amount: pipelineFact,  color: '#f59e0b', icon: '⏳', tab: 'invoices' },
              { label: 'Encaissé',              amount: pipelinePaid,  color: '#10b981', icon: '✅', tab: null },
            ].map((p, i) => {
              const pct = pipelineTotal > 0 ? (p.amount / pipelineTotal) * 100 : 0;
              return (
                <div key={i} style={{ marginBottom: '0.85rem', cursor: p.tab ? 'pointer' : 'default' }} onClick={() => p.tab && setTab(p.tab)} role={p.tab ? 'button' : undefined} tabIndex={p.tab ? 0 : undefined} onKeyDown={e => p.tab && e.key === 'Enter' && setTab(p.tab)} aria-label={p.tab ? `Voir ${p.label.toLowerCase()}` : undefined}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                      <span style={{ fontSize: '0.85rem' }}>{p.icon}</span>
                      <span style={{ color: 'var(--ako-text-2)', fontSize: '0.8rem' }}>{p.label}</span>
                    </div>
                    <span style={{ color: 'white', fontWeight: '700', fontSize: '0.88rem' }}>{p.amount.toFixed(0)} €</span>
                  </div>
                  <div style={{ height: '5px', background: 'rgba(51,65,85,0.8)', borderRadius: '999px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: p.color, borderRadius: '999px', transition: 'width 0.6s ease' }} />
                  </div>
                </div>
              );
            })}
            <div style={{ borderTop: '1px solid #334155', paddingTop: '0.8rem', marginTop: '0.4rem', display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748b', fontSize: '0.8rem' }}>Total pipeline</span>
              <span style={{ color: 'white', fontWeight: '700', fontSize: '0.88rem' }}>{pipelineTotal.toFixed(0)} €</span>
            </div>
          </div>
        </div>

        {/* ── Bannière upgrade ─────────────────────────────────────────── */}
        {!hasEssentiel && (
          <div style={{ ...s.card, marginBottom: '1.25rem', borderColor: 'rgba(251,191,36,0.35)', background: 'linear-gradient(135deg,rgba(30,41,59,0.9),rgba(251,191,36,0.04))' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <div style={{ color: 'white', fontWeight: '700', marginBottom: '0.25rem' }}>🔓 Déverrouillez la facturation et les devis</div>
                <div style={{ color: 'var(--ako-text-2)', fontSize: '0.875rem' }}>Plan Essentiel à 19€/mois — factures PDF, devis, agent IA et plus</div>
              </div>
              <button onClick={() => setTab('abonnement')} style={{ background: 'linear-gradient(135deg,#f59e0b,#d97706)', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.6rem 1.2rem', cursor: 'pointer', fontWeight: '700', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                Voir les offres →
              </button>
            </div>
          </div>
        )}

        {/* ── Factures récentes ─────────────────────────────────────────── */}
        <div style={s.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ color: 'var(--ako-heading)', margin: 0, fontSize: '0.92rem', fontWeight: '600' }}>📄 Factures récentes</h3>
            {hasEssentiel && invoices.length > 0 && (
              <button style={{ background: 'transparent', color: '#60a5fa', border: 'none', cursor: 'pointer', fontSize: '0.83rem', fontWeight: '600' }} onClick={() => setTab('invoices')}>
                Voir tout →
              </button>
            )}
          </div>
          {hasEssentiel ? (
            <>
              {invoices.slice(0, 5).map(inv => (
                <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', borderBottom: '1px solid #1e293b' }}>
                  <div>
                    <span style={{ color: 'white', fontWeight: '600' }}>{inv.number}</span>
                    <span style={{ color: '#64748b', marginLeft: '0.75rem', fontSize: '0.875rem' }}>{inv.client}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <span style={{ color: '#34d399', fontWeight: '600' }}>{inv.amount} €</span>
                    <span style={s.badge(inv.status)}>{inv.status}</span>
                  </div>
                </div>
              ))}
              {invoices.length === 0 && <div style={s.emptyState}>📭 Aucune facture pour le moment</div>}
            </>
          ) : (
            <div style={{ ...s.emptyState, cursor: 'pointer' }} onClick={() => setTab('abonnement')}>
              🔒 Disponible avec le plan Essentiel — <span style={{ color: '#60a5fa', textDecoration: 'underline' }}>Voir les offres</span>
            </div>
          )}
        </div>

      </div>
    );
  };

  const renderClients = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <h2 style={{ color: 'var(--ako-heading)', margin: 0 }}>
          👥 Clients <span style={{ color: '#475569', fontSize: '1rem', fontWeight: '400' }}>({filteredClients.length})</span>
        </h2>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <SearchBar value={search} onChange={setSearch} placeholder="Nom ou email..." />
          <button style={{ ...s.btn, background: 'var(--ako-surface)', border: '1px solid var(--ako-border)' }} onClick={() => api.exportCSV('clients').catch(e => toast(e.message, 'error'))}>⬇ CSV</button>
          <button style={s.btn} onClick={() => setModal('client')}>+ Nouveau client</button>
        </div>
      </div>
      {modal === 'client' && (
        <Modal title="Nouveau client"
          fields={[{ key: 'name', label: 'Nom' }, { key: 'email', label: 'Email', type: 'email' }, { key: 'phone', label: 'Téléphone' }, { key: 'address', label: 'Adresse' }]}
          onSave={async (d) => {
            if (!d.name) { toast('Le nom est requis', 'error'); return; }
            await api.createClient(d);
            notifyN8n('nouveau_client', { client: d.name, email: d.email, phone: d.phone }).catch(() => {});
            toast(`Client "${d.name}" créé avec succès`);
            setModal(null); loadData();
          }}
          onCancel={() => setModal(null)} />
      )}
      {editClient && (
        <EditClientModal
          client={editClient}
          onSave={async (d) => {
            await api.updateClient(editClient.id, d);
            toast('Client mis à jour');
            setEditClient(null);
            loadData();
          }}
          onCancel={() => setEditClient(null)} />
      )}
      <div style={s.card}>
        <div className="table-wrap">
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '520px' }}>
          <thead>
            <tr>
              <th scope="col" style={s.th}>Nom</th>
              <th scope="col" style={s.th}>Email</th>
              <th scope="col" style={s.th}>Téléphone</th>
              <th scope="col" style={s.th}>Statut</th>
              <th scope="col" style={s.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredClients.map(c => (
              <HoverRow key={c.id}>
                <td style={{ ...s.td, fontWeight: '600' }}>{c.name}</td>
                <td style={s.td}>{c.email}</td>
                <td style={s.td}>{c.phone}</td>
                <td style={s.td}><span style={s.badge(c.status)}>{c.status}</span></td>
                <td style={s.td}>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <button style={{ background: 'rgba(59,130,246,0.12)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.25)', borderRadius: '0.375rem', padding: '0.3rem 0.65rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' }} onClick={() => setEditClient(c)}>✏️ Modifier</button>
                    <button style={s.btnSmall} onClick={() => handleDeleteClient(c.id, c.name)}>🗑 Supprimer</button>
                  </div>
                </td>
              </HoverRow>
            ))}
          </tbody>
        </table>
        </div>
        {filteredClients.length === 0 && !search && <EmptyState icon="👥" title="Aucun client encore" description="Ajoutez votre premier client pour commencer à créer des factures et devis." action={() => setModal('client')} actionLabel="+ Ajouter un client" />}
        {filteredClients.length === 0 && search && <div style={s.emptyState}>🔍 Aucun résultat pour &ldquo;{search}&rdquo;</div>}
      </div>
    </div>
  );

  const [pdfLoading, setPdfLoading] = useState({});
  const [payLinkLoading, setPayLinkLoading] = useState({});

  const handleDownloadPdf = async (type, id, number) => {
    setPdfLoading(p => ({ ...p, [id]: true }));
    try {
      if (type === 'invoice') await api.downloadInvoicePdf(id, number);
      else await api.downloadQuotePdf(id, number);
      toast(`PDF ${number} téléchargé`, 'success');
    } catch (e) {
      toast(e.message || 'Erreur PDF', 'error');
    } finally {
      setPdfLoading(p => ({ ...p, [id]: false }));
    }
  };

  const renderInvoices = () => {
    if (selectedInvoiceId) {
      return (
        <InvoiceDetail
          invoiceId={selectedInvoiceId}
          artisanProfile={artisanProfile}
          onBack={() => setSelectedInvoiceId(null)}
          toast={toast}
        />
      );
    }

    // ── Stats
    const totalCount  = invoices.length;
    const paidCount   = invoices.filter(i => i.status === 'paid' || i.status === 'payée').length;
    const pendCount   = invoices.filter(i => ['pending','sent','envoyée'].includes(i.status)).length;
    const draftCount  = invoices.filter(i => i.status === 'draft' || i.status === 'brouillon').length;
    const totalAmount = invoices.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
    const paidAmount  = invoices.filter(i => i.status === 'paid' || i.status === 'payée').reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);

    // ── Filtrage
    const filterTabs = [
      { id: 'all',      label: `Toutes (${totalCount})` },
      { id: 'pending',  label: `En attente (${pendCount})` },
      { id: 'paid',     label: `Payées (${paidCount})` },
      { id: 'draft',    label: `Brouillon (${draftCount})` },
    ];
    const invByFilter = filteredInvoices.filter(i => {
      if (invoiceFilter === 'all')     return true;
      if (invoiceFilter === 'pending') return ['pending','sent','envoyée'].includes(i.status);
      if (invoiceFilter === 'paid')    return i.status === 'paid' || i.status === 'payée';
      if (invoiceFilter === 'draft')   return i.status === 'draft' || i.status === 'brouillon';
      return true;
    });

    return (
      <div>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h2 style={{ color: 'var(--ako-heading)', margin: 0, fontSize: '1.35rem', fontWeight: '700' }}>📄 Factures</h2>
            <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '0.15rem' }}>{totalCount} facture{totalCount > 1 ? 's' : ''} · {totalAmount.toFixed(0)} € au total</div>
          </div>
          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <SearchBar value={search} onChange={setSearch} placeholder="N° ou client..." />
            <button style={{ background: 'var(--ako-surface)', color: 'var(--ako-text-2)', border: '1px solid var(--ako-border)', borderRadius: '0.5rem', padding: '0.5rem 0.9rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.82rem' }} onClick={() => api.exportCSV('invoices').catch(e => toast(e.message, 'error'))}>⬇ CSV</button>
            <button style={s.btn} onClick={() => setFactureModal('invoice')}>+ Nouvelle facture</button>
          </div>
        </div>

        {/* Stats mini-cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: '0.65rem', marginBottom: '1.25rem' }}>
          {[
            { label: 'Total',       value: `${totalAmount.toFixed(0)} €`, color: '#60a5fa', icon: '📄' },
            { label: 'Encaissé',    value: `${paidAmount.toFixed(0)} €`,  color: '#34d399', icon: '✅' },
            { label: 'En attente',  value: `${pendCount}`,                color: '#f59e0b', icon: '⏳' },
            { label: 'Taux règlement', value: totalCount > 0 ? `${Math.round(paidCount / totalCount * 100)}%` : '—', color: '#a78bfa', icon: '📊' },
          ].map((k, i) => (
            <div key={i} style={{ background: 'rgba(30,41,59,0.7)', border: '1px solid var(--ako-border)', borderRadius: '0.7rem', padding: '0.85rem 1rem', display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '0.45rem', background: `${k.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', flexShrink: 0 }}>{k.icon}</div>
              <div>
                <div style={{ color: '#64748b', fontSize: '0.72rem', fontWeight: '600', marginBottom: '0.1rem' }}>{k.label}</div>
                <div style={{ color: k.color, fontSize: '1.1rem', fontWeight: '700', lineHeight: 1 }}>{k.value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: '0.3rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          {filterTabs.map(ft => (
            <button key={ft.id} onClick={() => setInvoiceFilter(ft.id)} style={{ background: invoiceFilter === ft.id ? 'rgba(59,130,246,0.15)' : 'transparent', color: invoiceFilter === ft.id ? '#60a5fa' : '#64748b', border: `1px solid ${invoiceFilter === ft.id ? 'rgba(59,130,246,0.35)' : '#334155'}`, borderRadius: '999px', padding: '0.3rem 0.85rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: invoiceFilter === ft.id ? '700' : '500', transition: 'all 0.15s' }}>
              {ft.label}
            </button>
          ))}
        </div>

        <div style={s.card}>
          <div className="table-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '520px' }}>
            <thead>
              <tr>
                <th scope="col" style={s.th}>N°</th>
                <th scope="col" style={s.th}>Client</th>
                <th scope="col" style={s.th}>Montant</th>
                <th scope="col" style={s.th}>Échéance</th>
                <th scope="col" style={s.th}>Statut</th>
                <th scope="col" style={{ ...s.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invByFilter.map(inv => (
                <HoverRow key={inv.id}>
                  <td
                    style={{ ...s.td, fontWeight: '700', color: '#60a5fa', cursor: 'pointer' }}
                    onClick={() => setSelectedInvoiceId(inv.id)}
                  >
                    {inv.number}
                  </td>
                  <td style={{ ...s.td, fontWeight: '500' }}>{inv.client_name || inv.client}</td>
                  <td style={{ ...s.td, color: '#34d399', fontWeight: '700' }}>{inv.amount} €</td>
                  <td style={{ ...s.td, color: 'var(--ako-text-2)' }}>{inv.due_date || '—'}</td>
                  <td style={s.td}><span style={s.badge(inv.status)}>{inv.status}</span></td>
                  <td style={{ ...s.td, textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <button onClick={() => setSelectedInvoiceId(inv.id)} style={{ background: 'rgba(51,65,85,0.6)', color: 'var(--ako-text-2)', border: '1px solid var(--ako-border)', borderRadius: '0.375rem', padding: '0.3rem 0.65rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' }}>Voir</button>
                      <button disabled={pdfLoading[inv.id]} onClick={() => handleDownloadPdf('invoice', inv.id, inv.number)} style={{ background: 'rgba(59,130,246,0.12)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.25)', borderRadius: '0.375rem', padding: '0.3rem 0.65rem', cursor: pdfLoading[inv.id] ? 'not-allowed' : 'pointer', fontSize: '0.78rem', fontWeight: '600', opacity: pdfLoading[inv.id] ? 0.6 : 1 }}>
                        {pdfLoading[inv.id] ? '⏳' : '⬇ PDF'}
                      </button>
                      {inv.status !== 'payée' && (
                        (inv.public_token || inv.payment_link_url) ? (
                          <button onClick={() => { const shareUrl = inv.public_token ? `https://app.akodigitale.fr/pay/${inv.public_token}` : inv.payment_link_url; navigator.clipboard.writeText(shareUrl); toast('Lien client copié !'); }} style={{ background: 'rgba(16,185,129,0.12)', color: '#34d399', border: '1px solid rgba(16,185,129,0.25)', borderRadius: '0.375rem', padding: '0.3rem 0.65rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' }}>📋 Copier lien</button>
                        ) : (
                          <button disabled={payLinkLoading[inv.id]} onClick={async () => { setPayLinkLoading(p => ({ ...p, [inv.id]: true })); try { const r = await api.createPaymentLink(inv.id); navigator.clipboard.writeText(r.url).catch(() => {}); toast('Lien de paiement créé et copié !'); loadData(); } catch (e) { toast(e.message || 'Erreur Stripe', 'error'); } setPayLinkLoading(p => ({ ...p, [inv.id]: false })); }} style={{ background: 'rgba(251,191,36,0.12)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.25)', borderRadius: '0.375rem', padding: '0.3rem 0.65rem', cursor: payLinkLoading[inv.id] ? 'not-allowed' : 'pointer', fontSize: '0.78rem', fontWeight: '600', opacity: payLinkLoading[inv.id] ? 0.6 : 1 }}>
                            {payLinkLoading[inv.id] ? '⏳' : '💳 Paiement'}
                          </button>
                        )
                      )}
                    </div>
                  </td>
                </HoverRow>
              ))}
            </tbody>
          </table>
          </div>
          {invByFilter.length === 0 && invoiceFilter === 'all' && !search && <EmptyState icon="📄" title="Aucune facture" description="Créez votre première facture et envoyez-la à votre client en quelques secondes." action={() => setFactureModal('invoice')} actionLabel="+ Nouvelle facture" />}
          {invByFilter.length === 0 && (invoiceFilter !== 'all' || search) && <div style={s.emptyState}>🔍 Aucun résultat pour ce filtre</div>}
        </div>
      </div>
    );
  };

  const renderQuotes = () => {
    if (selectedQuoteId) {
      return (
        <QuoteDetail
          quoteId={selectedQuoteId}
          artisanProfile={artisanProfile}
          onBack={() => setSelectedQuoteId(null)}
          toast={toast}
        />
      );
    }

    // ── Stats
    const totalQCount   = quotes.length;
    const acceptedCount = quotes.filter(q => q.status === 'acceptée' || q.status === 'accepted').length;
    const pendQCount    = quotes.filter(q => ['sent','pending','envoyé'].includes(q.status)).length;
    const expiredCount  = quotes.filter(q => q.status === 'expired' || q.status === 'expiré').length;
    const totalQAmount  = quotes.reduce((s, q) => s + (parseFloat(q.amount) || 0), 0);
    const convRate      = totalQCount > 0 ? Math.round(acceptedCount / totalQCount * 100) : 0;

    const qFilterTabs = [
      { id: 'all',      label: `Tous (${totalQCount})` },
      { id: 'pending',  label: `En attente (${pendQCount})` },
      { id: 'accepted', label: `Acceptés (${acceptedCount})` },
      { id: 'expired',  label: `Expirés (${expiredCount})` },
    ];
    const quotesByFilter = filteredQuotes.filter(qt => {
      if (quoteFilter === 'all')      return true;
      if (quoteFilter === 'pending')  return ['sent','pending','envoyé'].includes(qt.status);
      if (quoteFilter === 'accepted') return qt.status === 'acceptée' || qt.status === 'accepted';
      if (quoteFilter === 'expired')  return qt.status === 'expired' || qt.status === 'expiré';
      return true;
    });

    return (
      <div>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h2 style={{ color: 'var(--ako-heading)', margin: 0, fontSize: '1.35rem', fontWeight: '700' }}>💰 Devis</h2>
            <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '0.15rem' }}>{totalQCount} devis · {totalQAmount.toFixed(0)} € de CA potentiel</div>
          </div>
          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <SearchBar value={search} onChange={setSearch} placeholder="N° ou client..." />
            <button style={{ background: 'var(--ako-surface)', color: 'var(--ako-text-2)', border: '1px solid var(--ako-border)', borderRadius: '0.5rem', padding: '0.5rem 0.9rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.82rem' }} onClick={() => api.exportCSV('quotes').catch(e => toast(e.message, 'error'))}>⬇ CSV</button>
            <button style={s.btn} onClick={() => setFactureModal('quote')}>+ Nouveau devis</button>
          </div>
        </div>

        {/* Stats mini-cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: '0.65rem', marginBottom: '1.25rem' }}>
          {[
            { label: 'CA potentiel',   value: `${totalQAmount.toFixed(0)} €`, color: '#a78bfa', icon: '💰' },
            { label: 'Acceptés',       value: `${acceptedCount}`,             color: '#34d399', icon: '✅' },
            { label: 'En attente',     value: `${pendQCount}`,                color: '#f59e0b', icon: '📬' },
            { label: 'Taux conversion',value: `${convRate}%`,                 color: '#60a5fa', icon: '📊' },
          ].map((k, i) => (
            <div key={i} style={{ background: 'rgba(30,41,59,0.7)', border: '1px solid var(--ako-border)', borderRadius: '0.7rem', padding: '0.85rem 1rem', display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '0.45rem', background: `${k.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', flexShrink: 0 }}>{k.icon}</div>
              <div>
                <div style={{ color: '#64748b', fontSize: '0.72rem', fontWeight: '600', marginBottom: '0.1rem' }}>{k.label}</div>
                <div style={{ color: k.color, fontSize: '1.1rem', fontWeight: '700', lineHeight: 1 }}>{k.value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: '0.3rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          {qFilterTabs.map(ft => (
            <button key={ft.id} onClick={() => setQuoteFilter(ft.id)} style={{ background: quoteFilter === ft.id ? 'rgba(139,92,246,0.15)' : 'transparent', color: quoteFilter === ft.id ? '#a78bfa' : '#64748b', border: `1px solid ${quoteFilter === ft.id ? 'rgba(139,92,246,0.35)' : '#334155'}`, borderRadius: '999px', padding: '0.3rem 0.85rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: quoteFilter === ft.id ? '700' : '500', transition: 'all 0.15s' }}>
              {ft.label}
            </button>
          ))}
        </div>

        <div style={s.card}>
          <div className="table-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '520px' }}>
            <thead>
              <tr>
                <th scope="col" style={s.th}>N°</th>
                <th scope="col" style={s.th}>Client</th>
                <th scope="col" style={s.th}>Montant</th>
                <th scope="col" style={s.th}>Expiration</th>
                <th scope="col" style={s.th}>Statut</th>
                <th scope="col" style={{ ...s.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {quotesByFilter.map(qt => (
                <HoverRow key={qt.id}>
                  <td style={{ ...s.td, fontWeight: '700', color: '#a78bfa', cursor: 'pointer' }} onClick={() => setSelectedQuoteId(qt.id)}>{qt.number}</td>
                  <td style={{ ...s.td, fontWeight: '500' }}>{qt.client_name || qt.client_id}</td>
                  <td style={{ ...s.td, color: '#34d399', fontWeight: '700' }}>{qt.amount} €</td>
                  <td style={{ ...s.td, color: 'var(--ako-text-2)' }}>{qt.expiry_date || '—'}</td>
                  <td style={s.td}><span style={s.badge(qt.status)}>{qt.status}</span></td>
                  <td style={{ ...s.td, textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <button onClick={() => setSelectedQuoteId(qt.id)} style={{ background: 'rgba(51,65,85,0.6)', color: 'var(--ako-text-2)', border: '1px solid var(--ako-border)', borderRadius: '0.375rem', padding: '0.3rem 0.65rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' }}>Voir</button>
                      <button disabled={pdfLoading[qt.id]} onClick={() => handleDownloadPdf('quote', qt.id, qt.number)} style={{ background: 'rgba(124,58,237,0.12)', color: '#a78bfa', border: '1px solid rgba(124,58,237,0.25)', borderRadius: '0.375rem', padding: '0.3rem 0.65rem', cursor: pdfLoading[qt.id] ? 'not-allowed' : 'pointer', fontSize: '0.78rem', fontWeight: '600', opacity: pdfLoading[qt.id] ? 0.6 : 1 }}>
                        {pdfLoading[qt.id] ? '⏳' : '⬇ PDF'}
                      </button>
                      {!['facturée','annulée','converted'].includes(qt.status) && (
                        <button onClick={async () => { try { const r = await api.shareQuote(qt.id); await navigator.clipboard.writeText(r.url).catch(() => {}); toast('Lien de signature copié !'); } catch (e) { toast(e.message || 'Erreur', 'error'); } }} style={{ background: 'rgba(16,185,129,0.12)', color: '#34d399', border: '1px solid rgba(16,185,129,0.25)', borderRadius: '0.375rem', padding: '0.3rem 0.65rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' }}>
                          {qt.signed_at ? '✅ Copier lien' : '🖊 Signature'}
                        </button>
                      )}
                      {(qt.status === 'acceptée' || qt.status === 'accepted') && (
                        <button onClick={async () => { if (!window.confirm(`Convertir "${qt.number}" en facture ?`)) return; try { const r = await api.convertQuote(qt.id); toast(`✅ Facture ${r.invoice?.number || ''} créée !`); loadData(); } catch (e) { toast(e.message || 'Erreur conversion', 'error'); } }} style={{ background: 'rgba(251,191,36,0.12)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)', borderRadius: '0.375rem', padding: '0.3rem 0.65rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' }}>
                          📄 → Facture
                        </button>
                      )}
                    </div>
                  </td>
                </HoverRow>
              ))}
            </tbody>
          </table>
          </div>
          {quotesByFilter.length === 0 && quoteFilter === 'all' && !search && <EmptyState icon="💰" title="Aucun devis" description="Envoyez un devis professionnel à votre client, il peut l'accepter en ligne." action={() => setFactureModal('quote')} actionLabel="+ Nouveau devis" />}
          {quotesByFilter.length === 0 && (quoteFilter !== 'all' || search) && <div style={s.emptyState}>🔍 Aucun résultat pour ce filtre</div>}
        </div>
      </div>
    );
  };

  const renderProposals = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <h2 style={{ color: 'var(--ako-heading)', margin: 0 }}>📋 Propositions</h2>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <SearchBar value={search} onChange={setSearch} placeholder="Titre ou client..." />
          <button style={s.btn} onClick={() => setModal('proposal')}>+ Nouvelle proposition</button>
        </div>
      </div>
      {modal === 'proposal' && (
        <Modal title="Nouvelle proposition"
          fields={[{ key: 'client', label: 'Nom du client' }, { key: 'title', label: 'Titre' }, { key: 'amount', label: 'Montant (€)', type: 'number' }, { key: 'notes', label: 'Notes', type: 'textarea' }]}
          onSave={async (d) => {
            await api.createProposal(d);
            notifyN8n('proposition', { client: d.client, title: d.title, amount: d.amount, email: user.email }).catch(() => {});
            toast('Proposition créée avec succès');
            setModal(null); loadData();
          }}
          onCancel={() => setModal(null)} />
      )}
      <div style={s.card}>
        <div className="table-wrap">
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '440px' }}>
          <thead>
            <tr>
              <th scope="col" style={s.th}>N°</th>
              <th scope="col" style={s.th}>Client</th>
              <th scope="col" style={s.th}>Titre</th>
              <th scope="col" style={s.th}>Montant</th>
              <th scope="col" style={s.th}>Statut</th>
            </tr>
          </thead>
          <tbody>
            {filteredProposals.map(p => (
              <HoverRow key={p.id}>
                <td style={{ ...s.td, fontWeight: '600', color: '#60a5fa' }}>{p.number}</td>
                <td style={s.td}>{p.client_id}</td>
                <td style={s.td}>{p.title}</td>
                <td style={{ ...s.td, color: '#34d399', fontWeight: '600' }}>{p.amount} €</td>
                <td style={s.td}><span style={s.badge(p.status)}>{p.status}</span></td>
              </HoverRow>
            ))}
          </tbody>
        </table>
        </div>
        {filteredProposals.length === 0 && !search && <EmptyState icon="📋" title="Aucune proposition" description="Créez une proposition commerciale détaillée pour décrocher de nouveaux chantiers." />}
        {filteredProposals.length === 0 && search && <div style={s.emptyState}>🔍 Aucun résultat</div>}
      </div>
    </div>
  );

  const renderProjects = () => {
    const COLS = [
      { key: 'nouveau',   label: 'Nouveau',     color: '#3b82f6', emoji: '🆕' },
      { key: 'en_cours',  label: 'En cours',    color: '#f59e0b', emoji: '⚙️' },
      { key: 'en_pause',  label: 'En pause',    color: 'var(--ako-text-2)', emoji: '⏸️' },
      { key: 'termine',   label: 'Terminé',     color: '#22c55e', emoji: '✅' },
      { key: 'annule',    label: 'Annulé',      color: '#ef4444', emoji: '❌' },
    ];

    const reloadKanban = async () => {
      const d = await api.getKanban().catch(() => ({ kanban: {} }));
      setKanban(d.kanban || {});
      setProjects(Object.values(d.kanban || {}).flat());
    };

    const handleStatut = async (id, statut) => {
      await api.updateProjectStatut(id, statut);
      reloadKanban();
    };

    const handleSaveDetail = async (p) => {
      await api.updateProject(p.id, {
        name: p.name, client: p.client, description: p.description,
        notes: p.notes, dateDebut: p.dateDebut, dateFinPrevue: p.dateFinPrevue,
        budget: p.budget, adresseChantier: p.adresseChantier, dueDate: p.dueDate,
      });
      toast('Chantier mis à jour');
      setKanbanProject(null);
      reloadKanban();
    };

    const [newForm, setNewForm] = React.useState(null);

    const totalProjects = Object.values(kanban).flat().length;

    return (
      <div>
        {/* En-tête */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <h2 style={{ color: 'var(--ako-heading)', margin: 0 }}>
            🏗️ Chantiers <span style={{ color: '#475569', fontSize: '1rem', fontWeight: '400' }}>({totalProjects})</span>
          </h2>
          <button style={s.btn} onClick={() => setNewForm({ name: '', client: '', description: '', adresseChantier: '', dateDebut: '', dateFinPrevue: '', budget: '' })}>
            + Nouveau chantier
          </button>
        </div>

        {/* Modal nouveau chantier */}
        {newForm !== null && (
          <div style={{ position: 'fixed', inset: 0, background: 'var(--ako-overlay)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }} role="dialog" aria-modal="true">
            <div style={{ background: 'var(--ako-surface)', border: '1px solid var(--ako-border)', borderRadius: '0.75rem', padding: '1.5rem', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' }}>
              <h3 style={{ color: 'var(--ako-heading)', marginTop: 0 }}>🏗️ Nouveau chantier</h3>
              {[
                ['name', 'Nom du chantier *', 'text'],
                ['client', 'Client', 'text'],
                ['adresseChantier', 'Adresse du chantier', 'text'],
                ['dateDebut', 'Date de début', 'date'],
                ['dateFinPrevue', 'Date de fin prévue', 'date'],
                ['budget', 'Budget (€)', 'number'],
                ['description', 'Description', 'textarea'],
              ].map(([k, lbl, t]) => (
                <div key={k} style={{ marginBottom: '0.75rem' }}>
                  <label style={s.label}>{lbl}</label>
                  {t === 'textarea'
                    ? <textarea value={newForm[k] || ''} onChange={e => setNewForm(f => ({ ...f, [k]: e.target.value }))}
                        style={{ ...s.input, resize: 'vertical', minHeight: '80px' }} />
                    : <input type={t} value={newForm[k] || ''} onChange={e => setNewForm(f => ({ ...f, [k]: e.target.value }))} style={s.input} />
                  }
                </div>
              ))}
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button onClick={() => setNewForm(null)} style={s.btnRed}>Annuler</button>
                <button style={s.btn} onClick={async () => {
                  if (!newForm.name) { toast('Le nom est requis', 'error'); return; }
                  await api.createProject({ ...newForm, statutKanban: 'nouveau' });
                  toast(`Chantier "${newForm.name}" créé`);
                  setNewForm(null);
                  reloadKanban();
                }}>Créer</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal détail chantier */}
        {kanbanProject && (() => {
          const [editP, setEditP] = React.useState(kanbanProject);
          return (
            <div style={{ position: 'fixed', inset: 0, background: 'var(--ako-overlay)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }} role="dialog" aria-modal="true">
              <div style={{ background: 'var(--ako-surface)', border: '1px solid var(--ako-border)', borderRadius: '0.75rem', padding: '1.5rem', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ color: 'var(--ako-heading)', margin: 0 }}>🏗️ {kanbanProject.name}</h3>
                  <button onClick={() => setKanbanProject(null)} aria-label="Fermer" style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
                </div>
                {[
                  ['name', 'Nom', 'text'],
                  ['client', 'Client', 'text'],
                  ['adresseChantier', 'Adresse chantier', 'text'],
                  ['dateDebut', 'Date début', 'date'],
                  ['dateFinPrevue', 'Date fin prévue', 'date'],
                  ['budget', 'Budget (€)', 'number'],
                  ['dueDate', 'Échéance', 'date'],
                  ['description', 'Description', 'textarea'],
                  ['notes', 'Notes internes', 'textarea'],
                ].map(([k, lbl, t]) => (
                  <div key={k} style={{ marginBottom: '0.75rem' }}>
                    <label style={s.label}>{lbl}</label>
                    {t === 'textarea'
                      ? <textarea value={editP[k] || ''} onChange={e => setEditP(p => ({ ...p, [k]: e.target.value }))}
                          style={{ ...s.input, resize: 'vertical', minHeight: '70px' }} />
                      : <input type={t} value={editP[k] || ''} onChange={e => setEditP(p => ({ ...p, [k]: e.target.value }))} style={s.input} />
                    }
                  </div>
                ))}
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'space-between', marginTop: '1rem', flexWrap: 'wrap' }}>
                  <button onClick={() => handleDeleteProject(kanbanProject.id, kanbanProject.name).then(() => setKanbanProject(null))} style={s.btnRed}>🗑 Supprimer</button>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <button onClick={() => setKanbanProject(null)} style={{ ...s.btn, background: '#334155' }}>Annuler</button>
                    <button style={s.btn} onClick={() => handleSaveDetail(editP)}>💾 Enregistrer</button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Kanban board */}
        {totalProjects === 0 && (
          <EmptyState icon="🏗️" title="Aucun chantier" description="Créez votre premier chantier et suivez son avancement en temps réel." />
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', alignItems: 'start' }}>
          {COLS.map(col => {
            const cards = kanban[col.key] || [];
            return (
              <div key={col.key}>
                {/* En-tête colonne */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', padding: '0.5rem 0.75rem', background: 'rgba(30,41,59,0.6)', borderRadius: '0.5rem', borderTop: `3px solid ${col.color}` }}>
                  <span>{col.emoji}</span>
                  <span style={{ color: 'white', fontWeight: '700', fontSize: '0.85rem' }}>{col.label}</span>
                  <span style={{ marginLeft: 'auto', background: col.color + '22', color: col.color, borderRadius: '999px', padding: '0.1rem 0.5rem', fontSize: '0.75rem', fontWeight: '700' }}>{cards.length}</span>
                </div>

                {/* Cartes */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem', minHeight: '80px' }}>
                  {cards.map(p => (
                    <div key={p.id} style={{ background: 'rgba(30,41,59,0.9)', border: '1px solid var(--ako-border)', borderRadius: '0.625rem', padding: '0.875rem', cursor: 'pointer', transition: 'border-color 0.15s' }}
                      onClick={() => setKanbanProject(p)}>
                      <div style={{ fontWeight: '600', color: 'white', fontSize: '0.875rem', marginBottom: '0.3rem' }}>{p.name}</div>
                      {p.client && <div style={{ color: '#64748b', fontSize: '0.75rem', marginBottom: '0.3rem' }}>👤 {p.client}</div>}
                      {p.adresseChantier && <div style={{ color: '#64748b', fontSize: '0.72rem', marginBottom: '0.3rem' }}>📍 {p.adresseChantier}</div>}
                      {p.dateFinPrevue && <div style={{ color: 'var(--ako-text-2)', fontSize: '0.72rem', marginBottom: '0.5rem' }}>📅 {new Date(p.dateFinPrevue).toLocaleDateString('fr-FR')}</div>}
                      {p.budget && <div style={{ color: '#34d399', fontSize: '0.75rem', marginBottom: '0.5rem' }}>💰 {Number(p.budget).toLocaleString('fr-FR')} €</div>}
                      {/* Boutons déplacement */}
                      <div style={{ display: 'flex', gap: '0.3rem', marginTop: '0.5rem', flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                        {COLS.filter(c => c.key !== col.key).map(c => (
                          <button key={c.key} title={`→ ${c.label}`}
                            onClick={() => handleStatut(p.id, c.key)}
                            style={{ background: c.color + '22', color: c.color, border: `1px solid ${c.color}44`, borderRadius: '0.3rem', padding: '0.15rem 0.4rem', fontSize: '0.65rem', cursor: 'pointer', fontWeight: '600' }}>
                            {c.emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderUsers = () => (
    <div>
      <h2 style={{ color: 'var(--ako-heading)', marginTop: 0, marginBottom: '1.5rem' }}>
        👤 Utilisateurs <span style={{ color: '#475569', fontSize: '1rem', fontWeight: '400' }}>({users.length})</span>
      </h2>
      <div style={s.card}>
        <div className="table-wrap">
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '440px' }}>
          <thead>
            <tr>
              <th scope="col" style={s.th}>Nom</th>
              <th scope="col" style={s.th}>Email</th>
              <th scope="col" style={s.th}>Rôle</th>
              <th scope="col" style={s.th}>Inscrit le</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <HoverRow key={u.id}>
                <td style={{ ...s.td, fontWeight: '600' }}>{u.name}</td>
                <td style={s.td}>{u.email}</td>
                <td style={s.td}><span style={s.badge(u.role)}>{u.role}</span></td>
                <td style={s.td}>{new Date(u.created_at).toLocaleDateString('fr-FR')}</td>
              </HoverRow>
            ))}
          </tbody>
        </table>
        </div>
        {users.length === 0 && <div style={s.emptyState}>👤 Aucun utilisateur</div>}
      </div>
    </div>
  );

  const renderArtisans = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ color: 'var(--ako-heading)', margin: 0 }}>
          🔧 Artisans <span style={{ color: '#475569', fontSize: '1rem', fontWeight: '400' }}>({artisans.length})</span>
        </h2>
        <button style={s.btn} onClick={() => setModal('artisan')}>+ Nouvel artisan</button>
      </div>
      {modal === 'artisan' && (
        <Modal title="Créer un compte artisan"
          fields={[
            { key: 'name', label: 'Nom complet' },
            { key: 'email', label: 'Email', type: 'email' },
            { key: 'password', label: 'Mot de passe', type: 'password' },
            { key: 'company', label: 'Entreprise' },
            { key: 'phone', label: 'Téléphone' },
            { key: 'abonnement', label: 'Abonnement', type: 'select', options: [{ value: 'starter', label: 'Starter' }, { value: 'pro', label: 'Pro' }, { value: 'enterprise', label: 'Enterprise' }] },
          ]}
          onSave={async (d) => {
            await api.createArtisan(d);
            toast(`Artisan "${d.name}" créé avec succès`);
            setModal(null); loadData();
          }}
          onCancel={() => setModal(null)} />
      )}
      <div style={s.card}>
        <div className="table-wrap">
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
          <thead>
            <tr>
              <th scope="col" style={s.th}>Nom</th>
              <th scope="col" style={s.th}>Email</th>
              <th scope="col" style={s.th}>Entreprise</th>
              <th scope="col" style={s.th}>N° AKO</th>
              <th scope="col" style={s.th}>Abonnement</th>
            </tr>
          </thead>
          <tbody>
            {artisans.map(a => (
              <HoverRow key={a.id}>
                <td style={{ ...s.td, fontWeight: '600' }}>{a.name}</td>
                <td style={s.td}>{a.email}</td>
                <td style={s.td}>{a.company || a.nom_entreprise || '—'}</td>
                <td style={{ ...s.td, color: '#60a5fa', fontWeight: '600' }}>{a.numero_ako || '—'}</td>
                <td style={s.td}><span style={s.badge(a.abonnement || 'starter')}>{a.abonnement || 'starter'}</span></td>
              </HoverRow>
            ))}
          </tbody>
        </table>
        </div>
        {artisans.length === 0 && <div style={s.emptyState}>🔧 Aucun artisan enregistré</div>}
      </div>
    </div>
  );

  const renderAgentIA = () => (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ color: 'white', margin: '0 0 0.25rem' }}>🤖 Agent IA</h2>
        <p style={{ color: '#64748b', margin: 0 }}>Posez toutes vos questions sur la gestion de votre activité</p>
      </div>
      <div style={{ ...s.card, display: 'flex', flexDirection: 'column', height: '520px' }}>
        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem', paddingRight: '0.25rem' }}>
          {agentMessages.map((m, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '75%', padding: '0.75rem 1rem', borderRadius: m.role === 'user' ? '1rem 1rem 0 1rem' : '1rem 1rem 1rem 0',
                background: m.role === 'user' ? 'linear-gradient(135deg,#3b82f6,#2563eb)' : m.error ? 'rgba(239,68,68,0.15)' : 'rgba(51,65,85,0.8)',
                border: m.role === 'assistant' ? `1px solid ${m.error ? 'rgba(239,68,68,0.3)' : '#334155'}` : 'none',
                color: 'white', fontSize: '0.9rem', lineHeight: '1.6',
              }}>
                {m.text}
              </div>
            </div>
          ))}
          {agentLoading && (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <div style={{ background: 'rgba(51,65,85,0.8)', border: '1px solid var(--ako-border)', borderRadius: '1rem 1rem 1rem 0', padding: '0.75rem 1rem', color: '#64748b', fontSize: '0.9rem' }}>
                ⏳ L'agent réfléchit...
              </div>
            </div>
          )}
        </div>
        {/* Input */}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <input
            value={agentInput}
            onChange={e => setAgentInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendAgentMessage()}
            placeholder="Posez votre question... (Entrée pour envoyer)"
            style={{ ...s.input, marginBottom: 0, flex: 1 }}
          />
          <button onClick={sendAgentMessage} disabled={agentLoading || !agentInput.trim()} style={{ ...s.btn, opacity: agentLoading || !agentInput.trim() ? 0.5 : 1 }}>
            Envoyer
          </button>
        </div>
      </div>
    </div>
  );

  const renderSocial = () => {
    const LINKEDIN_CLIENT_ID = import.meta.env.VITE_LINKEDIN_CLIENT_ID || '77e9rumty83pvl';
    const redirectUri = encodeURIComponent(window.location.origin + '/callback');
    const scopes = encodeURIComponent('openid profile email w_member_social');
    const linkedinAuthUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${LINKEDIN_CLIENT_ID}&redirect_uri=${redirectUri}&scope=${scopes}&state=ako_linkedin`;
    const linkedinName = localStorage.getItem('linkedin_name');

    return (
    <div>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ color: 'white', margin: '0 0 0.25rem' }}>📱 Réseaux Sociaux</h2>
        <p style={{ color: '#64748b', margin: 0 }}>FacturArt gère votre présence en ligne — vous n'avez rien à configurer</p>
      </div>

      {/* LinkedIn — visible uniquement pour admin */}
      {user.role === 'admin' && (
        <div style={{ ...s.card, marginBottom: '1.5rem', borderColor: linkedinConnected ? 'rgba(10,102,194,0.5)' : '#334155', background: linkedinConnected ? 'linear-gradient(135deg,rgba(30,41,59,0.9),rgba(10,102,194,0.08))' : undefined }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: '#0a66c2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="white"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </div>
              <div>
                <div style={{ color: 'white', fontWeight: '600', fontSize: '0.95rem' }}>LinkedIn <span style={{ color: '#475569', fontSize: '0.75rem', fontWeight: '400' }}>(AKO Admin)</span></div>
                {linkedinConnected
                  ? <div style={{ color: '#34d399', fontSize: '0.82rem' }}>✅ Connecté{linkedinName ? ` — ${linkedinName}` : ''}</div>
                  : <div style={{ color: '#64748b', fontSize: '0.82rem' }}>Non connecté</div>
                }
              </div>
            </div>
            {linkedinConnected
              ? <button onClick={disconnectLinkedIn} style={{ ...s.btnRed, fontSize: '0.82rem', padding: '0.4rem 0.9rem' }}>Déconnecter</button>
              : <a href={linkedinAuthUrl} style={{ background: '#0a66c2', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.6rem 1.2rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                  Connecter LinkedIn
                </a>
            }
          </div>
        </div>
      )}

      {/* Comment ça marche */}
      <div style={{ ...s.card, marginBottom: '1.5rem', borderColor: 'rgba(139,92,246,0.4)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
          <div style={{ fontSize: '2.5rem' }}>🚀</div>
          <div style={{ flex: 1 }}>
            <h3 style={{ color: 'white', margin: '0 0 0.5rem' }}>AKO Social Autopilot</h3>
            <p style={{ color: 'var(--ako-text-2)', margin: '0 0 1.25rem', fontSize: '0.9rem', lineHeight: '1.7' }}>
              Notre équipe s'occupe de tout : veille sectorielle, rédaction des posts par IA, validation et publication sur vos réseaux.
              Vous n'avez pas besoin de connecter vos comptes — FacturArt gère votre présence digitale pour vous.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {['LinkedIn', 'Instagram', 'Facebook', 'Twitter/X'].map(r => (
                <span key={r} style={{ background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)', color: '#a78bfa', borderRadius: '999px', padding: '0.2rem 0.75rem', fontSize: '0.8rem', fontWeight: '600' }}>{r}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Étapes du service */}
      <div style={{ ...s.card, marginBottom: '1.5rem' }}>
        <h3 style={{ color: 'white', margin: '0 0 1.25rem', fontSize: '1rem' }}>Comment ça fonctionne</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {[
            { step: '1', icon: '🌐', title: 'Veille automatique', desc: 'Chaque semaine, notre système analyse les dernières actualités de votre secteur (BTP, artisanat, bâtiment).' },
            { step: '2', icon: '✍️', title: 'Génération de contenu', desc: 'Une IA rédige des posts adaptés à votre activité et votre ton de communication.' },
            { step: '3', icon: '✅', title: 'Validation par AKO', desc: 'Notre équipe valide et ajuste chaque contenu avant publication.' },
            { step: '4', icon: '📤', title: 'Publication', desc: 'Les posts sont publiés sur vos réseaux tous les lundis matin.' },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(139,92,246,0.2)', border: '1px solid rgba(139,92,246,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a78bfa', fontWeight: '800', fontSize: '0.85rem', flexShrink: 0 }}>{item.step}</div>
              <div>
                <div style={{ color: 'white', fontWeight: '600', marginBottom: '0.2rem', fontSize: '0.9rem' }}>{item.icon} {item.title}</div>
                <div style={{ color: '#64748b', fontSize: '0.85rem', lineHeight: '1.5' }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Déclencheur Autopilot */}
      <div style={{ ...s.card, marginBottom: '1.5rem', borderColor: 'rgba(139,92,246,0.4)', background: 'linear-gradient(135deg,rgba(30,41,59,0.9),rgba(139,92,246,0.05))' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h3 style={{ color: 'white', margin: '0 0 0.25rem', fontSize: '1rem' }}>⚡ Lancer l'Autopilot maintenant</h3>
            <p style={{ color: 'var(--ako-text-2)', margin: 0, fontSize: '0.85rem' }}>Déclenche la génération de contenu → validation Telegram → publication</p>
            {socialStatus && (
              <div style={{ marginTop: '0.75rem', fontSize: '0.82rem', color: socialStatus.success ? '#34d399' : '#fca5a5', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                {socialStatus.success ? '✅' : '❌'} {socialStatus.message}
                <span style={{ color: '#475569', marginLeft: '0.5rem' }}>{socialStatus.date}</span>
              </div>
            )}
          </div>
          <button onClick={triggerSocialAutopilot} disabled={socialLoading}
            style={{ background: socialLoading ? 'rgba(139,92,246,0.2)' : 'linear-gradient(135deg,#8b5cf6,#6d28d9)', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.65rem 1.5rem', cursor: socialLoading ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '0.9rem', whiteSpace: 'nowrap', opacity: socialLoading ? 0.7 : 1 }}>
            {socialLoading ? '⏳ Lancement...' : '🚀 Lancer l\'Autopilot'}
          </button>
        </div>
      </div>

      {/* Demande de contenu spécifique */}
      <div style={{ ...s.card, borderColor: 'rgba(16,185,129,0.3)' }}>
        <h3 style={{ color: 'white', margin: '0 0 0.5rem', fontSize: '1rem' }}>💬 Demander du contenu spécifique</h3>
        <p style={{ color: '#64748b', fontSize: '0.85rem', margin: '0 0 1rem', lineHeight: '1.6' }}>
          Vous avez une actualité à mettre en avant ? Une promotion, un nouveau service, un chantier terminé ?
          Contactez-nous et on s'en occupe.
        </p>
        <a href="https://t.me/akodigital" target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: 'linear-gradient(135deg,#10b981,#059669)', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.6rem 1.2rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem', textDecoration: 'none' }}>
          Contacter FacturArt
        </a>
      </div>
    </div>
  ); };

  const renderAccessCodes = () => {
    const handleGenerate = async () => {
      setGenLoading(true);
      try {
        const d = await api.generateCodes(genPlan, parseInt(genCount));
        setNewCodes(d.codes || []);
        toast(`${d.codes?.length || 0} code(s) généré(s) avec succès`);
        const fresh = await api.listCodes();
        setAccessCodes(fresh.codes || []);
      } catch (e) { toast(e.message || 'Erreur génération', 'error'); }
      setGenLoading(false);
    };

    const handleDelete = async (id, code) => {
      try {
        await api.deleteCode(id);
        toast(`Code ${code} supprimé`);
        setAccessCodes(p => p.filter(c => c.id !== id));
        setNewCodes(p => p.filter(c => c.id !== id));
      } catch (e) { toast(e.message || 'Erreur suppression', 'error'); }
    };

    return (
      <div>
        <h2 style={{ color: 'var(--ako-heading)', marginTop: 0, marginBottom: '1.5rem' }}>🎟️ Codes d'accès</h2>

        {/* Génération */}
        <div style={{ ...s.card, marginBottom: '1.5rem' }}>
          <h3 style={{ color: 'white', margin: '0 0 1rem', fontSize: '1rem' }}>Générer des codes</h3>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <label style={s.label}>Plan</label>
              <select value={genPlan} onChange={e => setGenPlan(e.target.value)} style={{ ...s.input, marginBottom: 0, width: '180px', appearance: 'none' }}>
                <option value="starter">Starter (9€/mois)</option>
                <option value="essentiel">Essentiel (19€/mois)</option>
                <option value="pro">Pro (39€/mois)</option>
              </select>
            </div>
            <div>
              <label style={s.label}>Quantité</label>
              <input type="number" min="1" max="50" value={genCount} onChange={e => setGenCount(e.target.value)} style={{ ...s.input, marginBottom: 0, width: '80px' }} />
            </div>
            <button onClick={handleGenerate} disabled={genLoading} style={{ ...s.btn, opacity: genLoading ? 0.6 : 1 }}>
              {genLoading ? '⏳ Génération...' : '+ Générer'}
            </button>
          </div>

          {newCodes.length > 0 && (
            <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '0.5rem' }}>
              <div style={{ color: '#34d399', fontWeight: '600', marginBottom: '0.5rem', fontSize: '0.875rem' }}>✅ Nouveaux codes générés :</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {newCodes.map(c => (
                  <span key={c.id} style={{ fontFamily: 'monospace', background: 'rgba(51,65,85,0.8)', color: 'var(--ako-text)', padding: '0.3rem 0.75rem', borderRadius: '0.375rem', fontSize: '0.9rem', fontWeight: '700', letterSpacing: '0.05em' }}>{c.code}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Liste des codes */}
        <div style={s.card}>
          <h3 style={{ color: 'white', margin: '0 0 1rem', fontSize: '1rem' }}>Tous les codes ({accessCodes.length})</h3>
          <div className="table-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '560px' }}>
            <thead>
              <tr>
                <th scope="col" style={s.th}>Code</th>
                <th scope="col" style={s.th}>Plan</th>
                <th scope="col" style={s.th}>Statut</th>
                <th scope="col" style={s.th}>Activé par</th>
                <th scope="col" style={s.th}>Expire le</th>
                <th scope="col" style={s.th}></th>
              </tr>
            </thead>
            <tbody>
              {accessCodes.map(c => (
                <HoverRow key={c.id}>
                  <td style={{ ...s.td, fontFamily: 'monospace', fontWeight: '700', letterSpacing: '0.04em', color: c.is_used ? '#64748b' : '#60a5fa' }}>{c.code}</td>
                  <td style={s.td}><span style={s.badge(c.plan)}>{c.plan}</span></td>
                  <td style={s.td}>
                    {c.is_used
                      ? <span style={{ color: '#34d399', fontSize: '0.8rem', fontWeight: '600' }}>✓ Activé</span>
                      : <span style={{ color: '#fbbf24', fontSize: '0.8rem', fontWeight: '600' }}>En attente</span>}
                  </td>
                  <td style={{ ...s.td, fontSize: '0.82rem', color: 'var(--ako-text-2)' }}>{c.activated_by_name || '—'}</td>
                  <td style={{ ...s.td, fontSize: '0.82rem', color: 'var(--ako-text-2)' }}>
                    {c.expires_at ? new Date(c.expires_at).toLocaleDateString('fr-FR') : '—'}
                  </td>
                  <td style={s.td}>
                    {!c.is_used && (
                      <button style={s.btnSmall} aria-label="Supprimer le code" onClick={() => handleDelete(c.id, c.code)}>🗑</button>
                    )}
                  </td>
                </HoverRow>
              ))}
            </tbody>
          </table>
          </div>
          {accessCodes.length === 0 && <div style={s.emptyState}>🎟️ Aucun code généré</div>}
        </div>
      </div>
    );
  };

  // ── Comptabilité renders ────────────────────────────────────────────────────

  const fmtMoney = (v) => Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  const statutColors = { brouillon: '#94a3b8', valide: '#34d399', archive: '#64748b' };
  const typeColors = { facture_fournisseur: '#60a5fa', releve_bancaire: '#fbbf24', ticket: '#a78bfa', autre: '#94a3b8' };

  const renderDocuments = () => {
    const [showForm, setShowForm] = React.useState(false);
    const [form, setForm] = React.useState({ dateDocument: '', libelle: '', montantTtc: '', montantHt: '', tva: '', categorieId: '', typeDocument: 'facture_fournisseur', statut: 'brouillon', notes: '' });
    const [uploading, setUploading] = React.useState(false);
    const [filterStatut, setFilterStatut] = React.useState('');
    const [showTicketForm, setShowTicketForm] = React.useState(false);
    const [ticketForm, setTicketForm] = React.useState({ montantTtc: '', categorieId: '' });
    const [ticketFile, setTicketFile] = React.useState(null);
    const [ticketUploading, setTicketUploading] = React.useState(false);

    // Auto-refresh si des documents sont en cours d'analyse
    React.useEffect(() => {
      const hasEnAnalyse = documents.some(d => d.analyse_statut === 'en_analyse');
      if (!hasEnAnalyse) return;
      const timer = setInterval(async () => {
        const d = await api.getDocuments(comptaFilters);
        setDocuments(d.documents || []);
        const stillAnalysing = (d.documents || []).some(doc => doc.analyse_statut === 'en_analyse');
        if (!stillAnalysing) {
          clearInterval(timer);
          toast('✨ Analyse IA terminée — documents pré-remplis !');
        }
      }, 4000);
      return () => clearInterval(timer);
    }, [documents]);

    const handleFile = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      try {
        const r = await api.uploadDocument(file);
        setForm(f => ({ ...f, fichierPath: r.fichier_path, fichierNom: r.fichier_nom }));
        toast('Fichier uploadé : ' + r.fichier_nom);
      } catch { toast('Erreur upload', 'error'); }
      setUploading(false);
    };

    const handleCreate = async () => {
      try {
        await api.createDocument(form);
        toast('Document créé');
        setShowForm(false);
        const d = await api.getDocuments(comptaFilters);
        setDocuments(d.documents || []);
      } catch (e) { toast(e.message, 'error'); }
    };

    const handleValider = async (doc) => {
      try {
        await api.updateDocument(doc.id, { statut: 'valide' });
        toast('Document validé — écriture créée');
        const d = await api.getDocuments(comptaFilters);
        setDocuments(d.documents || []);
      } catch (e) { toast(e.message, 'error'); }
    };

    const handleDelete = async (id) => {
      if (!confirm('Supprimer ce document ?')) return;
      try {
        await api.deleteDocument(id);
        setDocuments(docs => docs.filter(d => d.id !== id));
        toast('Document supprimé');
      } catch (e) { toast(e.message, 'error'); }
    };

    const handleTicketSubmit = async () => {
      if (!ticketFile && !ticketForm.montantTtc) { toast('Photo ou montant requis', 'error'); return; }
      setTicketUploading(true);
      try {
        if (ticketFile) {
          // Upload → crée le doc ET déclenche l'IA automatiquement
          const r = await api.uploadDocument(ticketFile);
          // Si l'artisan a aussi saisi le montant manuellement, on met à jour le doc créé
          if (ticketForm.montantTtc && r.document_id) {
            const ttc = parseFloat(ticketForm.montantTtc);
            await api.updateDocument(r.document_id, {
              montant_ttc: ttc.toFixed(2),
              montant_ht: (ttc / 1.2).toFixed(2),
              tva: (ttc * 0.2 / 1.2).toFixed(2),
              categorie_id: ticketForm.categorieId || null,
            });
          }
          toast('📷 Ticket uploadé — analyse IA en cours...');
        } else {
          // Pas de photo : saisie manuelle simple
          const today = new Date().toISOString().split('T')[0];
          const ttc = parseFloat(ticketForm.montantTtc);
          await api.createDocument({
            dateDocument: today,
            libelle: 'Ticket de caisse',
            montantTtc: ttc.toFixed(2),
            montantHt: (ttc / 1.2).toFixed(2),
            tva: (ttc * 0.2 / 1.2).toFixed(2),
            categorieId: ticketForm.categorieId || null,
            typeDocument: 'ticket',
            statut: 'brouillon',
          });
          toast('Ticket enregistré');
        }
        setShowTicketForm(false);
        setTicketForm({ montantTtc: '', categorieId: '' });
        setTicketFile(null);
        const d = await api.getDocuments(comptaFilters);
        setDocuments(d.documents || []);
      } catch (e) { toast(e.message || 'Erreur', 'error'); }
      setTicketUploading(false);
    };

    const filtered = filterStatut ? documents.filter(d => d.statut === filterStatut) : documents;

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <h2 style={{ color: 'var(--ako-heading)', margin: 0 }}>🧾 Documents entrants</h2>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)} style={{ ...s.searchInput, width: 'auto' }}>
              <option value="">Tous statuts</option>
              <option value="brouillon">Brouillon</option>
              <option value="valide">Validé</option>
              <option value="archive">Archivé</option>
            </select>
            <button onClick={() => { setShowTicketForm(true); setShowForm(false); }} style={{ ...s.btn, background: 'linear-gradient(135deg,#a78bfa,#7c3aed)' }}>📷 Ticket rapide</button>
            <button onClick={() => { setShowForm(true); setShowTicketForm(false); }} style={s.btn}>+ Document</button>
          </div>
        </div>

        {/* Formulaire ticket rapide — optimisé mobile */}
        {showTicketForm && (
          <div style={{ ...s.card, border: '1px solid rgba(167,139,250,0.4)', background: 'rgba(124,58,237,0.08)', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ color: '#a78bfa', margin: 0, fontSize: '1rem' }}>📷 Ticket de caisse rapide</h3>
              <button onClick={() => setShowTicketForm(false)} aria-label="Fermer" style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '1.2rem', padding: 0 }}>✕</button>
            </div>
            <p style={{ color: '#64748b', fontSize: '0.8rem', margin: '0 0 1rem' }}>
              Photographiez votre ticket, saisissez le montant — validez en 10 secondes.
            </p>

            {/* Zone photo — ouvre la caméra sur mobile */}
            <div
              onClick={() => document.getElementById('ticket-camera-input').click()}
              style={{
                border: '2px dashed rgba(167,139,250,0.4)', borderRadius: '0.75rem', padding: '1.5rem',
                textAlign: 'center', cursor: 'pointer', marginBottom: '1rem',
                background: ticketFile ? 'rgba(34,197,94,0.08)' : 'rgba(124,58,237,0.05)',
                transition: 'all 0.2s',
              }}
            >
              {ticketFile ? (
                <div>
                  <div style={{ fontSize: '2rem', marginBottom: '0.25rem' }}>✅</div>
                  <div style={{ color: '#34d399', fontSize: '0.85rem', fontWeight: '600' }}>{ticketFile.name}</div>
                  <div style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '0.25rem' }}>Appuyer pour changer</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: '2.5rem', marginBottom: '0.25rem' }}>📷</div>
                  <div style={{ color: '#a78bfa', fontWeight: '600', fontSize: '0.9rem' }}>Photo du ticket</div>
                  <div style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '0.25rem' }}>JPG, PNG ou PDF · caméra ou galerie</div>
                </div>
              )}
            </div>
            <input
              id="ticket-camera-input"
              type="file"
              accept="image/*,.pdf"
              capture="environment"
              style={{ display: 'none' }}
              onChange={e => setTicketFile(e.target.files[0] || null)}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
              <div>
                <label style={s.label}>Montant TTC *</label>
                <input
                  type="number"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={ticketForm.montantTtc}
                  onChange={e => setTicketForm(f => ({ ...f, montantTtc: e.target.value }))}
                  style={{ ...s.input, marginBottom: 0, fontSize: '1.1rem', fontWeight: '600' }}
                  autoFocus
                />
              </div>
              <div>
                <label style={s.label}>Catégorie</label>
                <select
                  value={ticketForm.categorieId}
                  onChange={e => setTicketForm(f => ({ ...f, categorieId: e.target.value }))}
                  style={{ ...s.input, appearance: 'none', marginBottom: 0 }}
                >
                  <option value="">Sans catégorie</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => { setShowTicketForm(false); setTicketForm({ montantTtc: '', categorieId: '' }); setTicketFile(null); }} style={{ ...s.btnRed, flex: 1 }}>Annuler</button>
              <button
                onClick={handleTicketSubmit}
                disabled={ticketUploading || !ticketForm.montantTtc}
                style={{ ...s.btn, flex: 2, background: ticketUploading ? 'rgba(51,65,85,0.8)' : 'linear-gradient(135deg,#a78bfa,#7c3aed)', opacity: (!ticketForm.montantTtc) ? 0.5 : 1 }}
              >
                {ticketUploading ? '⏳ Enregistrement...' : '💾 Enregistrer le ticket'}
              </button>
            </div>
          </div>
        )}

        {showForm && (
          <div style={s.card}>
            <h3 style={{ color: 'var(--ako-heading)', marginTop: 0 }}>Nouveau document</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {[['dateDocument','Date du document','date'],['libelle','Libellé','text'],['montantHt','Montant HT','number'],['montantTtc','Montant TTC','number'],['tva','TVA','number']].map(([k, l, t]) => (
                <div key={k}>
                  <label style={s.label}>{l}</label>
                  <input type={t} value={form[k] || ''} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} style={s.input} />
                </div>
              ))}
              <div>
                <label style={s.label}>Type</label>
                <select value={form.typeDocument} onChange={e => setForm(f => ({ ...f, typeDocument: e.target.value }))} style={{ ...s.input, appearance: 'none' }}>
                  <option value="facture_fournisseur">Facture fournisseur</option>
                  <option value="releve_bancaire">Relevé bancaire</option>
                  <option value="ticket">Ticket</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div>
                <label style={s.label}>Catégorie</label>
                <select value={form.categorieId} onChange={e => setForm(f => ({ ...f, categorieId: e.target.value }))} style={{ ...s.input, appearance: 'none' }}>
                  <option value="">Sans catégorie</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
              </div>
              <div>
                <label style={s.label}>Statut</label>
                <select value={form.statut} onChange={e => setForm(f => ({ ...f, statut: e.target.value }))} style={{ ...s.input, appearance: 'none' }}>
                  <option value="brouillon">Brouillon</option>
                  <option value="valide">Valider directement</option>
                </select>
              </div>
              <div>
                <label style={s.label}>Pièce jointe (PDF/image)</label>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={handleFile} style={{ color: 'var(--ako-text-2)', fontSize: '0.85rem' }} />
                {uploading && <span style={{ color: '#60a5fa', fontSize: '0.8rem' }}>⏳ Upload...</span>}
                {form.fichierNom && <span style={{ color: '#34d399', fontSize: '0.8rem' }}>✓ {form.fichierNom}</span>}
              </div>
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label style={s.label}>Notes</label>
              <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} style={{ ...s.input, resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
              <button onClick={() => setShowForm(false)} style={{ ...s.btnRed, flex: 1 }}>Annuler</button>
              <button onClick={handleCreate} style={{ ...s.btn, flex: 1 }}>💾 Enregistrer</button>
            </div>
          </div>
        )}

        <div style={{ ...s.card, padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                {['Date','Libellé','Type','Montant HT','Catégorie','Statut',''].map(h => <th key={h} style={s.th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {filtered.map(doc => (
                  <HoverRow key={doc.id}>
                    <td style={s.td}>{doc.dateDocument}</td>
                    <td style={{ ...s.td, fontWeight: '500', color: 'white' }}>{doc.libelle}</td>
                    <td style={s.td}><span style={{ ...s.badge('draft'), color: typeColors[doc.typeDocument] || '#94a3b8' }}>{doc.typeDocument?.replace('_', ' ')}</span></td>
                    <td style={{ ...s.td, fontFamily: 'monospace' }}>{fmtMoney(doc.montantHt)}</td>
                    <td style={{ ...s.td, color: 'var(--ako-text-2)' }}>{doc.categorie_nom || '—'}</td>
                    <td style={s.td}>
                      <span style={{ ...s.badge('draft'), color: statutColors[doc.statut] }}>{doc.statut}</span>
                    </td>
                    <td style={s.td}>
                      {/* Badge analyse IA */}
                      {doc.analyse_statut === 'en_analyse' && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.3)', color: '#fbbf24', borderRadius: '999px', padding: '0.15rem 0.6rem', fontSize: '0.72rem', fontWeight: '600', marginBottom: '0.35rem', whiteSpace: 'nowrap' }}>
                          ⏳ Analyse IA...
                        </span>
                      )}
                      {doc.analyse_statut === 'analyse_ok' && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.3)', color: '#a78bfa', borderRadius: '999px', padding: '0.15rem 0.6rem', fontSize: '0.72rem', fontWeight: '600', marginBottom: '0.35rem', whiteSpace: 'nowrap' }}>
                          ✨ Pré-rempli IA
                        </span>
                      )}
                      {doc.analyse_statut === 'erreur' && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#f87171', borderRadius: '999px', padding: '0.15rem 0.6rem', fontSize: '0.72rem', fontWeight: '600', marginBottom: '0.35rem', whiteSpace: 'nowrap' }}>
                          ⚠️ Erreur IA
                        </span>
                      )}
                      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                        {doc.statut === 'brouillon' && <button onClick={() => handleValider(doc)} style={{ ...s.btn, padding: '0.25rem 0.75rem', fontSize: '0.8rem' }}>✓ Valider</button>}
                        {doc.fichier_path && doc.analyse_statut !== 'en_analyse' && doc.analyse_statut !== 'analyse_ok' && (
                          <button
                            onClick={async () => {
                              try {
                                await api.analyserDocument(doc.id);
                                setDocuments(docs => docs.map(d => d.id === doc.id ? { ...d, analyse_statut: 'en_analyse' } : d));
                                toast('🤖 Analyse IA lancée...');
                              } catch (e) { toast(e.message, 'error'); }
                            }}
                            style={{ background: 'rgba(167,139,250,0.15)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.3)', borderRadius: '0.375rem', padding: '0.25rem 0.65rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' }}
                          >🤖 Analyser</button>
                        )}
                        <button onClick={() => handleDelete(doc.id)} aria-label="Supprimer le document" style={{ ...s.btnSmall }}>🗑</button>
                      </div>
                    </td>
                  </HoverRow>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && <div style={s.emptyState}>🧾 Aucun document</div>}
        </div>
      </div>
    );
  };

  const renderGrandLivre = () => {
    const [showForm, setShowForm] = React.useState(false);
    const [form, setForm] = React.useState({ dateEcriture: '', libelle: '', type: 'charge', montant: '', categorieId: '', notes: '' });
    const [filterType, setFilterType] = React.useState('');
    const filtered = filterType ? ecritures.filter(e => e.type === filterType) : ecritures;
    const totalRecettes = ecritures.filter(e => e.type === 'recette').reduce((s, e) => s + e.montant, 0);
    const totalCharges  = ecritures.filter(e => e.type === 'charge').reduce((s, e) => s + e.montant, 0);

    const handleCreate = async () => {
      try {
        await api.createEcriture(form);
        toast('Écriture ajoutée');
        setShowForm(false);
        const d = await api.getEcritures(comptaFilters);
        setEcritures(d.ecritures || []);
      } catch (e) { toast(e.message, 'error'); }
    };

    const handleDelete = async (id) => {
      if (!confirm('Supprimer cette écriture manuelle ?')) return;
      try {
        await api.deleteEcriture(id);
        setEcritures(ec => ec.filter(e => e.id !== id));
        toast('Écriture supprimée');
      } catch (e) { toast(e.message, 'error'); }
    };

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <h2 style={{ color: 'var(--ako-heading)', margin: 0 }}>📒 Grand livre</h2>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <select value={comptaFilters.annee} onChange={e => setComptaFilters(f => ({ ...f, annee: e.target.value }))} style={{ ...s.searchInput, width: 'auto' }}>
              {[2023,2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ ...s.searchInput, width: 'auto' }}>
              <option value="">Tout</option>
              <option value="recette">Recettes</option>
              <option value="charge">Charges</option>
            </select>
            <button style={{ ...s.btn, background: 'var(--ako-surface)', border: '1px solid var(--ako-border)' }} onClick={() => api.exportCSV('ecritures', { annee: comptaFilters.annee }).catch(e => toast(e.message, 'error'))}>⬇ CSV</button>
            <button
              title="Fichier des Écritures Comptables — obligatoire en cas de contrôle fiscal (art. L.47 A LPF)"
              style={{ ...s.btn, background: 'rgba(234,179,8,0.12)', color: '#fbbf24', border: '1px solid rgba(234,179,8,0.3)' }}
              onClick={() => api.downloadFEC(comptaFilters.annee).catch(e => toast(e.message, 'error'))}
            >⬇ FEC</button>
            <button onClick={() => setShowForm(true)} style={s.btn}>+ Saisie manuelle</button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
          {[
            { label: 'Recettes', value: totalRecettes, color: '#34d399' },
            { label: 'Charges',  value: totalCharges,  color: '#f87171' },
            { label: 'Résultat net', value: totalRecettes - totalCharges, color: totalRecettes - totalCharges >= 0 ? '#60a5fa' : '#fb923c' },
          ].map(({ label, value, color }) => (
            <div key={label} style={s.card}>
              <div style={{ color: 'var(--ako-text-2)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>{label}</div>
              <div style={{ color, fontSize: '1.4rem', fontWeight: '700', fontFamily: 'monospace' }}>{fmtMoney(value)}</div>
            </div>
          ))}
        </div>

        {showForm && (
          <div style={{ ...s.card, marginBottom: '1rem' }}>
            <h3 style={{ color: 'var(--ako-heading)', marginTop: 0 }}>Saisie manuelle</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {[['dateEcriture','Date','date'],['libelle','Libellé','text'],['montant','Montant HT','number']].map(([k,l,t]) => (
                <div key={k}>
                  <label style={s.label}>{l}</label>
                  <input type={t} value={form[k]||''} onChange={e => setForm(f=>({...f,[k]:e.target.value}))} style={s.input}/>
                </div>
              ))}
              <div>
                <label style={s.label}>Type</label>
                <select value={form.type} onChange={e => setForm(f=>({...f,type:e.target.value}))} style={{...s.input,appearance:'none'}}>
                  <option value="charge">Charge</option>
                  <option value="recette">Recette</option>
                </select>
              </div>
              <div>
                <label style={s.label}>Catégorie</label>
                <select value={form.categorieId} onChange={e => setForm(f=>({...f,categorieId:e.target.value}))} style={{...s.input,appearance:'none'}}>
                  <option value="">Sans catégorie</option>
                  {categories.map(c=><option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
              </div>
            </div>
            <div style={{display:'flex',gap:'1rem',marginTop:'0.5rem'}}>
              <button onClick={()=>setShowForm(false)} style={{...s.btnRed,flex:1}}>Annuler</button>
              <button onClick={handleCreate} style={{...s.btn,flex:1}}>💾 Enregistrer</button>
            </div>
          </div>
        )}

        <div style={{ ...s.card, padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                {['Date','Libellé','Type','Montant','Catégorie','Source',''].map(h=><th key={h} style={s.th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {filtered.map(e => (
                  <HoverRow key={e.id}>
                    <td style={s.td}>{e.dateEcriture}</td>
                    <td style={{...s.td,fontWeight:'500',color:'white'}}>{e.libelle}</td>
                    <td style={s.td}>
                      <span style={{...s.badge('draft'),color:e.type==='recette'?'#34d399':'#f87171'}}>
                        {e.type==='recette'?'↑ Recette':'↓ Charge'}
                      </span>
                    </td>
                    <td style={{...s.td,fontFamily:'monospace',color:e.type==='recette'?'#34d399':'#f87171'}}>
                      {e.type==='recette'?'+':'-'}{fmtMoney(e.montant)}
                    </td>
                    <td style={{...s.td,color:'#94a3b8'}}>{e.categorie_nom||'—'}</td>
                    <td style={{...s.td,color:'#64748b',fontSize:'0.8rem'}}>{e.source}</td>
                    <td style={s.td}>
                      {e.source==='manuel'&&<button aria-label="Supprimer l'écriture" onClick={()=>handleDelete(e.id)} style={s.btnSmall}>🗑</button>}
                    </td>
                  </HoverRow>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length===0&&<div style={s.emptyState}>📒 Aucune écriture pour cette période</div>}
        </div>
      </div>
    );
  };

  const renderUpgrade = (plan, titre, description) => {
    const prix = plan === 'starter' ? '9€/mois' : plan === 'essentiel' ? '19€/mois' : '39€/mois';
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', textAlign: 'center', padding: '2rem' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔒</div>
        <h2 style={{ color: 'var(--ako-text)', fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.75rem' }}>{titre}</h2>
        <p style={{ color: 'var(--ako-text-2)', maxWidth: '420px', lineHeight: '1.6', marginBottom: '2rem' }}>{description}</p>
        <div style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid var(--ako-border)', borderRadius: '1rem', padding: '1.5rem 2rem', marginBottom: '2rem' }}>
          <p style={{ color: 'var(--ako-text-2)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Disponible à partir du plan</p>
          <p style={{ color: '#3b82f6', fontSize: '1.25rem', fontWeight: '700', textTransform: 'capitalize' }}>{plan} — {prix}</p>
        </div>
        <button onClick={() => setTab('abonnement')} style={{ background: 'linear-gradient(135deg,#3b82f6,#8b5cf6)', color: '#fff', border: 'none', borderRadius: '0.75rem', padding: '0.85rem 2rem', fontSize: '1rem', fontWeight: '600', cursor: 'pointer' }}>
          Voir les offres
        </button>
      </div>
    );
  };

  const renderBilan = () => {
    const { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } = require('recharts');
    const reloadBilan = async () => {
      const [bil, cats] = await Promise.all([api.getBilan(comptaFilters), api.getCategories()]);
      setBilan(bil);
      setCategories(cats.categories||[]);
    };
    const b = bilan || {};
    const recettes = parseFloat(b.recettes) || parseFloat(b.recettes_total) || 0;
    const charges  = parseFloat(b.charges)  || parseFloat(b.charges_total)  || 0;
    const resultat = parseFloat(b.resultat_net) || (recettes - charges);
    const MOIS_LABELS = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];

    // Prépare données graphique mensuel
    const parMoisRaw = b.par_mois || b.recettes_par_mois || [];
    const moisMap = {};
    parMoisRaw.forEach(row => {
      const key = row.mois || (row.mois && String(row.mois));
      const label = key && key.includes('-') ? MOIS_LABELS[parseInt(key.split('-')[1],10)-1] : MOIS_LABELS[(parseInt(key,10)||1)-1];
      if (!moisMap[label]) moisMap[label] = { mois: label, recettes: 0, charges: 0 };
      if (row.type === 'recette') moisMap[label].recettes = parseFloat(row.total)||0;
      else if (row.type === 'charge') moisMap[label].charges = parseFloat(row.total)||0;
      else moisMap[label].recettes = parseFloat(row.total)||0; // legacy format
    });
    const chartData = Object.values(moisMap);

    // Données camembert par catégorie
    const parCatRaw = b.par_categorie || b.charges_par_categorie || [];
    const PIE_COLORS = ['#3b82f6','#f59e0b','#ef4444','#10b981','#8b5cf6','#06b6d4','#f97316'];
    const pieData = parCatRaw
      .filter(r => r.type === 'charge' || r.categorie)
      .map(r => ({
        name: r.nom || r.categorie || 'Autre',
        value: parseFloat(r.total) || 0,
        couleur: r.couleur,
      }))
      .filter(r => r.value > 0);

    const CustomTooltip = ({ active, payload, label }) => {
      if (!active || !payload?.length) return null;
      return (
        <div style={{ background: 'var(--ako-surface)', border: '1px solid var(--ako-border)', borderRadius: '0.5rem', padding: '0.75rem 1rem', fontSize: '0.82rem' }}>
          <p style={{ color: 'var(--ako-text-2)', margin: '0 0 0.4rem' }}>{label}</p>
          {payload.map((p, i) => (
            <p key={i} style={{ color: p.color, margin: '0.2rem 0', fontWeight: '600' }}>
              {p.name}: {fmtMoney(p.value)}
            </p>
          ))}
        </div>
      );
    };

    return (
      <div>
        {/* En-tête */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem', flexWrap:'wrap', gap:'0.75rem' }}>
          <h2 style={{ color:'white', margin:0 }}>📈 Bilan comptable</h2>
          <div style={{ display:'flex', gap:'0.5rem', alignItems:'center', flexWrap:'wrap' }}>
            <select value={comptaFilters.annee} onChange={e => setComptaFilters(f => ({ ...f, annee: e.target.value }))} style={{ ...s.searchInput, width:'auto' }}>
              {[2023,2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select value={comptaFilters.trimestre||''} onChange={e => setComptaFilters(f => ({ ...f, trimestre: e.target.value||undefined }))} style={{ ...s.searchInput, width:'auto' }}>
              <option value="">Année entière</option>
              <option value="1">T1</option><option value="2">T2</option>
              <option value="3">T3</option><option value="4">T4</option>
            </select>
            <button aria-label="Rafraîchir le bilan" onClick={reloadBilan} style={s.btn}>🔄</button>
          </div>
        </div>

        {/* KPIs */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:'1rem', marginBottom:'1.5rem' }}>
          {[
            { label:'Recettes', value:recettes, color:'#34d399', bg:'rgba(52,211,153,0.08)', border:'rgba(52,211,153,0.2)', icon:'📥' },
            { label:'Charges',  value:charges,  color:'#f87171', bg:'rgba(248,113,113,0.08)', border:'rgba(248,113,113,0.2)', icon:'📤' },
            { label:'Résultat net', value:resultat, color:resultat>=0?'#60a5fa':'#fb923c', bg:resultat>=0?'rgba(96,165,250,0.08)':'rgba(251,146,60,0.08)', border:resultat>=0?'rgba(96,165,250,0.2)':'rgba(251,146,60,0.2)', icon:resultat>=0?'✅':'⚠️' },
          ].map(({ label, value, color, bg, border, icon }) => (
            <div key={label} style={{ background:bg, border:`1px solid ${border}`, borderRadius:'1rem', padding:'1.25rem 1.5rem' }}>
              <div style={{ color:'#64748b', fontSize:'0.78rem', fontWeight:'600', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'0.5rem' }}>{icon} {label}</div>
              <div style={{ color, fontSize:'1.75rem', fontWeight:'800', fontFamily:'monospace', lineHeight:1 }}>{fmtMoney(value)}</div>
            </div>
          ))}
        </div>

        {/* Graphique barres mensuel */}
        {chartData.length > 0 && (
          <div style={{ ...s.card, marginBottom:'1rem' }}>
            <h3 style={{ color:'white', marginTop:0, fontSize:'1rem', marginBottom:'1.25rem' }}>📊 Recettes vs Charges par mois</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top:0, right:10, left:0, bottom:0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="mois" tick={{ fill:'#64748b', fontSize:11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill:'#64748b', fontSize:11 }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ color:'#94a3b8', fontSize:'0.8rem' }} />
                <Bar dataKey="recettes" name="Recettes" fill="#34d399" radius={[4,4,0,0]} />
                <Bar dataKey="charges"  name="Charges"  fill="#f87171" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:'1rem', marginBottom:'1rem' }}>
          {/* Camembert charges par catégorie */}
          <div style={s.card}>
            <h3 style={{ color:'white', marginTop:0, fontSize:'1rem', marginBottom:'1rem' }}>🏷️ Charges par catégorie</h3>
            {pieData.length === 0
              ? <p style={{ color:'#64748b', fontSize:'0.85rem' }}>Aucune charge enregistrée</p>
              : (
                <>
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                        {pieData.map((entry, i) => (
                          <Cell key={i} fill={entry.couleur || PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ background:'#1e293b', border:'1px solid #334155', borderRadius:'0.5rem', fontSize:'0.8rem' }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ marginTop:'0.5rem' }}>
                    {pieData.map((d, i) => (
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.3rem' }}>
                        <div style={{ width:'8px', height:'8px', borderRadius:'50%', background: d.couleur || PIE_COLORS[i % PIE_COLORS.length], flexShrink:0 }} />
                        <span style={{ color:'#94a3b8', fontSize:'0.78rem', flex:1 }}>{d.name}</span>
                        <span style={{ color:'white', fontSize:'0.78rem', fontFamily:'monospace' }}>{fmtMoney(d.value)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )
            }
          </div>

          {/* Actif / Passif */}
          <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
            <div style={{ ...s.card, borderColor:'rgba(52,211,153,0.25)', flex:1 }}>
              <h3 style={{ color:'#34d399', marginTop:0, fontSize:'0.9rem', marginBottom:'0.75rem' }}>⬆️ Actif</h3>
              {[
                { label:'Créances clients',   value:(b.actif||{}).creances_clients   || recettes * 0.1 },
                { label:'Trésorerie estimée', value:(b.actif||{}).tresorerie_estimee || resultat },
              ].map(({ label, value }) => (
                <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'0.4rem 0', borderBottom:'1px solid #1e293b' }}>
                  <span style={{ color:'#94a3b8', fontSize:'0.85rem' }}>{label}</span>
                  <span style={{ color:'white', fontFamily:'monospace', fontWeight:'600', fontSize:'0.85rem' }}>{fmtMoney(value)}</span>
                </div>
              ))}
              <div style={{ display:'flex', justifyContent:'space-between', padding:'0.5rem 0 0', fontWeight:'700' }}>
                <span style={{ color:'#34d399' }}>Total actif</span>
                <span style={{ color:'#34d399', fontFamily:'monospace' }}>{fmtMoney((b.actif||{}).total || recettes)}</span>
              </div>
            </div>
            <div style={{ ...s.card, borderColor:'rgba(248,113,113,0.25)', flex:1 }}>
              <h3 style={{ color:'#f87171', marginTop:0, fontSize:'0.9rem', marginBottom:'0.75rem' }}>⬇️ Passif</h3>
              {[
                { label:'Dettes fournisseurs', value:(b.passif||{}).dettes_fournisseurs || charges * 0.3 },
              ].map(({ label, value }) => (
                <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'0.4rem 0', borderBottom:'1px solid #1e293b' }}>
                  <span style={{ color:'#94a3b8', fontSize:'0.85rem' }}>{label}</span>
                  <span style={{ color:'white', fontFamily:'monospace', fontWeight:'600', fontSize:'0.85rem' }}>{fmtMoney(value)}</span>
                </div>
              ))}
              <div style={{ display:'flex', justifyContent:'space-between', padding:'0.5rem 0 0', fontWeight:'700' }}>
                <span style={{ color:'#f87171' }}>Total passif</span>
                <span style={{ color:'#f87171', fontFamily:'monospace' }}>{fmtMoney((b.passif||{}).total || charges)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPortail = () => {
    const baseUrl = import.meta.env.VITE_APP_URL || 'https://app.akodigitale.fr';

    const handleInvite = async (client) => {
      try {
        const r = await api.inviteClient({ client_id: client.id, client_name: client.name, client_email: client.email });
        await navigator.clipboard.writeText(r.url).catch(() => {});
        toast(`Lien portail pour ${client.name} copié !`);
        const d = await api.getPortailTokens();
        setPortailTokens(d.tokens || []);
      } catch (e) { toast(e.message || 'Erreur', 'error'); }
    };

    return (
      <div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem', flexWrap:'wrap', gap:'1rem' }}>
          <div>
            <h2 style={{ color:'white', margin:'0 0 0.25rem' }}>🔗 Portail client</h2>
            <p style={{ color:'#64748b', margin:0, fontSize:'0.9rem' }}>Envoyez un lien à votre client pour qu'il consulte ses factures et devis.</p>
          </div>
        </div>

        {/* Générer un lien depuis la liste des clients */}
        <div style={s.card}>
          <h3 style={{ color:'white', marginTop:0, marginBottom:'1rem', fontSize:'1rem' }}>Générer un lien portail</h3>
          {clients.length === 0
            ? <p style={{ color:'#64748b' }}>Créez d'abord des clients dans l'onglet Clients.</p>
            : (
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr>
                  <th scope="col" style={s.th}>Client</th>
                  <th scope="col" style={s.th}>Email</th>
                  <th scope="col" style={s.th}></th>
                </tr></thead>
                <tbody>
                  {clients.map(cl => {
                    const tok = portailTokens.find(t => t.client_id === cl.id);
                    return (
                      <tr key={cl.id}>
                        <td style={s.td}>{cl.name}{cl.company ? ` (${cl.company})` : ''}</td>
                        <td style={{ ...s.td, color:'#64748b' }}>{cl.email || '—'}</td>
                        <td style={s.td}>
                          <div style={{ display:'flex', gap:'0.4rem', alignItems:'center' }}>
                            {tok ? (
                              <>
                                <button
                                  onClick={() => { navigator.clipboard.writeText(`${baseUrl}/portail/${tok.token}`); toast('Lien copié !'); }}
                                  style={{ background:'rgba(16,185,129,0.12)', color:'#34d399', border:'1px solid rgba(16,185,129,0.25)', borderRadius:'0.375rem', padding:'0.3rem 0.7rem', cursor:'pointer', fontSize:'0.78rem', fontWeight:'600' }}
                                >📋 Copier</button>
                                <button
                                  onClick={async () => { await api.revokePortailToken(tok.id); const d = await api.getPortailTokens(); setPortailTokens(d.tokens||[]); toast('Accès révoqué'); }}
                                  style={{ ...s.btnSmall, fontSize:'0.78rem' }}
                                >Révoquer</button>
                              </>
                            ) : (
                              <button
                                onClick={() => handleInvite(cl)}
                                style={{ background:'rgba(59,130,246,0.12)', color:'#60a5fa', border:'1px solid rgba(59,130,246,0.25)', borderRadius:'0.375rem', padding:'0.3rem 0.7rem', cursor:'pointer', fontSize:'0.78rem', fontWeight:'600' }}
                              >🔗 Créer lien</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
        </div>

        {/* Accès actifs */}
        {portailTokens.length > 0 && (
          <div style={s.card}>
            <h3 style={{ color:'white', marginTop:0, marginBottom:'1rem', fontSize:'1rem' }}>Accès actifs ({portailTokens.length})</h3>
            {portailTokens.map(tok => (
              <div key={tok.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0.6rem 0', borderBottom:'1px solid #1e293b', flexWrap:'wrap', gap:'0.5rem' }}>
                <div>
                  <span style={{ color:'white', fontWeight:'600', fontSize:'0.9rem' }}>{tok.client_name || '—'}</span>
                  {tok.client_email && <span style={{ color:'#64748b', fontSize:'0.8rem', marginLeft:'0.5rem' }}>{tok.client_email}</span>}
                </div>
                <div style={{ display:'flex', gap:'0.4rem' }}>
                  <button onClick={() => { navigator.clipboard.writeText(`${baseUrl}/portail/${tok.token}`); toast('Lien copié !'); }}
                    style={{ background:'rgba(16,185,129,0.12)', color:'#34d399', border:'1px solid rgba(16,185,129,0.25)', borderRadius:'0.375rem', padding:'0.25rem 0.6rem', cursor:'pointer', fontSize:'0.78rem', fontWeight:'600' }}>📋 Copier</button>
                  <a href={`${baseUrl}/portail/${tok.token}`} target="_blank" rel="noreferrer"
                    style={{ background:'rgba(59,130,246,0.12)', color:'#60a5fa', border:'1px solid rgba(59,130,246,0.25)', borderRadius:'0.375rem', padding:'0.25rem 0.6rem', fontSize:'0.78rem', fontWeight:'600', textDecoration:'none' }}>↗ Voir</a>
                  <button onClick={async () => { await api.revokePortailToken(tok.id); const d = await api.getPortailTokens(); setPortailTokens(d.tokens||[]); toast('Accès révoqué'); }}
                    style={{ ...s.btnSmall, fontSize:'0.78rem', padding:'0.25rem 0.6rem' }}>Révoquer</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderDepenses = () => {
    const MODES = ['virement', 'carte', 'espèces', 'chèque', 'prélèvement'];
    const totalAnnee = depenses.reduce((s, d) => s + (d.montant || 0), 0);

    const reloadDepenses = async () => {
      const d = await api.getDepenses({ annee: depenseFiltreAnnee });
      setDepenses(d.depenses || []);
    };

    const handleSave = async () => {
      const f = depenseForm;
      if (!f?.date || !f?.libelle || !f?.montant) { toast('Date, libellé et montant requis', 'error'); return; }
      try {
        if (f.id) { await api.updateDepense(f.id, f); toast('Dépense mise à jour'); }
        else      { await api.createDepense(f); toast('Dépense enregistrée'); }
        setDepenseForm(null);
        await reloadDepenses();
      } catch (e) { toast(e.message || 'Erreur', 'error'); }
    };

    // Grouper par mois
    const parMois = {};
    depenses.forEach(d => {
      const m = d.date?.slice(0, 7) || '?';
      if (!parMois[m]) parMois[m] = [];
      parMois[m].push(d);
    });

    return (
      <div>
        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem', flexWrap:'wrap', gap:'1rem' }}>
          <div>
            <h2 style={{ color:'white', margin:'0 0 0.25rem' }}>🛒 Achats & Dépenses</h2>
            <span style={{ color:'#64748b', fontSize:'0.9rem' }}>Total {depenseFiltreAnnee} : <strong style={{ color:'#f87171' }}>–{totalAnnee.toLocaleString('fr-FR', { minimumFractionDigits:2 })} €</strong></span>
          </div>
          <div style={{ display:'flex', gap:'0.75rem', alignItems:'center' }}>
            <select value={depenseFiltreAnnee} onChange={e => { setDepenseFiltreAnnee(e.target.value); setTimeout(reloadDepenses, 0); }}
              style={{ ...s.searchInput, width:'100px' }}>
              {[2024,2025,2026].map(y => <option key={y} value={String(y)}>{y}</option>)}
            </select>
            <button style={s.btn} onClick={() => setDepenseForm({ date: new Date().toISOString().slice(0,10), libelle:'', montant:'', modePaiement:'virement' })}>+ Nouvelle dépense</button>
          </div>
        </div>

        {/* Tableau groupé par mois */}
        {Object.keys(parMois).sort((a,b) => b.localeCompare(a)).map(mois => {
          const items = parMois[mois];
          const totalMois = items.reduce((s,d) => s + d.montant, 0);
          const [y, m] = mois.split('-');
          const nomMois = new Date(y, m-1).toLocaleDateString('fr-FR', { month:'long', year:'numeric' });
          return (
            <div key={mois} style={{ ...s.card, padding:'0' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'1rem 1.5rem', borderBottom:'1px solid #334155' }}>
                <span style={{ color:'white', fontWeight:'700', textTransform:'capitalize' }}>{nomMois}</span>
                <span style={{ color:'#f87171', fontWeight:'700' }}>–{totalMois.toLocaleString('fr-FR', { minimumFractionDigits:2 })} €</span>
              </div>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <tbody>
                  {items.map(dep => (
                    <tr key={dep.id} style={{ borderBottom:'1px solid #1e293b' }}>
                      <td style={{ ...s.td, width:'110px', color:'#64748b', fontSize:'0.82rem' }}>{dep.date}</td>
                      <td style={{ ...s.td, fontWeight:'500' }}>{dep.libelle}</td>
                      <td style={{ ...s.td, color:'#94a3b8', fontSize:'0.82rem' }}>{dep.fournisseur || ''}</td>
                      <td style={{ ...s.td, color:'#94a3b8', fontSize:'0.82rem' }}>{dep.categorieNom || '—'}</td>
                      <td style={{ ...s.td, color:'#94a3b8', fontSize:'0.82rem' }}>{dep.modePaiement}</td>
                      <td style={{ ...s.td, color:'#f87171', fontWeight:'700', textAlign:'right' }}>–{dep.montant.toLocaleString('fr-FR', { minimumFractionDigits:2 })} €</td>
                      <td style={{ ...s.td, width:'80px' }}>
                        <div style={{ display:'flex', gap:'0.3rem' }}>
                          <button onClick={() => setDepenseForm({ ...dep })} style={{ background:'rgba(51,65,85,0.6)', color:'#94a3b8', border:'1px solid #334155', borderRadius:'0.375rem', padding:'0.2rem 0.5rem', cursor:'pointer', fontSize:'0.75rem' }}>✏️</button>
                          <button onClick={async () => { if (!window.confirm('Supprimer ?')) return; await api.deleteDepense(dep.id); await reloadDepenses(); toast('Supprimée'); }} style={{ ...s.btnSmall, padding:'0.2rem 0.5rem', fontSize:'0.75rem' }} aria-label="Supprimer la dépense">🗑️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}

        {depenses.length === 0 && <EmptyState icon="🛒" title="Aucune dépense" description="Enregistrez vos achats et charges pour suivre votre trésorerie." action={() => setDepenseForm({ date: new Date().toISOString().slice(0,10), libelle:'', montant:'', modePaiement:'virement' })} actionLabel="+ Première dépense" />}

        {/* Modal */}
        {depenseForm !== null && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:100, padding:'1rem' }}>
            <div style={{ background:'#1e293b', border:'1px solid #334155', borderRadius:'1rem', padding:'2rem', width:'100%', maxWidth:'480px', maxHeight:'90vh', overflowY:'auto' }}>
              <h3 style={{ color:'white', marginTop:0 }}>{depenseForm.id ? 'Modifier la dépense' : 'Nouvelle dépense'}</h3>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
                <div>
                  <label style={s.label}>Date *</label>
                  <input type="date" style={s.input} value={depenseForm.date||''} onChange={e => setDepenseForm(f=>({...f, date:e.target.value}))} />
                </div>
                <div>
                  <label style={s.label}>Montant TTC (€) *</label>
                  <input type="number" step="0.01" min="0" style={s.input} value={depenseForm.montant||''} placeholder="0.00" onChange={e => setDepenseForm(f=>({...f, montant:e.target.value}))} />
                </div>
              </div>
              <label style={s.label}>Libellé *</label>
              <input style={s.input} value={depenseForm.libelle||''} placeholder="Ex: Achat fournitures chantier" onChange={e => setDepenseForm(f=>({...f, libelle:e.target.value}))} />
              <label style={s.label}>Fournisseur</label>
              <input style={s.input} value={depenseForm.fournisseur||''} placeholder="Nom du fournisseur" onChange={e => setDepenseForm(f=>({...f, fournisseur:e.target.value}))} />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
                <div>
                  <label style={s.label}>Mode de paiement</label>
                  <select style={{ ...s.input, appearance:'none' }} value={depenseForm.modePaiement||'virement'} onChange={e => setDepenseForm(f=>({...f, modePaiement:e.target.value}))}>
                    {MODES.map(m => <option key={m} value={m}>{m.charAt(0).toUpperCase()+m.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label style={s.label}>Catégorie</label>
                  <input style={s.input} value={depenseForm.categorieNom||''} placeholder="Ex: Matériaux" onChange={e => setDepenseForm(f=>({...f, categorieNom:e.target.value}))} />
                </div>
              </div>
              <label style={s.label}>Notes</label>
              <textarea rows={2} style={{ ...s.input, resize:'vertical' }} value={depenseForm.notes||''} onChange={e => setDepenseForm(f=>({...f, notes:e.target.value}))} />
              <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end', marginTop:'0.5rem' }}>
                {depenseForm.id && <button onClick={async () => { if (!window.confirm('Supprimer ?')) return; await api.deleteDepense(depenseForm.id); setDepenseForm(null); await reloadDepenses(); toast('Supprimée'); }} style={s.btnRed}>Supprimer</button>}
                <button onClick={() => setDepenseForm(null)} style={{ ...s.btn, background:'#334155' }}>Annuler</button>
                <button onClick={handleSave} style={s.btn}>Enregistrer</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAgenda = () => {
    const STATUTS = { planifie: { label: 'Planifié', color: '#60a5fa' }, confirme: { label: 'Confirmé', color: '#34d399' }, annule: { label: 'Annulé', color: '#f87171' }, termine: { label: 'Terminé', color: 'var(--ako-text-2)' } };
    const COULEURS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

    // Calendrier mensuel
    const [year, month] = agendaMois.split('-').map(Number);
    const firstDay = new Date(year, month - 1, 1).getDay(); // 0=dim
    const daysInMonth = new Date(year, month, 0).getDate();
    const startOffset = (firstDay + 6) % 7; // lundi=0
    const today = new Date().toISOString().slice(0, 10);

    const rdvByDay = {};
    appointments.forEach(rdv => {
      if (!rdvByDay[rdv.date]) rdvByDay[rdv.date] = [];
      rdvByDay[rdv.date].push(rdv);
    });

    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    const prevMois = () => {
      const d = new Date(year, month - 2, 1);
      setAgendaMois(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
      setTimeout(() => loadData(), 0);
    };
    const nextMois = () => {
      const d = new Date(year, month, 1);
      setAgendaMois(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
      setTimeout(() => loadData(), 0);
    };

    const handleSaveRdv = async () => {
      if (!rdvForm?.title || !rdvForm?.date) { toast('Titre et date requis', 'error'); return; }
      try {
        let saved;
        if (rdvForm.id) {
          saved = await api.updateAppointment(rdvForm.id, rdvForm);
          toast('RDV mis à jour');
        } else {
          saved = await api.createAppointment(rdvForm);
          toast('RDV créé');
        }
        const rdvId = rdvForm.id || saved?.appointment?.id;
        const clientEmail = rdvForm.clientEmail || '';
        setRdvSaved({ id: rdvId, title: rdvForm.title, clientName: rdvForm.clientName, clientEmail });
        setRdvNotifyEmail(clientEmail);
        setRdvForm(null);
        const d = await api.getAppointments({ mois: agendaMois });
        setAppointments(d.appointments || []);
      } catch (e) { toast(e.message || 'Erreur', 'error'); }
    };

    const MOIS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

    return (
      <div>
        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem', flexWrap:'wrap', gap:'1rem' }}>
          <h2 style={{ color:'white', margin:0 }}>📅 Agenda</h2>
          <button style={s.btn} onClick={() => setRdvForm({ title:'', date: today, heureDebut:'09:00', heureFin:'10:00', couleur:'#3b82f6', statut:'planifie' })}>+ Nouveau RDV</button>
        </div>

        {/* Nav mois */}
        <div style={{ display:'flex', alignItems:'center', gap:'1rem', marginBottom:'1rem' }}>
          <button onClick={prevMois} aria-label="Mois précédent" style={{ ...s.btn, background:'#1e293b', border:'1px solid #334155', padding:'0.4rem 0.8rem' }}>←</button>
          <span style={{ color:'white', fontWeight:'700', fontSize:'1.1rem', minWidth:'160px', textAlign:'center' }}>{MOIS_FR[month-1]} {year}</span>
          <button onClick={nextMois} aria-label="Mois suivant" style={{ ...s.btn, background:'#1e293b', border:'1px solid #334155', padding:'0.4rem 0.8rem' }}>→</button>
        </div>

        {/* Grille calendrier */}
        <div style={{ ...s.card, padding:'1rem' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:'1px', background:'#334155', borderRadius:'0.5rem', overflow:'hidden' }}>
            {['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'].map(j => (
              <div key={j} style={{ background:'#1e293b', padding:'0.5rem', textAlign:'center', color:'#64748b', fontSize:'0.8rem', fontWeight:'600' }}>{j}</div>
            ))}
            {cells.map((day, i) => {
              const dateStr = day ? `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}` : null;
              const rdvs = dateStr ? (rdvByDay[dateStr] || []) : [];
              const isToday = dateStr === today;
              return (
                <div key={i} style={{ background: isToday ? 'rgba(59,130,246,0.15)' : '#0f172a', minHeight:'80px', padding:'0.4rem', borderTop: isToday ? '2px solid #3b82f6' : '2px solid transparent', cursor: day ? 'pointer' : 'default' }}
                  onClick={() => day && setRdvForm({ title:'', date: dateStr, heureDebut:'09:00', heureFin:'10:00', couleur:'#3b82f6', statut:'planifie' })}>
                  {day && <div style={{ color: isToday ? '#60a5fa' : '#94a3b8', fontSize:'0.8rem', fontWeight: isToday ? '700' : '400', marginBottom:'0.25rem' }}>{day}</div>}
                  {rdvs.slice(0, 3).map(rdv => (
                    <div key={rdv.id}
                      onClick={e => { e.stopPropagation(); setRdvForm({ ...rdv }); }}
                      style={{ background: rdv.couleur || '#3b82f6', color:'white', borderRadius:'3px', padding:'1px 4px', fontSize:'0.7rem', marginBottom:'2px', overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis', cursor:'pointer' }}>
                      {rdv.heureDebut ? rdv.heureDebut.slice(0,5)+' ' : ''}{rdv.title}
                    </div>
                  ))}
                  {rdvs.length > 3 && <div style={{ color:'#64748b', fontSize:'0.65rem' }}>+{rdvs.length-3}</div>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Liste des RDV du mois */}
        {appointments.length > 0 && (
          <div style={s.card}>
            <h3 style={{ color:'white', marginTop:0, marginBottom:'1rem', fontSize:'1rem' }}>RDV ce mois</h3>
            {appointments.map(rdv => (
              <div key={rdv.id} style={{ display:'flex', alignItems:'center', gap:'1rem', padding:'0.75rem 0', borderBottom:'1px solid #1e293b', flexWrap:'wrap' }}>
                <div style={{ width:'4px', height:'40px', borderRadius:'2px', background: rdv.couleur || '#3b82f6', flexShrink:0 }} />
                <div style={{ flex:1 }}>
                  <div style={{ color:'white', fontWeight:'600', fontSize:'0.9rem' }}>{rdv.title}</div>
                  <div style={{ color:'#64748b', fontSize:'0.8rem' }}>
                    {rdv.date} {rdv.heureDebut && `• ${rdv.heureDebut.slice(0,5)}`}{rdv.heureFin && `–${rdv.heureFin.slice(0,5)}`}
                    {rdv.clientName && ` • ${rdv.clientName}`}
                    {rdv.lieu && ` • 📍 ${rdv.lieu}`}
                  </div>
                </div>
                <span style={{ padding:'0.2rem 0.6rem', borderRadius:'999px', fontSize:'0.75rem', fontWeight:'600', background: STATUTS[rdv.statut]?.color + '22', color: STATUTS[rdv.statut]?.color }}>{STATUTS[rdv.statut]?.label || rdv.statut}</span>
                <div style={{ display:'flex', gap:'0.4rem' }}>
                  <button onClick={() => setRdvForm({ ...rdv })} style={{ background:'rgba(51,65,85,0.6)', color:'#94a3b8', border:'1px solid #334155', borderRadius:'0.375rem', padding:'0.3rem 0.65rem', cursor:'pointer', fontSize:'0.78rem' }}>✏️</button>
                  <button onClick={async () => { if (!window.confirm('Supprimer ce RDV ?')) return; await api.deleteAppointment(rdv.id); const d = await api.getAppointments({ mois: agendaMois }); setAppointments(d.appointments || []); toast('RDV supprimé'); }} style={{ ...s.btnSmall }} aria-label="Supprimer">🗑️</button>
                </div>
              </div>
            ))}
          </div>
        )}
        {appointments.length === 0 && <div style={s.emptyState}>📅 Aucun RDV ce mois. Cliquez sur un jour du calendrier pour en créer un.</div>}

        {/* Modal form */}
        {rdvForm !== null && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:100, padding:'1rem' }}>
            <div style={{ background:'#1e293b', border:'1px solid #334155', borderRadius:'1rem', padding:'2rem', width:'100%', maxWidth:'500px', maxHeight:'90vh', overflowY:'auto' }}>
              <h3 style={{ color:'white', marginTop:0 }}>{rdvForm.id ? 'Modifier le RDV' : 'Nouveau RDV'}</h3>
              <label style={s.label}>Titre *</label>
              <input style={s.input} value={rdvForm.title||''} onChange={e => setRdvForm(f => ({...f, title: e.target.value}))} placeholder="Ex: Visite chantier Dupont" />
              <label style={s.label}>Client</label>
              <select
                style={{ ...s.input, appearance: 'none' }}
                value={rdvForm.clientId || ''}
                onChange={e => {
                  const cl = clients.find(c => c.id === e.target.value);
                  setRdvForm(f => ({
                    ...f,
                    clientId: cl?.id || '',
                    clientName: cl?.name || f.clientName,
                    clientEmail: cl?.email || f.clientEmail || '',
                    lieu: f.lieu || cl?.address || '',
                  }));
                }}
              >
                <option value="">— Sélectionner un client —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}{c.email ? ` (${c.email})` : ''}</option>)}
              </select>
              {rdvForm.clientId && (
                <input style={{ ...s.input, marginTop: '0.4rem', fontSize: '0.8rem', color: 'var(--ako-text-2)' }}
                  value={rdvForm.clientName || ''} readOnly placeholder="Nom du client" />
              )}
              {!rdvForm.clientId && (
                <input style={{ ...s.input, marginTop: '0.4rem', fontSize: '0.85rem' }}
                  value={rdvForm.clientName || ''} onChange={e => setRdvForm(f => ({...f, clientName: e.target.value}))} placeholder="Ou saisissez le nom manuellement" />
              )}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
                <div>
                  <label style={s.label}>Date *</label>
                  <input type="date" style={s.input} value={rdvForm.date||''} onChange={e => setRdvForm(f => ({...f, date: e.target.value}))} />
                </div>
                <div>
                  <label style={s.label}>Statut</label>
                  <select style={{ ...s.input, appearance:'none' }} value={rdvForm.statut||'planifie'} onChange={e => setRdvForm(f => ({...f, statut: e.target.value}))}>
                    {Object.entries(STATUTS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={s.label}>Heure début</label>
                  <input type="time" style={s.input} value={rdvForm.heureDebut||''} onChange={e => setRdvForm(f => ({...f, heureDebut: e.target.value}))} />
                </div>
                <div>
                  <label style={s.label}>Heure fin</label>
                  <input type="time" style={s.input} value={rdvForm.heureFin||''} onChange={e => setRdvForm(f => ({...f, heureFin: e.target.value}))} />
                </div>
              </div>
              <label style={s.label}>Lieu</label>
              <input style={s.input} value={rdvForm.lieu||''} onChange={e => setRdvForm(f => ({...f, lieu: e.target.value}))} placeholder="Adresse ou lieu du RDV" />
              <label style={s.label}>Notes</label>
              <textarea rows={3} style={{ ...s.input, resize:'vertical' }} value={rdvForm.notes||''} onChange={e => setRdvForm(f => ({...f, notes: e.target.value}))} placeholder="Notes..." />
              <label style={s.label}>Couleur</label>
              <div style={{ display:'flex', gap:'0.5rem', marginBottom:'1rem' }}>
                {COULEURS.map(c => (
                  <div key={c} onClick={() => setRdvForm(f => ({...f, couleur: c}))} style={{ width:'28px', height:'28px', borderRadius:'50%', background:c, cursor:'pointer', border: rdvForm.couleur===c ? '3px solid white' : '3px solid transparent', transition:'border 0.15s' }} />
                ))}
              </div>
              <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end', marginTop:'0.5rem' }}>
                {rdvForm.id && <button onClick={async () => { if (!window.confirm('Supprimer ?')) return; await api.deleteAppointment(rdvForm.id); setRdvForm(null); const d = await api.getAppointments({ mois: agendaMois }); setAppointments(d.appointments || []); toast('Supprimé'); }} style={s.btnRed}>Supprimer</button>}
                <button onClick={() => setRdvForm(null)} style={{ ...s.btn, background:'#334155' }}>Annuler</button>
                <button onClick={handleSaveRdv} style={s.btn}>Enregistrer</button>
              </div>
            </div>
          </div>
        )}

        {/* Bannière envoi lien RDV */}
        {rdvSaved && (
          <div style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid #3b82f644', borderRadius: '0.75rem', padding: '1rem 1.25rem', marginTop: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <div style={{ color: '#60a5fa', fontWeight: '600', fontSize: '0.9rem' }}>✅ RDV « {rdvSaved.title} » enregistré</div>
              {rdvSaved.clientName && <div style={{ color: 'var(--ako-text-2)', fontSize: '0.8rem', marginTop: '0.2rem' }}>Client : {rdvSaved.clientName}</div>}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="email"
                placeholder="Email du client"
                value={rdvNotifyEmail}
                onChange={e => setRdvNotifyEmail(e.target.value)}
                style={{ ...s.input, width: '220px', marginBottom: 0, fontSize: '0.85rem', padding: '0.45rem 0.75rem' }}
              />
              <button
                disabled={rdvNotifying || !rdvNotifyEmail}
                onClick={async () => {
                  setRdvNotifying(true);
                  try {
                    await api.notifyAppointment(rdvSaved.id, rdvNotifyEmail);
                    toast('Email envoyé à ' + rdvNotifyEmail);
                    setRdvSaved(null);
                  } catch (e) { toast(e.message || 'Erreur envoi', 'error'); }
                  finally { setRdvNotifying(false); }
                }}
                style={{ ...s.btn, fontSize: '0.85rem', padding: '0.45rem 1rem', whiteSpace: 'nowrap' }}
              >
                {rdvNotifying ? '…' : '📧 Envoyer le lien'}
              </button>
              <button onClick={() => setRdvSaved(null)} aria-label="Fermer" style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '1.1rem' }}>✕</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCatalogue = () => {
    const UNITES = ['forfait', 'm²', 'ml', 'heure', 'jour', 'pièce', 'unité'];
    const CATEGORIES = ['Maçonnerie', 'Électricité', 'Plomberie', 'Menuiserie', 'Peinture', 'Carrelage', 'Isolation', 'Toiture', 'Autre'];
    const fmt = (v) => Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const EMPTY = { nom: '', prixUnitaire: '', unite: 'forfait', tvaTaux: 20, categorie: '', description: '' };
    const [form, setForm] = React.useState(null); // null=fermé, {}=nouveau, {id,...}=édition
    const [search, setSearch] = React.useState('');

    const reloadCatalog = async () => {
      const params = {};
      if (catalogFilter.categorie) params.categorie = catalogFilter.categorie;
      if (catalogFilter.favori) params.favori = 'true';
      const d = await api.getCatalog(params);
      setCatalogItems(d.items || []);
    };

    const handleSave = async () => {
      if (!form.nom?.trim()) { toast('Le nom est requis', 'error'); return; }
      try {
        if (form.id) {
          await api.updateCatalogItem(form.id, form);
          toast('Item mis à jour');
        } else {
          await api.createCatalogItem(form);
          toast('Item créé');
        }
        setForm(null);
        reloadCatalog();
      } catch (e) { toast(e.message, 'error'); }
    };

    const handleDelete = async (id, nom) => {
      if (!window.confirm(`Supprimer "${nom}" ?`)) return;
      await api.deleteCatalogItem(id);
      reloadCatalog();
    };

    const handleToggleFavori = async (id) => {
      await api.toggleCatalogFavori(id);
      reloadCatalog();
    };

    const filtered = catalogItems.filter(item =>
      !search || item.nom.toLowerCase().includes(search.toLowerCase()) || (item.categorie || '').toLowerCase().includes(search.toLowerCase())
    );

    const categories = [...new Set(catalogItems.map(i => i.categorie).filter(Boolean))];

    return (
      <div>
        {/* En-tête */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <h2 style={{ color: 'var(--ako-heading)', margin: 0 }}>🏷️ Catalogue</h2>
            <p style={{ color: '#64748b', margin: '0.25rem 0 0', fontSize: '0.8rem' }}>
              Bibliothèque de prestations réutilisables dans vos devis
            </p>
          </div>
          <button style={s.btn} onClick={() => setForm({ ...EMPTY })}>+ Nouvelle prestation</button>
        </div>

        {/* Filtres */}
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            placeholder="Rechercher..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...s.searchInput, flex: '1 1 180px' }}
          />
          <select
            value={catalogFilter.categorie}
            onChange={e => { setCatalogFilter(f => ({ ...f, categorie: e.target.value })); setTimeout(reloadCatalog, 50); }}
            style={{ ...s.searchInput, width: 'auto' }}
          >
            <option value="">Toutes catégories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button
            onClick={() => { setCatalogFilter(f => ({ ...f, favori: !f.favori })); setTimeout(reloadCatalog, 50); }}
            style={{ ...s.btn, background: catalogFilter.favori ? 'linear-gradient(135deg,#f59e0b,#d97706)' : '#1e293b', border: '1px solid var(--ako-border)', fontSize: '0.85rem' }}
          >
            ⭐ Favoris
          </button>
        </div>

        {/* Modal form */}
        {form !== null && (
          <div style={{ position: 'fixed', inset: 0, background: 'var(--ako-overlay)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }} role="dialog" aria-modal="true">
            <div style={{ background: 'var(--ako-surface)', border: '1px solid var(--ako-border)', borderRadius: '0.75rem', padding: '1.5rem', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto' }}>
              <h3 style={{ color: 'var(--ako-heading)', marginTop: 0 }}>{form.id ? '✏️ Modifier' : '+ Nouvelle prestation'}</h3>

              <div style={{ marginBottom: '0.75rem' }}>
                <label style={s.label}>Nom *</label>
                <input style={s.input} value={form.nom || ''} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} placeholder="ex: Pose carrelage" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div>
                  <label style={s.label}>Prix unitaire (€)</label>
                  <input type="number" style={s.input} value={form.prixUnitaire || ''} onChange={e => setForm(f => ({ ...f, prixUnitaire: e.target.value }))} placeholder="0.00" step="0.01" />
                </div>
                <div>
                  <label style={s.label}>Unité</label>
                  <select style={s.input} value={form.unite || 'forfait'} onChange={e => setForm(f => ({ ...f, unite: e.target.value }))}>
                    {UNITES.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label style={s.label}>TVA (%)</label>
                  <select style={s.input} value={form.tvaTaux ?? 20} onChange={e => setForm(f => ({ ...f, tvaTaux: Number(e.target.value) }))}>
                    {[0, 5.5, 10, 20].map(t => <option key={t} value={t}>{t} %</option>)}
                  </select>
                </div>
                <div>
                  <label style={s.label}>Catégorie</label>
                  <select style={s.input} value={form.categorie || ''} onChange={e => setForm(f => ({ ...f, categorie: e.target.value }))}>
                    <option value="">— Aucune —</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={s.label}>Description (optionnel)</label>
                <textarea style={{ ...s.input, resize: 'vertical', minHeight: '70px' }} value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button onClick={() => setForm(null)} style={s.btnRed}>Annuler</button>
                <button style={s.btn} onClick={handleSave}>💾 Enregistrer</button>
              </div>
            </div>
          </div>
        )}

        {/* Grille items */}
        {filtered.length === 0 ? (
          <EmptyState icon="🏷️" title="Catalogue vide" description="Ajoutez vos prestations habituelles pour les réutiliser rapidement dans vos devis." />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.875rem' }}>
            {filtered.map(item => (
              <div key={item.id} style={{ ...s.card, position: 'relative', borderTop: `3px solid ${item.favori ? '#f59e0b' : '#334155'}`, marginBottom: 0 }}>
                {/* Favori toggle */}
                <button onClick={() => handleToggleFavori(item.id)}
                  style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', background: 'none', border: 'none', fontSize: '1.1rem', cursor: 'pointer', opacity: item.favori ? 1 : 0.35 }}>
                  ⭐
                </button>

                <div style={{ fontWeight: '700', color: 'white', fontSize: '0.95rem', marginBottom: '0.25rem', paddingRight: '2rem' }}>{item.nom}</div>

                {item.categorie && (
                  <span style={{ display: 'inline-block', background: 'rgba(59,130,246,0.15)', color: '#60a5fa', borderRadius: '999px', padding: '0.1rem 0.5rem', fontSize: '0.72rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                    {item.categorie}
                  </span>
                )}

                {item.description && (
                  <p style={{ color: '#64748b', fontSize: '0.78rem', margin: '0 0 0.5rem', lineHeight: '1.4' }}>{item.description}</p>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
                  <div>
                    <span style={{ color: '#34d399', fontWeight: '700', fontSize: '1rem', fontFamily: 'monospace' }}>{fmt(item.prixUnitaire)} €</span>
                    <span style={{ color: '#64748b', fontSize: '0.75rem' }}> / {item.unite}</span>
                  </div>
                  <span style={{ color: '#475569', fontSize: '0.72rem' }}>TVA {item.tvaTaux} %</span>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                  <button onClick={() => setForm({ ...item, prixUnitaire: item.prixUnitaire })}
                    style={{ flex: 1, background: 'rgba(59,130,246,0.12)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.25)', borderRadius: '0.375rem', padding: '0.4rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' }}>
                    ✏️ Modifier
                  </button>
                  <button onClick={() => handleDelete(item.id, item.nom)}
                    style={{ background: 'rgba(239,68,68,0.1)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '0.375rem', padding: '0.4rem 0.6rem', cursor: 'pointer', fontSize: '0.78rem' }}>
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderCloture = () => {
    const fmt = (v) => (v ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const anneeEnCours = new Date().getFullYear();
    const annees = [anneeEnCours - 2, anneeEnCours - 1, anneeEnCours];

    const reload = async () => {
      const d = await api.getExercices();
      setExercices(d.exercices || []);
    };

    const handleCloture = async (annee) => {
      if (!window.confirm(`Clôturer l'exercice ${annee} ?\n\nToutes les écritures de ${annee} seront verrouillées et ne pourront plus être modifiées.`)) return;
      try {
        const r = await api.cloturerExercice(annee);
        toast(`Exercice ${annee} clôturé — Résultat net : ${fmt(r.resultat_net)} €`);
        reload();
      } catch (e) { toast(e.message, 'error'); }
    };

    const handleReouverture = async (annee) => {
      if (!window.confirm(`Réouvrir l'exercice ${annee} ?\n\nLes écritures seront à nouveau modifiables.`)) return;
      try {
        await api.rouvrirExercice(annee);
        toast(`Exercice ${annee} réouvert.`, 'warning');
        reload();
      } catch (e) { toast(e.message, 'error'); }
    };

    const getExercice = (annee) => exercices.find(e => e.annee === annee);

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <h2 style={{ color: 'var(--ako-heading)', margin: 0 }}>🔒 Clôture d'exercice</h2>
            <p style={{ color: '#64748b', margin: '0.25rem 0 0', fontSize: '0.8rem' }}>
              Verrouillez vos exercices comptables annuels pour garantir l'intégrité des données
            </p>
          </div>
          <button aria-label="Rafraîchir les prestations" onClick={reload} style={s.btn}>🔄</button>
        </div>

        {/* Avertissement */}
        <div style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: '0.75rem', padding: '1rem 1.25rem', marginBottom: '1.5rem', color: '#fbbf24', fontSize: '0.8rem', lineHeight: '1.6' }}>
          <strong>⚠️ Action irréversible</strong> — La clôture verrouille toutes les écritures de l'exercice. Elles ne pourront plus être modifiées ni supprimées. Assurez-vous que toutes les écritures sont correctes avant de clôturer.
        </div>

        {/* Cartes par année */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {annees.map(annee => {
            const ex = getExercice(annee);
            const isCloture = !!ex?.cloture_at;
            const isEnCours = annee === anneeEnCours;

            return (
              <div key={annee} style={{ ...s.card, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', borderColor: isCloture ? 'rgba(34,197,94,0.3)' : isEnCours ? 'rgba(59,130,246,0.3)' : '#334155' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '1.1rem', fontWeight: '700', color: 'white' }}>Exercice {annee}</span>
                    {isEnCours && !isCloture && (
                      <span style={{ ...s.badge('active'), fontSize: '0.72rem' }}>En cours</span>
                    )}
                    {isCloture && (
                      <span style={{ ...s.badge('paid'), fontSize: '0.72rem' }}>Clôturé</span>
                    )}
                    {!isCloture && !isEnCours && (
                      <span style={{ ...s.badge('pending'), fontSize: '0.72rem' }}>Non clôturé</span>
                    )}
                  </div>

                  {isCloture && ex && (
                    <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ color: '#64748b', fontSize: '0.72rem', fontWeight: '600' }}>Recettes</div>
                        <div style={{ color: '#60a5fa', fontFamily: 'monospace', fontWeight: '700' }}>{fmt(ex.recettes_total)} €</div>
                      </div>
                      <div>
                        <div style={{ color: '#64748b', fontSize: '0.72rem', fontWeight: '600' }}>Charges</div>
                        <div style={{ color: '#f87171', fontFamily: 'monospace', fontWeight: '700' }}>{fmt(ex.charges_total)} €</div>
                      </div>
                      <div>
                        <div style={{ color: '#64748b', fontSize: '0.72rem', fontWeight: '600' }}>Résultat net</div>
                        <div style={{ color: ex.resultat_net >= 0 ? '#34d399' : '#f87171', fontFamily: 'monospace', fontWeight: '700' }}>
                          {ex.resultat_net >= 0 ? '+' : ''}{fmt(ex.resultat_net)} €
                        </div>
                      </div>
                      <div>
                        <div style={{ color: '#64748b', fontSize: '0.72rem', fontWeight: '600' }}>Clôturé le</div>
                        <div style={{ color: 'var(--ako-text-2)', fontSize: '0.8rem' }}>{new Date(ex.cloture_at).toLocaleDateString('fr-FR')}</div>
                      </div>
                    </div>
                  )}

                  {!isCloture && (
                    <div style={{ color: '#475569', fontSize: '0.8rem' }}>
                      {isEnCours ? 'Exercice en cours — clôturez en fin d\'année après vérification de toutes les écritures.' : 'Aucune clôture enregistrée pour cet exercice.'}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', flexShrink: 0 }}>
                  {!isCloture && (
                    <button
                      onClick={() => handleCloture(annee)}
                      style={{ background: 'linear-gradient(135deg,#22c55e,#16a34a)', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.6rem 1.2rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem' }}
                    >
                      🔒 Clôturer {annee}
                    </button>
                  )}
                  {isCloture && (
                    <button
                      onClick={() => handleReouverture(annee)}
                      style={{ background: 'rgba(251,191,36,0.12)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)', borderRadius: '0.5rem', padding: '0.6rem 1.2rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem' }}
                    >
                      🔓 Réouvrir
                    </button>
                  )}
                  {isCloture && (
                    <button
                      onClick={() => api.getBilan({ annee }).then(b => { setBilan(b); setTab('bilan'); }).catch(e => toast(e.message, 'error'))}
                      style={{ ...s.btn, background: 'var(--ako-surface)', border: '1px solid var(--ako-border)', fontSize: '0.85rem' }}
                    >
                      📊 Voir bilan
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Historique complet si plus d'exercices */}
        {exercices.filter(e => !annees.includes(e.annee)).length > 0 && (
          <div style={{ ...s.card, marginTop: '1rem' }}>
            <h3 style={{ color: 'var(--ako-text-2)', fontSize: '0.85rem', marginTop: 0, marginBottom: '0.75rem' }}>Exercices plus anciens</h3>
            {exercices.filter(e => !annees.includes(e.annee)).map(ex => (
              <div key={ex.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid #1e293b', flexWrap: 'wrap', gap: '0.5rem' }}>
                <span style={{ color: 'var(--ako-text)', fontWeight: '600' }}>Exercice {ex.annee}</span>
                <div style={{ display: 'flex', gap: '1.5rem', color: '#64748b', fontSize: '0.82rem', fontFamily: 'monospace' }}>
                  <span style={{ color: '#60a5fa' }}>{fmt(ex.recettes_total)} €</span>
                  <span style={{ color: '#f87171' }}>-{fmt(ex.charges_total)} €</span>
                  <span style={{ color: ex.resultat_net >= 0 ? '#34d399' : '#f87171', fontWeight: '700' }}>{fmt(ex.resultat_net)} € net</span>
                </div>
                <span style={{ ...s.badge('paid'), fontSize: '0.72rem' }}>Clôturé</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderCaDeclaration = () => {
    const fmt = (v) => (v ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const pct = (v) => ((v ?? 0) * 100).toFixed(1) + ' %';

    const CATEGORIES = [
      { value: 'bic_ventes',   label: 'BIC — Vente de marchandises' },
      { value: 'bic_services', label: 'BIC — Prestations de services' },
      { value: 'bnc',          label: 'BNC — Professions libérales (CIPAV)' },
      { value: 'bnc_autres',   label: 'BNC — Autres activités (SSI)' },
    ];

    const reloadCa = async (cat) => {
      const d = await api.getCaDeclaration(comptaFilters.annee, cat || caCategorie);
      setCaData(d);
    };

    const handleCatChange = async (val) => {
      setCaCategorie(val);
      const d = await api.getCaDeclaration(comptaFilters.annee, val);
      setCaData(d);
    };

    const seuil = caData?.seuil_applicable || 77700;
    const ca_annuel = caData?.annuel?.ca || 0;
    const pctSeuil = Math.min(100, Math.round((ca_annuel / seuil) * 100));

    return (
      <div>
        {/* En-tête */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <h2 style={{ color: 'var(--ako-heading)', margin: 0 }}>📋 Déclaration CA Micro-entrepreneur</h2>
            <p style={{ color: '#64748b', margin: '0.25rem 0 0', fontSize: '0.8rem' }}>
              Cotisations URSSAF · Taux 2025 · {caData?.categorie_label || '—'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={caCategorie}
              onChange={e => handleCatChange(e.target.value)}
              style={{ ...s.searchInput, width: 'auto', minWidth: '220px' }}
            >
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <select value={comptaFilters.annee} onChange={e => { setComptaFilters(f => ({ ...f, annee: e.target.value })); }} style={{ ...s.searchInput, width: 'auto' }}>
              {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button aria-label="Rafraîchir le chiffre d'affaires" onClick={() => reloadCa()} style={s.btn}>🔄</button>
          </div>
        </div>

        {/* Seuil de CA */}
        {caData && (
          <div style={{ ...s.card, marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <span style={{ color: 'var(--ako-text-2)', fontSize: '0.85rem', fontWeight: '600' }}>
                Plafond annuel micro-entrepreneur
              </span>
              <span style={{ color: pctSeuil >= 90 ? '#f87171' : pctSeuil >= 70 ? '#fbbf24' : '#34d399', fontWeight: '700', fontSize: '0.9rem' }}>
                {fmt(ca_annuel)} € / {fmt(seuil)} € ({pctSeuil} %)
              </span>
            </div>
            <div style={{ background: 'var(--ako-surface)', borderRadius: '999px', height: '8px', overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: '999px', transition: 'width 0.5s ease',
                width: pctSeuil + '%',
                background: pctSeuil >= 90 ? 'linear-gradient(90deg,#ef4444,#dc2626)' : pctSeuil >= 70 ? 'linear-gradient(90deg,#f59e0b,#d97706)' : 'linear-gradient(90deg,#22c55e,#16a34a)',
              }} />
            </div>
            {pctSeuil >= 90 && (
              <p style={{ color: '#f87171', fontSize: '0.78rem', marginTop: '0.5rem', marginBottom: 0 }}>
                ⚠️ Attention : vous approchez du plafond micro-entrepreneur. Au-delà, vous basculerez au régime réel.
              </p>
            )}
          </div>
        )}

        {/* KPIs annuels */}
        {caData && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            {[
              { label: 'CA annuel', val: fmt(caData.annuel.ca) + ' €', color: '#60a5fa' },
              { label: 'Taux cotisations', val: pct(caData.taux), color: '#a78bfa' },
              { label: 'Cotisations estimées', val: fmt(caData.annuel.cotisations) + ' €', color: '#f87171' },
            ].map(item => (
              <div key={item.label} style={{ ...s.card, padding: '1rem', textAlign: 'center', marginBottom: 0 }}>
                <div style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.4rem' }}>{item.label}</div>
                <div style={{ color: item.color, fontSize: '1.3rem', fontWeight: '700', fontFamily: 'monospace' }}>{item.val}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tableau par trimestre */}
        {caData && (
          <div style={s.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <h3 style={{ color: 'var(--ako-heading)', margin: 0, fontSize: '1rem' }}>Détail par trimestre</h3>
              <a
                href="https://www.net-entreprises.fr"
                target="_blank"
                rel="noopener noreferrer"
                style={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: 'white', borderRadius: '0.5rem', padding: '0.5rem 1rem', fontSize: '0.8rem', fontWeight: '600', textDecoration: 'none', display: 'inline-block' }}
              >
                🔗 Déclarer sur net-entreprises.fr
              </a>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '480px' }}>
                <thead>
                  <tr>
                    {['Période', 'CA réalisé', `Cotisations (${pct(caData.taux)})`, 'Total à payer', 'Statut'].map(h => (
                      <th key={h} style={s.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {caData.trimestres.map(t => {
                    const isDue = t.ca > 0;
                    return (
                      <tr key={t.trimestre}>
                        <td style={s.td}><strong style={{ color: 'var(--ako-text)' }}>{t.label}</strong></td>
                        <td style={{ ...s.td, color: '#60a5fa', fontFamily: 'monospace' }}>{fmt(t.ca)} €</td>
                        <td style={{ ...s.td, color: '#a78bfa', fontFamily: 'monospace' }}>{fmt(t.cotisations)} €</td>
                        <td style={{ ...s.td, color: t.total_a_payer > 0 ? '#f87171' : '#64748b', fontFamily: 'monospace', fontWeight: '700' }}>
                          {fmt(t.total_a_payer)} €
                        </td>
                        <td style={s.td}>
                          {isDue
                            ? <span style={{ ...s.badge('pending'), fontSize: '0.75rem' }}>À déclarer</span>
                            : <span style={{ ...s.badge('draft'), fontSize: '0.75rem' }}>Aucun CA</span>}
                        </td>
                      </tr>
                    );
                  })}
                  {/* Total */}
                  <tr style={{ borderTop: '2px solid #334155' }}>
                    <td style={{ ...s.td, fontWeight: '700', color: 'white' }}>Total {caData.annee}</td>
                    <td style={{ ...s.td, color: '#60a5fa', fontFamily: 'monospace', fontWeight: '700' }}>{fmt(caData.annuel.ca)} €</td>
                    <td style={{ ...s.td, color: '#a78bfa', fontFamily: 'monospace', fontWeight: '700' }}>{fmt(caData.annuel.cotisations)} €</td>
                    <td style={{ ...s.td, color: '#f87171', fontFamily: 'monospace', fontWeight: '700' }}>{fmt(caData.annuel.total_a_payer)} €</td>
                    <td style={s.td} />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Note légale */}
        {caData && (
          <div style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '0.5rem', padding: '0.875rem 1rem', marginTop: '1rem', color: '#93c5fd', fontSize: '0.8rem', lineHeight: '1.6' }}>
            ℹ️ <strong>Rappel :</strong> La déclaration se fait tous les trimestres (ou mensuellement sur option) sur{' '}
            <a href="https://www.autoentrepreneur.urssaf.fr" target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa' }}>autoentrepreneur.urssaf.fr</a>.{' '}
            Les montants affichés sont des estimations basées sur votre CA comptabilisé. Consultez votre expert-comptable pour validation.
          </div>
        )}

        {!caData && (
          <div style={s.emptyState}>Chargement des données CA…</div>
        )}
      </div>
    );
  };

  const renderTvaDeclaration = () => {
    const fmt = (v) => (v ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const reloadTva = async () => {
      const d = await api.getTvaDeclaration(comptaFilters.annee);
      setTvaData(d);
    };

    const isfranchise = !tvaData || tvaData.regime === 'franchise_base';

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <h2 style={{ color: 'var(--ako-heading)', margin: 0 }}>⊞ Déclaration TVA</h2>
            <p style={{ color: '#64748b', margin: '0.25rem 0 0', fontSize: '0.8rem' }}>
              {isfranchise
                ? 'Régime : franchise en base (art. 293 B) — aucune TVA à déclarer'
                : `Régime : ${tvaData?.regime === 'reel_simplifie' ? 'Réel simplifié (CA12 annuelle)' : 'Réel normal (CA3 mensuelle)'}`}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <select value={comptaFilters.annee} onChange={e => setComptaFilters(f => ({ ...f, annee: e.target.value }))} style={{ ...s.searchInput, width: 'auto' }}>
              {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button aria-label="Rafraîchir la TVA" onClick={reloadTva} style={s.btn}>🔄</button>
          </div>
        </div>

        {isfranchise && (
          <div style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: '0.75rem', padding: '1.25rem 1.5rem', marginBottom: '1.5rem', color: '#fbbf24', fontSize: '0.875rem', lineHeight: '1.6' }}>
            <strong>⚠️ Franchise en base TVA</strong><br />
            Vous bénéficiez de la franchise en base (art. 293 B du CGI). Vous n'êtes pas soumis à la TVA : vous ne la facturez pas et ne la récupérez pas. Aucune déclaration TVA à effectuer.<br />
            <span style={{ color: 'var(--ako-text-2)', fontSize: '0.8rem' }}>Modifiez votre régime TVA dans <strong>Mon Profil</strong> si votre situation change.</span>
          </div>
        )}

        {!isfranchise && tvaData && (
          <>
            {/* Résumé annuel */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              {[
                { label: 'TVA collectée', val: tvaData.annuel.tva_collectee, color: '#60a5fa' },
                { label: 'TVA déductible', val: tvaData.annuel.tva_deductible, color: '#34d399' },
                { label: 'Net à reverser', val: tvaData.annuel.net, color: tvaData.annuel.net > 0 ? '#f87171' : '#34d399' },
              ].map(item => (
                <div key={item.label} style={{ ...s.card, padding: '1rem', textAlign: 'center' }}>
                  <div style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.4rem' }}>{item.label}</div>
                  <div style={{ color: item.color, fontSize: '1.4rem', fontWeight: '700', fontFamily: 'monospace' }}>{fmt(item.val)} €</div>
                </div>
              ))}
            </div>

            {/* Détail par trimestre */}
            <div style={s.card}>
              <h3 style={{ color: 'var(--ako-heading)', marginTop: 0, marginBottom: '1rem', fontSize: '1rem' }}>Détail par trimestre</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
                  <thead>
                    <tr>
                      {['Période', 'TVA collectée', 'TVA déductible', 'Net à reverser', 'Factures', 'Charges'].map(h => (
                        <th key={h} style={s.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tvaData.trimestres.map(t => (
                      <tr key={t.trimestre} style={{ background: t.net > 0 ? 'rgba(248,113,113,0.04)' : 'transparent' }}>
                        <td style={s.td}><strong style={{ color: 'var(--ako-text)' }}>{t.label}</strong></td>
                        <td style={{ ...s.td, color: '#60a5fa', fontFamily: 'monospace' }}>{fmt(t.tva_collectee)} €</td>
                        <td style={{ ...s.td, color: '#34d399', fontFamily: 'monospace' }}>{fmt(t.tva_deductible)} €</td>
                        <td style={{ ...s.td, color: t.net > 0 ? '#f87171' : '#34d399', fontFamily: 'monospace', fontWeight: '700' }}>
                          {t.net > 0 ? '+' : ''}{fmt(t.net)} €
                        </td>
                        <td style={{ ...s.td, color: 'var(--ako-text-2)' }}>{t.nb_factures}</td>
                        <td style={{ ...s.td, color: 'var(--ako-text-2)' }}>{t.nb_charges}</td>
                      </tr>
                    ))}
                    {/* Ligne total */}
                    <tr style={{ borderTop: '2px solid #334155' }}>
                      <td style={{ ...s.td, fontWeight: '700', color: 'white' }}>Total {tvaData.annee}</td>
                      <td style={{ ...s.td, color: '#60a5fa', fontFamily: 'monospace', fontWeight: '700' }}>{fmt(tvaData.annuel.tva_collectee)} €</td>
                      <td style={{ ...s.td, color: '#34d399', fontFamily: 'monospace', fontWeight: '700' }}>{fmt(tvaData.annuel.tva_deductible)} €</td>
                      <td style={{ ...s.td, color: tvaData.annuel.net > 0 ? '#f87171' : '#34d399', fontFamily: 'monospace', fontWeight: '700' }}>
                        {tvaData.annuel.net > 0 ? '+' : ''}{fmt(tvaData.annuel.net)} €
                      </td>
                      <td style={s.td} /><td style={s.td} />
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '0.5rem', padding: '0.875rem 1rem', marginTop: '1rem', color: '#93c5fd', fontSize: '0.8rem', lineHeight: '1.6' }}>
              ℹ️ <strong>TVA collectée</strong> = TVA sur vos factures envoyées/payées.{' '}
              <strong>TVA déductible</strong> = TVA sur vos documents entrants validés.{' '}
              Ces montants sont des indicatifs — consultez un expert-comptable pour vos déclarations officielles (CA3 / CA12).
            </div>
          </>
        )}

        {!isfranchise && !tvaData && (
          <div style={s.emptyState}>Chargement des données TVA…</div>
        )}
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  // Pages qui prennent tout l'écran (couvrent sidebar + main) — mobile uniquement
  const FULLSCREEN_TABS = new Set(['parametres', 'profil', 'abonnement']);
  const isFullScreen = isMobile && FULLSCREEN_TABS.has(tab);

  // Pages full-screen uniquement sur mobile (agent-ia, analytique)
  const MOBILE_FULLSCREEN_TABS = new Set(['analytique', 'agent-ia']);
  const isMobileFullScreen = isMobile && (MOBILE_FULLSCREEN_TABS.has(tab) || isFullScreen);

  const sidebarStyle = isMobile
    ? { ...s.sidebar, position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 200, transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform 0.25s ease', width: '260px' }
    : s.sidebar;

  const mainStyle = isMobile
    ? isMobileFullScreen
      ? { ...s.main, padding: '1rem', paddingTop: '4.5rem', paddingBottom: '1rem' }
      : { ...s.main, padding: '1rem', paddingTop: '4.5rem', paddingBottom: '5.5rem' }
    : s.main;

  const handleNavClick = (id) => {
    setTab(id);
    setSelectedInvoiceId(null);
    setSelectedQuoteId(null);
    if (isMobile) setSidebarOpen(false);
  };

  const activeNav = nav.find(n => n.id === tab);

  const bottomNavItems = [
    { id: 'dashboard', icon: '📊', label: 'Accueil' },
    { id: 'invoices', icon: '📄', label: 'Factures' },
    { id: 'clients', icon: '👥', label: 'Clients' },
    { id: 'documents', icon: '🧾', label: 'Compta' },
    { id: '__menu__', icon: '☰', label: 'Menu' },
  ];

  return (
    <div style={s.layout}>
      {showOnboarding && (
        <OnboardingWizard
          user={user}
          artisanProfile={artisanProfile}
          toast={toast}
          onComplete={(updatedProfile) => {
            if (updatedProfile) setArtisanProfile(p => ({ ...p, ...updatedProfile }));
            setShowOnboarding(false);
          }}
        />
      )}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes slideInRight { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slideInLeft { from { opacity: 0; transform: translateX(-100%); } to { opacity: 1; transform: translateX(0); } }
        input::placeholder, textarea::placeholder { color: #475569; }
        select option { background: #1e293b; color: #e2e8f0; }

        /* Sidebar scrollbar */
        nav::-webkit-scrollbar { width: 4px; }
        nav::-webkit-scrollbar-track { background: transparent; }
        nav::-webkit-scrollbar-thumb { background: #334155; border-radius: 2px; }

        /* Nav group separator */
        .nav-group + .nav-group { border-top: 1px solid rgba(51,65,85,0.5); padding-top: 0.25rem; }

        /* Smooth sidebar transition */
        .sidebar-animated { transition: transform 0.28s cubic-bezier(0.4,0,0.2,1) !important; }

        /* Bottom nav safe area for iOS */
        .bottom-nav { padding-bottom: env(safe-area-inset-bottom, 0px); }

        /* Active nav item indicator */
        .nav-active-dot { width: 4px; height: 4px; border-radius: 50%; background: #3b82f6; margin-left: auto; flex-shrink: 0; }

        /* Responsive tables */
        .table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }

        /* Modal grids 1 col on mobile */
        @media (max-width: 600px) {
          .grid-2col, .grid-3col { grid-template-columns: 1fr !important; }
          .ako-modal-pad { padding: 1.1rem !important; }
          .ako-items-row { grid-template-columns: 1fr 56px 80px 30px !important; gap: 0.3rem !important; }
          .ako-search-inp { width: 100% !important; }
          .ako-action-row { flex-direction: column !important; }
          .ako-action-row button { width: 100% !important; }
          .ako-bottom-label { font-size: 0.6rem !important; }
          .ako-kpi-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .ako-quick-grid { grid-template-columns: 1fr !important; }
        }

        /* Prevent body scroll leak */
        html, body { overflow-x: hidden; max-width: 100vw; }

        /* Touch targets */
        button { -webkit-tap-highlight-color: transparent; }
      `}</style>

      {demande && <DemandeModal user={user} type={demande} onClose={() => setDemande(null)} />}
      {factureModal && (
        <FactureDevisModal
          type={factureModal}
          user={user}
          clients={clients}
          artisanProfile={artisanProfile}
          invoiceCount={invoices.length}
          quoteCount={quotes.length}
          onClose={() => setFactureModal(null)}
          onSuccess={loadData}
          toast={toast}
        />
      )}
      {/* Overlay mobile */}
      {isMobile && sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} role="presentation" aria-hidden="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 199 }} />
      )}

      {/* Sidebar */}
      <div style={sidebarStyle}>
        {/* Logo */}
        <div style={{ marginBottom: '1.5rem', paddingBottom: '1.25rem', borderBottom: '1px solid #1e3a5f' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem' }}>
            <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: 'linear-gradient(135deg,#3b82f6,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', fontSize: '1rem', color: 'white', flexShrink: 0, boxShadow: '0 2px 8px rgba(59,130,246,0.4)' }}>F</div>
            <div>
              <div style={{ fontSize: '1.1rem', fontWeight: '800', background: 'linear-gradient(135deg,#60a5fa,#818cf8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', lineHeight: 1.2 }}>FacturArt</div>
              <div style={{ color: '#475569', fontSize: '0.65rem', letterSpacing: '0.04em' }}>by AKO Digital</div>
            </div>
          </div>
          {/* Badge plan actif */}
          {userPlan && (() => {
            const planColors = {
              libre:     { bg: 'rgba(100,116,139,0.2)', color: 'var(--ako-text-2)', border: 'rgba(100,116,139,0.3)' },
              starter:   { bg: 'rgba(59,130,246,0.15)', color: '#93c5fd', border: 'rgba(59,130,246,0.3)' },
              essentiel: { bg: 'rgba(16,185,129,0.15)', color: '#6ee7b7', border: 'rgba(16,185,129,0.3)' },
              pro:       { bg: 'rgba(139,92,246,0.15)', color: '#c4b5fd', border: 'rgba(139,92,246,0.3)' },
              business:  { bg: 'rgba(251,191,36,0.15)', color: '#fbbf24', border: 'rgba(251,191,36,0.3)' },
              actif:     { bg: 'rgba(139,92,246,0.15)', color: '#c4b5fd', border: 'rgba(139,92,246,0.3)' },
            };
            const c = planColors[userPlan.plan] || planColors.libre;
            const planLabel = { libre: 'Libre', starter: 'Starter', essentiel: 'Essentiel', pro: 'Pro', business: 'Business', actif: 'Pro' }[userPlan.plan] || userPlan.plan;
            return (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: c.bg, border: `1px solid ${c.border}`, borderRadius: '999px', padding: '2px 10px', fontSize: '0.72rem', fontWeight: '700', color: c.color, letterSpacing: '0.03em' }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: c.color, display: 'inline-block' }} />
                Plan {planLabel}
              </div>
            );
          })()}
        </div>
        <nav style={{ flex: 1, overflowY: 'auto' }}>
          {navGroups.map((group, gi) => {
            const isCollapsed = collapsedGroups.has(group.label);
            // Groupes qui peuvent être repliés (trop longs)
            const collapsible = ['Comptabilité', 'IA & Marketing', 'Administration'].includes(group.label);
            // Si un item du groupe est actif, forcer l'ouverture
            const hasActiveItem = group.items.some(i => i.id === tab);
            const showItems = !isCollapsed || hasActiveItem;

            return (
              <div key={group.label} style={{ marginBottom: gi < navGroups.length - 1 ? '0.25rem' : 0 }}>
                {/* Group label — cliquable si collapsible */}
                <div
                  onClick={collapsible ? () => toggleGroup(group.label) : undefined}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#334155', fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', padding: '0.7rem 1rem 0.25rem', marginTop: gi === 0 ? 0 : '0.4rem', userSelect: 'none', cursor: collapsible ? 'pointer' : 'default' }}
                  onMouseEnter={e => { if (collapsible) e.currentTarget.style.color = '#475569'; }}
                  onMouseLeave={e => { if (collapsible) e.currentTarget.style.color = '#334155'; }}
                >
                  <span>{group.label}</span>
                  {collapsible && (
                    <span style={{ fontSize: '0.6rem', color: '#334155', transition: 'transform 0.2s', display: 'inline-block', transform: showItems ? 'rotate(0deg)' : 'rotate(-90deg)' }}>▾</span>
                  )}
                </div>
                {/* Items — masqués si replié */}
                {showItems && group.items.map(item => {
                  const isActive = tab === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => !item.locked && handleNavClick(item.id)}
                      aria-current={isActive ? 'page' : undefined}
                      aria-disabled={item.locked ? 'true' : undefined}
                      onMouseEnter={e => { if (!isActive && !item.locked) { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = '#e2e8f0'; } }}
                      onMouseLeave={e => { if (!isActive && !item.locked) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; } }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.65rem',
                        padding: '0.55rem 0.75rem',
                        borderRadius: '0.5rem', cursor: item.locked ? 'default' : 'pointer',
                        color: isActive ? '#60a5fa' : '#64748b',
                        background: isActive ? 'rgba(59,130,246,0.12)' : 'transparent',
                        border: 'none', width: '100%', textAlign: 'left',
                        fontWeight: isActive ? '600' : '400', fontSize: '0.875rem',
                        marginBottom: '1px', transition: 'color 0.15s, background 0.15s',
                        opacity: item.locked ? 0.45 : 1,
                        borderLeft: isActive ? '3px solid #3b82f6' : '3px solid transparent',
                        paddingLeft: isActive ? 'calc(0.75rem - 3px)' : '0.75rem',
                      }}
                    >
                      <span style={{ fontSize: '0.95rem', width: '18px', textAlign: 'center', flexShrink: 0 }}>{item.icon}</span>
                      <span style={{ flex: 1 }}>{item.label}</span>
                      {item.locked && <span style={{ fontSize: '0.65rem', color: '#f59e0b', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '4px', padding: '1px 5px', flexShrink: 0 }}>{item.badge || 'Pro'}</span>}
                      {isActive && !item.locked && <span className="nav-active-dot" />}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>
        <div style={{ borderTop: '1px solid #1e3a5f', paddingTop: '0.875rem', position: 'relative' }}>
          {userMenuOpen && (
            <>
              <div onClick={() => setUserMenuOpen(false)} role="presentation" aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: 90 }} />
              <div style={{ position: 'absolute', bottom: 'calc(100% + 0.5rem)', left: 0, right: 0, background: 'var(--ako-surface)', border: '1px solid var(--ako-border)', borderRadius: '0.75rem', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', zIndex: 100, overflow: 'hidden' }}>
                <div style={{ padding: '0.875rem 1rem', borderBottom: '1px solid var(--ako-border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg,#3b82f6,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', flexShrink: 0 }}>{user.avatar}</div>
                    <div style={{ overflow: 'hidden' }}>
                      <div style={{ color: 'white', fontSize: '0.85rem', fontWeight: '700', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.name}</div>
                      <div style={{ color: '#475569', fontSize: '0.72rem' }}>{user.email}</div>
                    </div>
                  </div>
                </div>
                <button onClick={() => { setTab('parametres'); setUserMenuOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', width: '100%', padding: '0.7rem 1rem', background: 'none', border: 'none', color: 'var(--ako-text-2)', fontSize: '0.85rem', cursor: 'pointer', textAlign: 'left' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                  <span>⚙️</span> Paramètres
                </button>
                <div style={{ height: '1px', background: '#334155', margin: '0' }} />
                <button onClick={() => { setUserMenuOpen(false); onLogout(); }} style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', width: '100%', padding: '0.7rem 1rem', background: 'none', border: 'none', color: '#fca5a5', fontSize: '0.85rem', cursor: 'pointer', textAlign: 'left' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                  <span>🚪</span> Déconnexion
                </button>
              </div>
            </>
          )}
          <button onClick={() => setUserMenuOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', width: '100%', background: userMenuOpen ? 'rgba(59,130,246,0.1)' : 'transparent', border: userMenuOpen ? '1px solid rgba(59,130,246,0.3)' : '1px solid transparent', borderRadius: '0.625rem', padding: '0.5rem 0.625rem', cursor: 'pointer', transition: 'all 0.15s' }}
            onMouseEnter={e => { if (!userMenuOpen) { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'rgba(51,65,85,0.6)'; } }}
            onMouseLeave={e => { if (!userMenuOpen) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; } }}>
            <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: 'linear-gradient(135deg,#3b82f6,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', flexShrink: 0, boxShadow: '0 1px 4px rgba(59,130,246,0.4)' }}>{user.avatar}</div>
            <div style={{ overflow: 'hidden', flex: 1, textAlign: 'left' }}>
              <div style={{ color: 'var(--ako-text)', fontSize: '0.82rem', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.name}</div>
              <div style={{ color: '#475569', fontSize: '0.7rem' }}>{user.role === 'admin' ? '👑 Admin' : userPlan ? (({ libre: 'Plan Libre', starter: 'Plan Starter', essentiel: 'Plan Essentiel', pro: 'Plan Pro', business: 'Plan Business', actif: 'Plan Pro' })[userPlan.plan] || userPlan.plan) : 'Plan Libre'}</div>
            </div>
            <span style={{ color: '#334155', fontSize: '0.65rem', transform: userMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>▲</span>
          </button>
        </div>
      </div>

      {/* Bannière installation PWA */}
      {showInstallBanner && (
        <div style={{ position: 'fixed', bottom: isMobile ? '70px' : '1.5rem', left: '50%', transform: 'translateX(-50%)', zIndex: 9999, background: 'rgba(15,23,42,0.97)', border: '1px solid var(--ako-border)', borderRadius: '1rem', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', maxWidth: '420px', width: 'calc(100% - 2rem)' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'linear-gradient(135deg,#3b82f6,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', fontSize: '1.2rem', color: 'white', flexShrink: 0 }}>A</div>
          <div style={{ flex: 1 }}>
            <div style={{ color: 'white', fontWeight: '700', fontSize: '0.9rem' }}>Installer FacturArt</div>
            <div style={{ color: '#64748b', fontSize: '0.78rem' }}>Accès rapide depuis votre écran d'accueil</div>
          </div>
          <button onClick={handleInstallApp} style={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 1rem', cursor: 'pointer', fontWeight: '700', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Installer</button>
          <button onClick={() => setShowInstallBanner(false)} aria-label="Fermer" style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '1.2rem', padding: '0 0.25rem', lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* Main content */}
      <div style={mainStyle}>
        {/* Header mobile — standard (masqué en mode full-screen) */}
        {isMobile && !isMobileFullScreen && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: '56px', background: 'var(--ako-surface)', borderBottom: '1px solid var(--ako-border)', zIndex: 198, display: 'flex', alignItems: 'center', paddingLeft: '0.5rem', paddingRight: '1rem', gap: '0.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
            <button
              onClick={() => setSidebarOpen(true)}
              style={{ background: 'none', border: 'none', color: 'white', fontSize: '1.4rem', cursor: 'pointer', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '0.5rem', flexShrink: 0 }}
            >
              ☰
            </button>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <span style={{ color: 'white', fontWeight: '700', fontSize: '1rem' }}>
                {activeNav ? `${activeNav.icon} ${activeNav.label}` : 'FacturArt'}
              </span>
            </div>
            <button
              onClick={() => setFactureModal('invoice')}
              style={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', border: 'none', color: 'white', borderRadius: '0.5rem', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', cursor: 'pointer', flexShrink: 0 }}
              title="Créer une facture"
            >
              +
            </button>
          </div>
        )}
        {/* Header full-screen mobile — remplace le header+bottomnav sur les sous-pages */}
        {isMobileFullScreen && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: '56px', background: 'var(--ako-bg)', borderBottom: '1px solid #1e293b', zIndex: 198, display: 'flex', alignItems: 'center', paddingLeft: '0.25rem', paddingRight: '1rem', gap: '0.5rem' }}>
            <button
              onClick={() => setTab('dashboard')}
              aria-label="Retour au tableau de bord"
              style={{ background: 'none', border: 'none', color: '#60a5fa', fontSize: '0.9rem', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.5rem 0.75rem', borderRadius: '0.5rem', flexShrink: 0 }}
            >
              ‹ Retour
            </button>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <span style={{ color: 'white', fontWeight: '700', fontSize: '1rem' }}>
                {activeNav ? activeNav.label : 'FacturArt'}
              </span>
            </div>
            <div style={{ width: '64px' }} />{/* équilibre optique */}
          </div>
        )}
        {/* Bottom navigation mobile — masquée en mode full-screen */}
        {isMobile && !isMobileFullScreen && (
          <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, height: '60px', background: 'var(--ako-surface)', borderTop: '1px solid #334155', zIndex: 198, display: 'flex', alignItems: 'stretch' }}>
            {bottomNavItems.map(item => {
              const isMenu = item.id === '__menu__';
              const isActive = !isMenu && tab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => isMenu ? setSidebarOpen(true) : handleNavClick(item.id)}
                  style={{
                    flex: 1, background: 'none', border: 'none', cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    gap: '2px', paddingTop: '6px', paddingBottom: '6px',
                    color: isActive ? '#60a5fa' : isMenu && sidebarOpen ? '#60a5fa' : '#64748b',
                    borderTop: isActive ? '2px solid #3b82f6' : '2px solid transparent',
                    transition: 'all 0.15s',
                  }}
                >
                  <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>{item.icon}</span>
                  <span style={{ fontSize: '0.65rem', fontWeight: isActive ? '700' : '400', lineHeight: 1 }}>{item.label}</span>
                </button>
              );
            })}
          </div>
        )}
        {isLoading ? <LoadingSpinner /> : (
          <>
            {tab === 'dashboard' && renderDashboard()}
            {tab === 'analytique' && <Analytique />}
            {tab === 'clients' && renderClients()}
            {tab === 'portail' && (hasStarter ? renderPortail() : renderUpgrade('starter', 'Portail client', 'Donnez à vos clients un espace pour consulter leurs factures et devis, et payer en ligne.'))}
            {tab === 'invoices' && (hasStarter ? renderInvoices() : <LockedBanner title="Factures — Plan Starter requis (9€/mois)" onUnlock={setTab} />)}
            {tab === 'quotes' && (hasStarter ? renderQuotes() : <LockedBanner title="Devis — Plan Starter requis (9€/mois)" onUnlock={setTab} />)}
            {tab === 'proposals' && (hasPro ? renderProposals() : <LockedBanner title="Propositions — Plan Pro requis (39€/mois)" onUnlock={setTab} />)}
            {tab === 'projects' && (hasPro ? renderProjects() : <LockedBanner title="Projets — Plan Pro requis (39€/mois)" onUnlock={setTab} />)}
            {tab === 'profil' && !isFullScreen && <Profil user={user} />}
            {tab === 'agent-ia' && (hasEssentiel ? renderAgentIA() : <LockedBanner title="Agent IA — Plan Essentiel requis" onUnlock={setTab} />)}
            {tab === 'social' && (hasPro ? renderSocial() : <LockedBanner title="Réseaux Sociaux — Plan Pro requis" onUnlock={setTab} />)}
            {tab === 'abonnement' && !isFullScreen && <Abonnement user={user} />}
            {tab === 'parametres' && !isFullScreen && <Parametres user={user} />}
            {tab === 'users' && user.role === 'admin' && renderUsers()}
            {tab === 'artisans' && user.role === 'admin' && renderArtisans()}
            {tab === 'access-codes' && user.role === 'admin' && renderAccessCodes()}
            {tab === 'depenses' && (hasEssentiel ? renderDepenses() : renderUpgrade('essentiel', 'Achats & Dépenses', 'Saisissez rapidement vos achats et charges. Chaque dépense alimente automatiquement votre comptabilité.'))}
            {tab === 'documents' && (hasEssentiel ? renderDocuments() : renderUpgrade('essentiel', 'Documents entrants', 'Gérez vos factures fournisseurs, tickets et relevés bancaires. Validez-les pour qu\'ils alimentent automatiquement votre comptabilité.'))}
            {tab === 'grand-livre' && (hasEssentiel ? renderGrandLivre() : renderUpgrade('essentiel', 'Grand livre', 'Consultez toutes vos écritures comptables (recettes et charges) avec filtres par période et catégorie.'))}
            {tab === 'tva' && (hasEssentiel ? renderTvaDeclaration() : renderUpgrade('essentiel', 'Déclaration TVA', 'Consultez votre TVA collectée et déductible par trimestre pour préparer vos déclarations.'))}
            {tab === 'ca-micro' && (hasEssentiel ? renderCaDeclaration() : renderUpgrade('essentiel', 'Déclaration CA Micro', 'Calculez vos cotisations URSSAF par trimestre et suivez votre plafond micro-entrepreneur.'))}
            {tab === 'cloture' && (hasEssentiel ? renderCloture() : renderUpgrade('essentiel', 'Clôture d\'exercice', 'Verrouillez vos exercices comptables annuels pour garantir l\'intégrité de vos données.'))}
            {tab === 'agenda' && (hasStarter ? renderAgenda() : renderUpgrade('starter', 'Agenda', 'Gérez vos rendez-vous et visualisez votre planning mensuel.'))}
            {tab === 'contrats' && (hasStarter ? <Contrats clients={clients} /> : renderUpgrade('starter', 'Contrats', 'Envoyez des documents à signer électroniquement à vos clients.'))}
            {tab === 'livre-recettes' && <LivreRecettes />}
            {tab === 'catalogue' && (hasEssentiel ? renderCatalogue() : renderUpgrade('essentiel', 'Catalogue', 'Créez votre catalogue de produits et services pour les réutiliser rapidement dans vos factures et devis.'))}
            {tab === 'bilan' && (hasPro ? renderBilan() : renderUpgrade('pro', 'Bilan comptable', 'Visualisez votre résultat net, actif/passif et l\'évolution mensuelle de votre trésorerie.'))}
          </>
        )}
      </div>

      {/* ── Overlay full-screen : Paramètres / Profil / Abonnement ─────── */}
      {isFullScreen && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--ako-bg)', zIndex: 300, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Barre de navigation de l'overlay */}
          <div style={{ height: '56px', flexShrink: 0, background: 'var(--ako-surface)', borderBottom: '1px solid var(--ako-border)', display: 'flex', alignItems: 'center', padding: '0 1rem', gap: '1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
            <button
              onClick={() => setTab('dashboard')}
              aria-label="Retour au tableau de bord"
              style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', background: 'none', border: 'none', color: '#60a5fa', fontSize: '0.9rem', fontWeight: '600', cursor: 'pointer', padding: '0.4rem 0.75rem', borderRadius: '0.5rem', flexShrink: 0, transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.1)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              ‹ Retour
            </button>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <span style={{ color: 'white', fontWeight: '700', fontSize: '1rem' }}>
                {tab === 'parametres' && '⚙️ Paramètres'}
                {tab === 'profil' && '👤 Mon Profil'}
                {tab === 'abonnement' && '💳 Abonnement'}
              </span>
            </div>
            {/* Liens raccourcis entre sous-pages */}
            <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
              {[
                { id: 'profil',      label: 'Profil',      icon: '👤' },
                { id: 'abonnement',  label: 'Abonnement',  icon: '💳' },
              ].map(p => (
                <button key={p.id} onClick={() => setTab(p.id)} style={{ background: tab === p.id ? 'rgba(59,130,246,0.15)' : 'none', border: `1px solid ${tab === p.id ? 'rgba(59,130,246,0.3)' : 'transparent'}`, color: tab === p.id ? '#60a5fa' : '#64748b', fontSize: '0.78rem', fontWeight: '600', borderRadius: '0.4rem', padding: '0.3rem 0.6rem', cursor: 'pointer', transition: 'all 0.15s' }}>
                  <span className="ako-hide-sm">{p.icon} {p.label}</span>
                </button>
              ))}
            </div>
          </div>
          {/* Contenu scrollable */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {tab === 'parametres' && <Parametres user={user} />}
            {tab === 'profil'     && <Profil user={user} />}
            {tab === 'abonnement' && <Abonnement user={user} />}
          </div>
        </div>
      )}

    </div>
  );
};

export default Dashboard;
